import React, {
  useEffect, useRef, useState, useCallback, useMemo, useContext,
} from 'react';
import ReactDOM from 'react-dom';
import { AppContext } from 'Context';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
// Splash is a webm (vp9 + alpha) instead of a GIF — drawImage on a <video> reliably gives
// the current frame in every browser, whereas drawImage on an animated <img> is unreliable
// (Safari snapshots only the first frame; off-screen imgs may not animate at all).
import splashVideoSrc from 'Assets/splash_anim_1080.webm';
import { useMobileCameraSession, STATUS as MC_STATUS } from 'Components/MobileCamera/MobileCameraConnector';
import QRPairingModal from 'Components/MobileCamera/QRPairingModal';
import { fetchSpeechBlobUrl } from 'Utils/aiSpeechSynthesis';
import { VIDEO_RECORDER_COMMENTARY_ENABLED, GUEST_INVITE_ENABLED } from 'Config/featureFlags';
import NetflixPlayer from 'Components/NetflixPlayer';
import {
  createRoom as createGuestRoom,
  closeRoom as closeGuestRoom,
  getRoomSignalingUrl as getGuestRoomSignalingUrl,
  getIceServers as getGuestIceServers,
} from 'Api/roomsApi';
import { Pathname } from 'Routes';
import { getSuggestionsForOccasion, getSuggestionsForOccasions } from './occasionQuestions';
import styles from './GiftRecorder.module.css';

// Splash intro is currently disabled. Flip to true to re-enable the post-record concat.
const ENABLE_SPLASH_INTRO = false;
const SPLASH_DURATION_MS = 2000;
const SPLASH_FADE_MS = 500;

// Per-clip timing
const DEFAULT_DURATION_CAP_S = 60;
const WARNING_THRESHOLD_S = 15;
// 0 = no countdown — recording starts immediately on "Start Recording".
// Set to a positive number for a 3-2-1 lead-in before recording actually
// begins (so the user has a beat to settle after pressing Start). Set to 0
// to skip the countdown and record instantly.
const PREP_COUNTDOWN_S = 3;
const FLIP_ANIM_MS = 480; // must match @keyframes flyToSlot duration in CSS

const PREFERRED_MIME_TYPES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=h264,opus',
  'video/webm',
  'video/mp4'
];

const SLOT_STATUS = {
  EMPTY: 'empty',
  RECORDING: 'recording',
  DONE: 'done',
  SKIPPED: 'skipped',
};

const PHASE = {
  IDLE: 'idle',
  PREP: 'prep',
  RECORDING: 'recording',
  REVIEW: 'review',
};

const pickSupportedMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  return PREFERRED_MIME_TYPES.find((type) => {
    try { return MediaRecorder.isTypeSupported(type); } catch (e) { return false; }
  }) || '';
};

const formatTime = (s) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const getQuestion = (item) => (item && typeof item === 'object' ? item.question : item) || '';
const getSubject = (item) => (item && typeof item === 'object' ? (item.subject || item.question) : item) || '';

// First word of a (possibly full) name, trimmed.
const firstNameOf = (full) => (full || '').trim().split(/\s+/)[0] || '';

// Resolve the host's display name for the guest-invite call. The recorder is
// reused across flows (gift sender, gift receiver, signup, …) and each keeps
// the user's name in a different place, so we check the known sources in
// priority order and fall back to "Host".
//   1. giftDraft:v1 → senderName        (gift sender flow; may be a full name,
//      so we take the first word)
//   2. profileFirstName                 (gift receiver / signup — the signed-in
//      user's AppContext userDetails.firstname, passed in by the component)
//   3. localStorage 'firstname'         (last-resort fallback)
const resolveHostName = (profileFirstName) => {
  try {
    const raw = localStorage.getItem('giftDraft:v1');
    if (raw) {
      const draft = JSON.parse(raw);
      const fn = firstNameOf(draft && draft.senderName);
      if (fn) return fn;
    }
  } catch (e) { /* ignore malformed draft */ }
  const ctxName = firstNameOf(profileFirstName);
  if (ctxName) return ctxName;
  try {
    const fn = firstNameOf(localStorage.getItem('firstname'));
    if (fn) return fn;
  } catch (e) { /* ignore */ }
  return 'Host';
};

// Round icon button for the host's in-call self-controls (mute / camera).
// Goes red+filled when the control is "active" (muted / camera-off), matching
// the meeting-control convention used on the guest side (InterviewCall).
const hostCallControlBtn = (active) => ({
  width: '40px',
  height: '40px',
  flexShrink: 0,
  borderRadius: '999px',
  border: 'none',
  background: active ? '#ff4d4f' : 'rgba(255,255,255,0.12)',
  color: '#fff',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: active ? '0 4px 14px rgba(255, 77, 79, 0.35)' : 'none',
  fontFamily: 'inherit',
});

let SLOT_ID_COUNTER = 0;
const nextSlotId = () => {
  SLOT_ID_COUNTER += 1;
  return `slot-${SLOT_ID_COUNTER}`;
};

// Stable portal target for dragged slots. The gift-flow step container
// has `transform: translateY(0)` (from stepEnter animation), which creates
// a containing block that breaks react-beautiful-dnd's drag-position math.
// Rendering the dragged slot in a body-level portal escapes that block so
// the chip tracks the pointer.
const getDragPortalTarget = () => {
  if (typeof document === 'undefined') return null;
  let el = document.getElementById('gift-recorder-drag-portal');
  if (!el) {
    el = document.createElement('div');
    el.id = 'gift-recorder-drag-portal';
    document.body.appendChild(el);
  }
  return el;
};

// Stable <body>-level target for the mobile fullscreen camera modal. The
// recorder can be mounted inside a transformed ancestor (e.g. gift-flow's
// `.stepEnter`, whose `transform: translateY(0)` end-state establishes a
// containing block), which would make a `position: fixed` stage resolve
// relative to that ancestor instead of the viewport. Portalling the stage
// here escapes the transform so the modal is truly fullscreen. The target is
// created once and reused, so on mobile the stage portals into the SAME node
// for the whole session — React never remounts it, keeping the live camera +
// composite canvas stream alive across open/close toggles.
const getStagePortalTarget = () => {
  if (typeof document === 'undefined') return null;
  let el = document.getElementById('gift-recorder-stage-portal');
  if (!el) {
    el = document.createElement('div');
    el.id = 'gift-recorder-stage-portal';
    document.body.appendChild(el);
  }
  return el;
};

// Each slot carries a `section` tag derived from the (optional) sections
// prop so deletion of a preset slot leaves the remaining slots' tags
// intact — a Hustle slot doesn't become a Beginning slot just because an
// earlier index was removed. Custom-added slots have section=null and
// are rendered under "Other".
//
// `commentary` is null for talking-head recordings; when the user picks
// background media (photos/videos) it becomes:
//   { mediaList: [{ id, file, type }], pipConfig: { x, y, w, h, shape } }
// stored as a fraction of the canvas (0..1) so PIP placement survives
// canvas-resolution changes. The presence of commentary toggles the draw
// loop into PIP layout and disables this slot's auto-advance.
const buildInitialSlots = (questions, sections) => {
  const slots = questions.map(() => ({
    id: nextSlotId(),
    status: SLOT_STATUS.EMPTY,
    file: null,
    url: '',
    thumbnail: '',
    section: null,
    commentary: null,
  }));
  if (Array.isArray(sections)) {
    let acc = 0;
    sections.forEach((s) => {
      const len = (s.questions && s.questions.length) || 0;
      for (let i = 0; i < len; i += 1) {
        if (slots[acc + i]) slots[acc + i].section = s.title;
      }
      acc += len;
    });
  }
  return slots;
};

const GiftRecorder = ({
  questions = [],
  recipientName,
  occasionLabel,
  occasions,
  onComplete,
  onCancel,
  // Optional persisted-state snapshot from a previous mount of THIS section.
  // Shape: { liveQuestions: [...], slots: [{ id, status, file, url, thumbnail,
  // section, commentary }, ...] }. When supplied we skip the normal
  // `buildInitialSlots(questions, …)` path and restore exactly what the user
  // last saw — preserves their slot edits (deletions / custom additions) and
  // already-recorded clips across hub ↔ recorder navigation. Snapshots are
  // emitted back via the second arg of onComplete (meta.snapshot) and as the
  // single arg of onCancel (so callers can ignore them safely).
  initialState,
  // Optional sections metadata for the receiver flow. Shape:
  //   [{ title, intro, questions: [{ question }, ...] }, ...]
  // When set:
  //   • headerTitle / headerSubtitle reflect the *current slot's* section so
  //     the page heading updates as the recorder walks through sections.
  //   • The REVIEW grid groups cards by section, with any custom-added
  //     prompts (slot index >= preset count) shown under "Unsectioned".
  //   • onComplete is called with a second arg `{ fileSections: [...] }`
  //     parallel to the files array, so callers can tag uploads with the
  //     right category_name per clip.
  // When null (sender flow), all of the above is bypassed and behavior
  // matches the pre-section version exactly.
  sections = null,
  // When true (receiver flow), the recorder asks the user "Do you have any
  // questions to add?" each time it runs out of preset prompts. Yes opens the
  // custom-question modal and records the new prompt; No proceeds to the
  // unified section-grouped review.
  autoAskMoreAfterPreset = false,
  // When true, the recorder skips the REVIEW phase: as soon as it runs out
  // of empty slots it auto-calls onComplete with the recorded files. Used by
  // the tile-hub flow where review is handled at the host level after the
  // user finishes all sections.
  skipReview = false,
  // When true, the custom-question modal is opened automatically on first
  // mount. Used by the tile hub's "Other" mount so the user lands directly
  // on the add-question dialog instead of an empty slot strip.
  autoOpenAddQuestion = false,
  // When true, the recorder renders its own page-corner Back button that
  // confirms ("your recordings will be lost") before calling onCancel. The
  // receiver flow leaves this false because its host (PersonalVideoMessageFlow)
  // already renders a page-level back. Sender / contributor flows opt in.
  showInRecorderBack = false,
  // When true, the recorder renders a top-right "Finish" button that marks
  // every still-empty/in-flight slot as skipped and emits onComplete with
  // whatever's been recorded so far. Bypasses the review screen — used to
  // bail out of the recording flow when the user is satisfied with their
  // current clips and doesn't want to record the remaining prompts.
  showInRecorderFinish = false,
}) => {
  // ── Refs ────────────────────────────────────────────────────────────
  const liveVideoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const recordedMimeRef = useRef('');

  // Composite stream: canvas drawn from live <video> with subject overlay burned in
  const canvasRef = useRef(null);
  const compositeStreamRef = useRef(null);
  const drawLoopIdRef = useRef(null);
  const lastFrameTimeRef = useRef(0);
  const subjectAnimRef = useRef({ displayed: '', target: '', alpha: 1 });

  // ── Commentary mode plumbing ─────────────────────────────────────────
  // When the active slot has a `commentary` payload, the canvas draws the
  // user-selected media (image or video frame) as the background and renders
  // the webcam as a draggable, resizable, shape-pickable picture-in-picture
  // overlay. The draw loop reads these refs each frame so navigation +
  // PIP-config edits are reflected without rebuilding the stream.
  const commentaryActiveRef = useRef(null);
  const commentaryMediaIndexRef = useRef(0);
  const commentaryMediaUrlsRef = useRef(new Map());
  const commentaryVideoElRef = useRef(null);
  const commentaryImageElRef = useRef(null);
  const pipConfigRef = useRef({
    x: 0.74, y: 0.66, w: 0.22, h: 0.30, shape: 'squircle', // fractions of canvas
  });
  // Remembers the PIP height that existed before the user picked the circle
  // shape. Picking 'circle' overwrites `h` to force a square box, so we stash
  // the original here and restore it when switching back to a non-circle
  // shape — otherwise the box stays stuck at the circle's squashed height.
  const preCircleHeightRef = useRef(null);
  // Web Audio mix: mic (1.0) + active source video (0.2 ducked) → mixDest
  // (recording). The source video also feeds a separate monitor gain at
  // 0.7 → audioCtx.destination so the user can HEAR the video while
  // narrating over it. Mic is intentionally NOT routed to the monitor —
  // playing the mic back through the speakers would cause feedback.
  const audioContextRef = useRef(null);
  const audioMixDestRef = useRef(null);
  const micSourceNodeRef = useRef(null);
  const sourceVideoNodeRef = useRef(null);
  const micGainNodeRef = useRef(null);
  const sourceGainNodeRef = useRef(null);
  const monitorGainNodeRef = useRef(null);

  // ── Guest-invite WebRTC session ──────────────────────────────────
  // Optional feature: the host can invite a remote person to join the
  // recording session live. The guest opens a shareable link, joins
  // via the same /interview/join/:roomId page used by the standalone
  // interview-record feature, and we set up a P2P call here. The
  // host's outgoing audio is the SAME mixed track that feeds the
  // recording (mic + TTS + source video), so when the recorder reads
  // the question aloud the guest hears it too without any extra work.
  // The remote stream is shown in a small PIP overlay on the stage.
  // (Compositing the remote video into the recording canvas is a
  // separate larger change — kept out of this MVP.)
  const [guestPopupOpen, setGuestPopupOpen] = useState(false);
  const [guestRoomId, setGuestRoomId] = useState(null);
  const [guestStatus, setGuestStatus] = useState('idle');
  // 'idle' | 'inviting' | 'connecting' | 'connected' | 'ended'
  const [guestStream, setGuestStream] = useState(null);
  // True while the connected guest is actively talking — drives the
  // primary-colour active-speaker ring on the PIP overlay (Zoom/Meet
  // style). Driven by the Web Audio analyser effect further below.
  const [guestSpeaking, setGuestSpeaking] = useState(false);
  // True while the HOST (this device) is talking — drives the matching
  // ring on the host's own video. Same analyser approach, host mic track.
  const [hostSpeaking, setHostSpeaking] = useState(false);
  // True when the guest's incoming video is taller than it is wide (i.e.
  // they joined on a phone held upright). Lets the PIP overlay flip to a
  // portrait box so a mobile guest isn't cropped into a landscape tile.
  const [guestPortrait, setGuestPortrait] = useState(false);
  const handleGuestVideoSize = useCallback((e) => {
    const v = e.currentTarget;
    if (v && v.videoWidth && v.videoHeight) {
      setGuestPortrait(v.videoHeight > v.videoWidth);
    }
  }, []);
  // Host self mic-mute / camera-off during a guest call. These flip the
  // host's OWN capture tracks (track.enabled) — so muting/blanking applies
  // to whatever the host's mic+camera feed, which is both the guest's view
  // and the recording (it's a single source; there's no separate track).
  const [hostMicMuted, setHostMicMuted] = useState(false);
  const [hostCamOff, setHostCamOff] = useState(false);
  // The guest's reported mic/camera state (signalled over the data channel
  // — a disabled track is invisible to us otherwise). Drives the mic-off
  // badge / camera-off avatar on the guest PIP.
  const [guestMuted, setGuestMuted] = useState(false);
  const [guestCamOff, setGuestCamOff] = useState(false);
  // The guest's chosen display name (from the join lobby, over the data
  // channel). Falls back to "Guest". The host's own name comes from the
  // signed-in profile (first name), falling back to "Host".
  const [guestName, setGuestName] = useState('');
  const displayGuestName = (guestName && guestName.trim()) || 'Guest';
  // Signed-in user's profile name (gift receiver / signup flows). AppContext
  // is optional here — the standalone sender flow may render outside it.
  const { userDetails } = useContext(AppContext) || {};
  const hostName = useMemo(
    () => resolveHostName(userDetails && userDetails.firstname),
    [userDetails && userDetails.firstname],
  );
  const toggleHostMic = useCallback(() => {
    const track = streamRef.current && streamRef.current.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setHostMicMuted(!track.enabled);
  }, []);
  const toggleHostCam = useCallback(() => {
    const track = streamRef.current && streamRef.current.getVideoTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setHostCamOff(!track.enabled);
  }, []);
  const [guestLinkCopied, setGuestLinkCopied] = useState(false);
  const [guestIceServers, setGuestIceServers] = useState([]);
  // Layout for the guest tile.
  //   'pip'           → host fills the canvas, guest in a corner overlay
  //   'side-by-side'  → host left, guest right, both half-width
  // Mirrored into a ref so the closure-captured drawFrame in
  // buildCompositeStream can read it without taking it as a dep.
  const [guestLayout, setGuestLayout] = useState('pip');
  const guestLayoutRef = useRef('pip');
  useEffect(() => { guestLayoutRef.current = guestLayout; }, [guestLayout]);
  // Mirror camera-off state + names into refs so the closure-captured
  // drawFrame in buildCompositeStream can paint the "Camera off" letter
  // avatar into a side-by-side canvas half (there's no DOM tile there).
  const guestCamOffRef = useRef(false);
  useEffect(() => { guestCamOffRef.current = guestCamOff; }, [guestCamOff]);
  const guestNameRef = useRef('Guest');
  useEffect(() => { guestNameRef.current = displayGuestName; }, [displayGuestName]);
  const hostCamOffRef = useRef(false);
  useEffect(() => { hostCamOffRef.current = hostCamOff; }, [hostCamOff]);
  const hostNameRef = useRef('Host');
  useEffect(() => { hostNameRef.current = hostName; }, [hostName]);
  // Hidden <video> bound to guestStream — kept around as the source for
  // drawImage calls in the draw loop. The visible PIP overlay is a
  // SEPARATE element so it can be hidden in side-by-side mode without
  // tearing down the decoder.
  const guestDrawVideoRef = useRef(null);
  // Mirror the live stream into a ref so the draw loop can drawImage
  // from it whenever a frame is ready, without re-binding.
  const guestStreamRef = useRef(null);
  useEffect(() => {
    guestStreamRef.current = guestStream;
    // Keep the hidden draw-source video bound to the latest stream.
    const v = guestDrawVideoRef.current;
    if (v) {
      if (v.srcObject !== guestStream) v.srcObject = guestStream;
      if (guestStream) { try { v.play(); } catch (e) { /* noop */ } }
    }
  }, [guestStream]);
  // Active-speaker detection for the connected guest. A small, recording-
  // independent Web Audio graph watches the guest stream's RMS level and
  // flips guestSpeaking, which the PIP overlay turns into a primary-colour
  // ring. It never touches the recording mix (that has its own graph).
  useEffect(() => {
    if (!guestStream) { setGuestSpeaking(false); return undefined; }
    const AudioCtor = window.AudioContext || window.webkitAudioContext;
    const track = guestStream.getAudioTracks()[0];
    if (!AudioCtor || !track) return undefined;
    const ctx = new AudioCtor();
    if (ctx.state === 'suspended') { try { ctx.resume(); } catch (e) { /* noop */ } }
    let analyser;
    let data;
    try {
      const node = ctx.createMediaStreamSource(new MediaStream([track]));
      analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.6;
      node.connect(analyser);
      data = new Uint8Array(analyser.frequencyBinCount);
    } catch (e) {
      try { ctx.close(); } catch (err) { /* noop */ }
      return undefined;
    }
    const THRESHOLD = 0.05;
    const HOLD_FRAMES = 12;
    let raf = 0;
    let hold = 0;
    let last = false;
    const tick = () => {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i += 1) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      if (rms > THRESHOLD) hold = HOLD_FRAMES;
      else if (hold > 0) hold -= 1;
      const speaking = hold > 0;
      if (speaking !== last) { last = speaking; setGuestSpeaking(speaking); }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      setGuestSpeaking(false);
      try { ctx.close(); } catch (e) { /* noop */ }
    };
  }, [guestStream]);

  // Active-speaker detection for the HOST's own mic — only while a guest is
  // connected (no point ringing the host's camera during a solo recording).
  // Mirrors the guest analyser but reads the local mic track off streamRef.
  useEffect(() => {
    if (!GUEST_INVITE_ENABLED || guestStatus !== 'connected') {
      setHostSpeaking(false);
      return undefined;
    }
    const stream = streamRef.current;
    const track = stream && stream.getAudioTracks()[0];
    const AudioCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtor || !track) return undefined;
    const ctx = new AudioCtor();
    if (ctx.state === 'suspended') { try { ctx.resume(); } catch (e) { /* noop */ } }
    let analyser;
    let data;
    try {
      const node = ctx.createMediaStreamSource(new MediaStream([track]));
      analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.6;
      node.connect(analyser);
      data = new Uint8Array(analyser.frequencyBinCount);
    } catch (e) {
      try { ctx.close(); } catch (err) { /* noop */ }
      return undefined;
    }
    const THRESHOLD = 0.05;
    const HOLD_FRAMES = 12;
    let raf = 0;
    let hold = 0;
    let last = false;
    const tick = () => {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i += 1) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      if (rms > THRESHOLD) hold = HOLD_FRAMES;
      else if (hold > 0) hold -= 1;
      const speaking = hold > 0;
      if (speaking !== last) { last = speaking; setHostSpeaking(speaking); }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      setHostSpeaking(false);
      try { ctx.close(); } catch (e) { /* noop */ }
    };
  }, [guestStatus]);

  // When a guest call ends, re-enable the host's tracks so a self mute /
  // camera-off toggled during the call doesn't silently linger into the
  // rest of a solo recording. Also clear the guest's reported state.
  useEffect(() => {
    if (guestStatus === 'connected') return;
    const stream = streamRef.current;
    if (stream) {
      // eslint-disable-next-line no-param-reassign
      stream.getAudioTracks().forEach((t) => { t.enabled = true; });
      // eslint-disable-next-line no-param-reassign
      stream.getVideoTracks().forEach((t) => { t.enabled = true; });
    }
    setHostMicMuted(false);
    setHostCamOff(false);
    setGuestMuted(false);
    setGuestCamOff(false);
    setGuestName('');
  }, [guestStatus]);

  const guestWsRef = useRef(null);
  const guestPcRef = useRef(null);
  const guestVideoElRef = useRef(null);
  // Data channel to the guest for app metadata (mic/camera state). The
  // rooms WS relay only forwards offer/answer/ice, so custom 'media-state'
  // messages have to ride the peer connection, not the socket.
  const guestDcRef = useRef(null);
  // Latest host media state mirrored into a ref so the data-channel onopen
  // handler reads current values without a stale closure.
  const hostMediaRef = useRef({ muted: false, camOff: false, name: 'Host' });
  useEffect(() => { hostMediaRef.current = { muted: hostMicMuted, camOff: hostCamOff, name: hostName }; }, [hostMicMuted, hostCamOff, hostName]);

  // Signal the host's own mic/camera state to the guest so their call UI
  // can show a "muted" / "camera off" indicator for us (track.enabled is
  // invisible across the wire). Goes over the data channel (the WS relay
  // doesn't forward custom types); the channel's onopen sends the initial
  // state, this effect handles subsequent toggles.
  useEffect(() => {
    if (!GUEST_INVITE_ENABLED || guestStatus !== 'connected') return;
    const dc = guestDcRef.current;
    if (!dc || dc.readyState !== 'open') return;
    try {
      dc.send(JSON.stringify({ type: 'media-state', audioEnabled: !hostMicMuted, videoEnabled: !hostCamOff, name: hostName }));
    } catch (e) { /* noop */ }
  }, [hostMicMuted, hostCamOff, guestStatus, hostName]);

  // Mirror guestRoomId into a ref so the teardown logic (called from
  // multiple places) reads the latest id WITHOUT having to be a
  // useCallback that re-binds on every guestRoomId change. The
  // previous useCallback+useEffect dep loop on guestRoomId caused a
  // cascade of cleanups that fired closeRoom immediately after create.
  const guestRoomIdRef = useRef(null);
  useEffect(() => { guestRoomIdRef.current = guestRoomId; }, [guestRoomId]);

  // Splash (kept mounted for future re-enable; unused while ENABLE_SPLASH_INTRO=false)
  const splashVideoRef = useRef(null);

  // FLIP animation refs — slot strip + camera stage. We look up the target
  // slot by data-slot-index at FLIP time (querySelector) rather than keeping
  // a per-slot ref array, which had ordering issues across rerenders.
  const stageRef = useRef(null);
  const slotStripRef = useRef(null);

  // Onstop branching — set true to throw away the current clip (Skip)
  const discardCurrentRef = useRef(false);
  // Slot index the current MediaRecorder is filling
  const recordingTargetRef = useRef(-1);
  // Re-record flag — when stopping the recorder for a re-take of the SAME
  // slot, onstop should drop chunks without flying into a slot or skipping.
  const rerecordRef = useRef(false);

  // ── State ───────────────────────────────────────────────────────────
  const [permissionState, setPermissionState] = useState('pending');
  const [permissionError, setPermissionError] = useState('');

  // `questions` is the seed; `liveQuestions` is the working list users can
  // append custom prompts to via the "+ Add Question" button in the slot strip.
  const hasInitialStateRef = useRef(!!(initialState
    && (initialState.liveQuestions || initialState.slots)));
  const [liveQuestions, setLiveQuestions] = useState(
    hasInitialStateRef.current && initialState.liveQuestions
      ? initialState.liveQuestions
      : questions,
  );
  // Mirror to a ref so refs-only callbacks (prep ticker speech, etc.) can
  // read the latest array without taking it as a hook dep.
  const liveQuestionsRef = useRef(
    hasInitialStateRef.current && initialState.liveQuestions
      ? initialState.liveQuestions
      : questions,
  );
  useEffect(() => { liveQuestionsRef.current = liveQuestions; }, [liveQuestions]);
  const [slots, setSlots] = useState(() => {
    if (hasInitialStateRef.current && initialState.slots) {
      // The previous mount revoked its object URLs on unmount, so mint
      // fresh ones from the still-valid File for any DONE slot. slot.url
      // is only consumed by the REVIEW-phase preview (skipReview flows
      // bypass it), so this is defensive — keeps restored slots in a
      // playable state if a future flow ever renders the strip preview.
      return initialState.slots.map((s) => {
        if (s && s.file && s.status === SLOT_STATUS.DONE) {
          try {
            return { ...s, url: URL.createObjectURL(s.file) };
          } catch (e) {
            return s;
          }
        }
        return s;
      });
    }
    return buildInitialSlots(questions, sections);
  });
  // Mirror slots into a ref so closures captured at first render (e.g. the
  // useCallback([]) startActualRecording) can still read the latest slot
  // state when computing per-slot canvas dims.
  const slotsRef = useRef(slots);
  useEffect(() => { slotsRef.current = slots; }, [slots]);
  const [currentSlot, setCurrentSlot] = useState(0);

  // Snapshot the current liveQuestions + slots so the host (e.g.
  // PersonalVideoMessageFlow) can persist it per-section and restore the
  // user's exact slot layout + recorded clips on re-entry. Reads from
  // refs so it's safe to call from outside-React paths (onstop, settle
  // effect) and from handlers declared further down in this component.
  const buildSnapshot = () => ({
    liveQuestions: liveQuestionsRef.current
      ? liveQuestionsRef.current.slice()
      : [],
    slots: (slotsRef.current || []).map((s) => ({ ...s })),
  });

  // One-shot guard so the skipReview settle-effect can't double-fire after
  // it's already emitted onComplete/onCancel for this mount. Declared up
  // here (not at the effect site) so handleFinishAll can flip it BEFORE
  // calling onComplete directly — otherwise the effect would still see
  // "all settled" on the next commit and fire a second onComplete.
  const skipReviewSettledRef = useRef(false);

  // Custom-question modal state. The subject is no longer asked — we derive
  // it from the question text (first ~50 chars). The suggestions dropdown
  // shows occasion-specific prompts when the field is focused.
  const [customAddOpen, setCustomAddOpen] = useState(false);
  const [customQuestion, setCustomQuestion] = useState('');
  const [customFocused, setCustomFocused] = useState(false);
  // When true, submitting the custom-question modal also kicks off recording
  // for the newly added slot (used by the "+ Add Question" tile on the
  // REVIEW page so the user goes straight into PREP).
  const [addAndRecord, setAddAndRecord] = useState(false);
  // Holds the slot index that should auto-start recording AFTER the next
  // liveQuestions/slots commit. We can't call beginRecordingForSlot inline
  // because the closure reads stale liveQuestions; the useEffect below
  // picks this up once the state has landed.
  const pendingRecordIndexRef = useRef(-1);
  // Receiver flow: section title to tag onto the next custom-added slot, so
  // tapping "+ Add Question" from inside Beginning's review (etc.) lands the
  // new prompt back in that section instead of always under "Other".
  const pendingCustomSectionRef = useRef(null);

  const [phase, setPhase] = useState(PHASE.IDLE);
  // Receiver flow: when true, the REVIEW phase renders a "Do you have any
  // questions to add?" prompt instead of the section-grouped review grid.
  // Set true the first time we enter REVIEW with autoAskMoreAfterPreset on,
  // then never re-fired (see receiverAskMoreFired) — after the user makes
  // their first Yes/No choice we go straight to the review on subsequent
  // REVIEW entries.
  const [receiverAskMore, setReceiverAskMore] = useState(false);
  const [receiverAskMoreFired, setReceiverAskMoreFired] = useState(false);
  const [prepCountdown, setPrepCountdown] = useState(0); // 5..1, 0 = inactive
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [durationCap, setDurationCap] = useState(DEFAULT_DURATION_CAP_S);
  const [warningDismissed, setWarningDismissed] = useState(false);
  // True while the in-flight recording is paused (MediaRecorder.pause()).
  // The elapsed-seconds ticker freezes and the canvas keeps painting, but no
  // new chunks are produced until resume.
  const [recordingPaused, setRecordingPaused] = useState(false);

  // ── Commentary mode UI state ─────────────────────────────────────────
  // Files awaiting the order modal — set by the file picker, cleared on
  // Apply (commit to slot) or Cancel.
  const [commentaryPendingFiles, setCommentaryPendingFiles] = useState([]);
  // Commentary recording resolution cap (height in pixels). Default 1080.
  // When commentary is active for a slot, the composite canvas is resized
  // to (cap × 16/9, cap) right before MediaRecorder starts, so the source
  // media's higher resolution is preserved instead of being downscaled to
  // the webcam's 720p. Talking-head mode (no commentary) keeps the webcam
  // dims for performance / file-size reasons.
  // Options: 1080 (HD), 1440 (2K), 2160 (4K).
  const [commentaryQualityCap, setCommentaryQualityCap] = useState(1080);
  // Mirror to a ref so startActualRecording (useCallback([])) reads the
  // latest cap when computing target canvas dims, even though it captured
  // its scope at first render.
  const commentaryQualityCapRef = useRef(1080);
  useEffect(() => { commentaryQualityCapRef.current = commentaryQualityCap; }, [commentaryQualityCap]);
  // Remember the webcam-derived canvas dims set in buildCompositeStream so
  // resizeCompositeCanvas can restore them when leaving commentary mode.
  const webcamCanvasDimsRef = useRef({ w: 1280, h: 720 });
  // Max source height (pixels) across the files currently pending in the
  // order modal. Probed via a hidden <video>/<img> for each file. Drives
  // which Recording-quality options the dropdown shows — no point offering
  // 4K when the user only picked 1080p clips.
  const [commentarySourceMaxHeight, setCommentarySourceMaxHeight] = useState(0);
  // Ordering modal visibility — uses react-beautiful-dnd to reorder thumbs.
  const [commentaryOrderOpen, setCommentaryOrderOpen] = useState(false);

  // Probe pending files' native resolution so the order-modal dropdown can
  // hide quality options the sources don't justify (no 4K when the user
  // only picked HD clips). Fires whenever the modal opens or its file list
  // changes. Bails early when there's nothing to probe.
  useEffect(() => {
    if (!commentaryOrderOpen || commentaryPendingFiles.length === 0) {
      setCommentarySourceMaxHeight(0);
      return undefined;
    }
    let cancelled = false;
    const probeFile = (file) => new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const cleanup = () => { try { URL.revokeObjectURL(url); } catch (e) { /* noop */ } };
      const isVideo = (file.type || '').startsWith('video/');
      const isImage = (file.type || '').startsWith('image/');
      if (isVideo) {
        const v = document.createElement('video');
        v.preload = 'metadata';
        v.muted = true;
        v.onloadedmetadata = () => { const h = v.videoHeight || 0; cleanup(); resolve(h); };
        v.onerror = () => { cleanup(); resolve(0); };
        v.src = url;
      } else if (isImage) {
        const img = new Image();
        img.onload = () => { const h = img.naturalHeight || 0; cleanup(); resolve(h); };
        img.onerror = () => { cleanup(); resolve(0); };
        img.src = url;
      } else {
        cleanup();
        resolve(0);
      }
    });
    Promise.all(commentaryPendingFiles.map(probeFile)).then((heights) => {
      if (cancelled) return;
      const maxH = heights.reduce((a, b) => (b > a ? b : a), 0);
      setCommentarySourceMaxHeight(maxH);
    });
    return () => { cancelled = true; };
  }, [commentaryOrderOpen, commentaryPendingFiles]);

  // Derive which cap levels are offered. Always include 1080p (the safe
  // default) so the dropdown is never empty.
  // "Native" — exposed only when the source resolution sits between the
  // standard tiers (e.g. 3.2K ≈ 1800p) so the user can pick the actual
  // source height instead of being forced down to a standard tier. Hard
  // capped at 2160p (4K) for browser/encoder safety.
  const HARD_CAP_H = 2160;
  const nativeCapH = Math.min(commentarySourceMaxHeight, HARD_CAP_H);
  const isStandardTier = (h) => h === 1080 || h === 1440 || h === 2160;
  const showNativeOption = nativeCapH > 1080 && !isStandardTier(nativeCapH);
  const availableQualityCaps = (() => {
    const caps = [1080];
    if (commentarySourceMaxHeight >= 1440) caps.push(1440);
    if (commentarySourceMaxHeight >= 2160) caps.push(2160);
    if (showNativeOption) caps.push(nativeCapH);
    // Sort ascending so the dropdown reads HD → 2K → Native → 4K.
    return Array.from(new Set(caps)).sort((a, b) => a - b);
  })();

  // If the user previously set 4K/2K but the new file batch doesn't reach
  // that resolution, clamp down to the highest available so the visible
  // dropdown value matches the actual cap.
  useEffect(() => {
    if (!availableQualityCaps.includes(commentaryQualityCap)) {
      const fallback = availableQualityCaps[availableQualityCaps.length - 1];
      setCommentaryQualityCap(fallback);
    }
    // intentionally depend only on max height — availableQualityCaps is
    // derived inline each render from that.
  }, [commentarySourceMaxHeight]);
  // Active media index within the current slot's commentary list. Drives
  // both the canvas background AND the floating control strip indicator.
  const [commentaryMediaIndex, setCommentaryMediaIndex] = useState(0);
  // Play state of the active VIDEO media. Photos ignore this; the control
  // strip's play/pause button toggles this for video items.
  const [commentaryIsPlaying, setCommentaryIsPlaying] = useState(false);
  // Forces a re-render after pip config edits without re-mounting refs.
  const [pipConfigVersion, setPipConfigVersion] = useState(0);
  // Hidden file input — opens via the "Add media" prep-panel button.
  const commentaryFileInputRef = useRef(null);
  // Live playback tracking for the controller bar's seek + time readout.
  // timeupdate fires ~4x/sec so this is cheap.
  const [commentaryVideoTime, setCommentaryVideoTime] = useState(0);
  const [commentaryVideoDuration, setCommentaryVideoDuration] = useState(0);
  // Flag set by the Prev/Next handlers — the auto-play effect below picks
  // it up after React commits the new `src` to the hidden video element.
  // Reset on consume so initial-mount (no nav) doesn't auto-play.
  const shouldAutoPlayNextRef = useRef(false);
  // Mute toggle for source video audio in the recording. Default unmuted
  // (mic+ducked source mix). When muted, sourceGain ramps to 0 so the clip
  // is mic-only.
  const [commentarySourceMuted, setCommentarySourceMuted] = useState(false);

  // Mobile-camera-as-webcam integration. Default 'desktop' — the existing
  // getUserMedia path runs on mount. Switching to 'mobile' swaps streamRef
  // in place once a phone pairing connects, leaving the rest of the
  // composite-stream + MediaRecorder pipeline untouched.
  const [cameraSource, setCameraSource] = useState('desktop'); // 'desktop' | 'mobile'
  const [qrOpen, setQrOpen] = useState(false);
  // When the paired phone disconnects (user taps Disconnect on the phone, or
  // the connection drops), we surface a modal here rather than silently
  // freezing on the last frame. Acknowledging it falls back to the web camera.
  const [mobileDisconnected, setMobileDisconnected] = useState(false);
  const mcSession = useMobileCameraSession();

  // Flying clip — the slot's own thumbnail does the FLIP. We render it inside
  // the slot from the moment the clip lands; on the first frame we apply a
  // transform that visually places it at the camera, then on the next frame
  // flip to identity. Because the element lives inside the slot, the END
  // position is guaranteed correct (it's the slot's natural position, not a
  // computed fixed-position rect that could drift due to ancestor containing
  // blocks, scrollbars, or layout shifts).
  const [flyingClip, setFlyingClip] = useState(null); // { slotIndex, transform }

  // Inline review players (one per slot) — track which slot is playing
  const [playingSlot, setPlayingSlot] = useState(-1);

  // ── Mobile recording UX ───────────────────────────────────────────────
  // On phones the side-by-side "question column + camera" layout is cramped,
  // so we switch to a full-width vertical list of question tiles. Tapping a
  // tile opens the camera in a fullscreen 3D modal (the camera stage is
  // portaled to <body> — see getStagePortalTarget). When the clip for that
  // tile lands (or is skipped / cancelled) the modal closes and the user is
  // back on the tile list, free to tap the next question. Desktop (>720px)
  // keeps the original two-column layout untouched.
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 720px)').matches
      : false));
  const [mobileSessionOpen, setMobileSessionOpen] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(max-width: 720px)');
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Switch-mid-recording confirm modal. When non-null, the modal is open and
  // the user is being asked whether to save or dump the in-flight clip before
  // jumping to `toSlot`.
  const [pendingSwitch, setPendingSwitch] = useState(null); // { fromSlot, toSlot } | null

  // When a switch-save is in progress, this holds the slot we should jump to
  // AFTER the current clip has landed (via flyClipIntoSlot → advanceFromSlot).
  // advanceFromSlot reads this to override its default "next empty" scan.
  const pendingTargetAfterClipRef = useRef(-1);

  // Back-button confirmation visibility (sender / contributor flows only —
  // gated by the showInRecorderBack prop). Open = the warning modal is up.
  const [pendingBackExit, setPendingBackExit] = useState(false);

  // "Ask question aloud" — when ON, the current slot's question is spoken
  // via the browser SpeechSynthesis API the moment recording actually starts
  // (after the 3-2-1 countdown). The mic naturally picks the speaker audio
  // up, so the question ends up baked into the clip (Q-then-A format).
  // Default ON, per-session only (no localStorage).
  const [askAloud, setAskAloud] = useState(true);
  // Refs to read latest values from inside effects that we don't want
  // re-running every time the toggle / questions array changes.
  const askAloudRef = useRef(true);
  useEffect(() => { askAloudRef.current = askAloud; }, [askAloud]);

  // ── Callback ref for the live <video> so srcObject re-attaches across remounts ──
  const setLiveVideoNode = useCallback((node) => {
    liveVideoRef.current = node;
    if (node && streamRef.current) {
      try {
        // eslint-disable-next-line no-param-reassign
        node.srcObject = streamRef.current;
      } catch (e) { /* noop */ }
    }
  }, []);

  // ── Reset slots when the questions prop changes shape (e.g., new occasion) ──
  // Skipped on the very first run when initialState was provided — that
  // path already initialized liveQuestions/slots from the snapshot, so a
  // reset here would wipe the user's restored work. Subsequent length
  // changes still run (covers the live-occasion-switch case).
  const skipFirstQuestionsResetRef = useRef(hasInitialStateRef.current);
  useEffect(() => {
    if (skipFirstQuestionsResetRef.current) {
      skipFirstQuestionsResetRef.current = false;
      return;
    }
    setLiveQuestions(questions);
    setSlots(buildInitialSlots(questions, sections));
    setCurrentSlot(0);
    setPhase(PHASE.IDLE);
    setElapsedSeconds(0);
    setDurationCap(DEFAULT_DURATION_CAP_S);
    setWarningDismissed(false);
  }, [questions.length]);

  // ── Acquire camera + mic ──
  useEffect(() => {
    let cancelled = false;
    const startCamera = async () => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setPermissionState('denied');
        setPermissionError('Your browser does not support camera access. Please use Chrome, Firefox, Safari, or Edge.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
          audio: true
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (liveVideoRef.current) liveVideoRef.current.srcObject = stream;
        setPermissionState('granted');
      } catch (err) {
        setPermissionState('denied');
        const msg = err?.name === 'NotAllowedError'
          ? 'Camera & microphone permission was blocked. Please allow access in your browser settings to record.'
          : err?.message || 'Could not access camera or microphone.';
        setPermissionError(msg);
      }
    };
    startCamera();

    return () => {
      cancelled = true;
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch (e) { /* noop */ }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // ── Swap the live stream the recorder draws from. Used to switch between
  // desktop camera and the paired mobile camera at runtime. The composite
  // stream's video track is canvas-derived and stays stable; we only need
  // to update srcObject on the live <video> (so the canvas's drawImage
  // pulls the new frames) and replace the audio track on the composite. ──
  const swapLiveStream = useCallback((newStream, { stopOldTracks }) => {
    const old = streamRef.current;
    streamRef.current = newStream;
    if (liveVideoRef.current) {
      try { liveVideoRef.current.srcObject = newStream; } catch (e) { /* noop */ }
    }
    const composite = compositeStreamRef.current;
    if (composite) {
      composite.getAudioTracks().forEach((t) => {
        try { composite.removeTrack(t); } catch (e) { /* noop */ }
      });
      const newAudio = newStream.getAudioTracks()[0];
      if (newAudio) {
        try { composite.addTrack(newAudio); } catch (e) { /* noop */ }
      }
    }
    // If a guest is connected, hand the new camera to their peer connection
    // too — otherwise the guest keeps the OLD (now-stopped) video track and
    // sees a frozen last frame after a desktop⇆phone camera swap. The guest's
    // audio rides the canvas-derived mix track, which is stable, so only the
    // video sender needs swapping.
    const guestPc = guestPcRef.current;
    if (guestPc) {
      const newVideo = newStream.getVideoTracks()[0];
      if (newVideo) {
        const sender = guestPc.getSenders().find((s) => s.track && s.track.kind === 'video');
        if (sender) { try { sender.replaceTrack(newVideo).catch(() => {}); } catch (e) { /* noop */ } }
      }
    }
    if (stopOldTracks && old && old !== newStream) {
      old.getTracks().forEach((t) => { try { t.stop(); } catch (e) { /* noop */ } });
    }
  }, []);

  // When the mobile session reaches CONNECTED, swap to its stream + close QR.
  useEffect(() => {
    if (cameraSource !== 'mobile') return;
    if (mcSession.status === MC_STATUS.CONNECTED && mcSession.stream) {
      swapLiveStream(mcSession.stream, { stopOldTracks: true });
      setQrOpen(false);
    }
  }, [cameraSource, mcSession.status, mcSession.stream, swapLiveStream]);

  // Re-acquire the desktop camera + audio. Used when the user switches
  // back from the mobile camera or the mobile session dies.
  const reacquireDesktopCamera = useCallback(async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
        audio: true,
      });
      swapLiveStream(stream, { stopOldTracks: true });
    } catch (err) { /* noop — UI will show the existing permission-denied state */ }
  }, [swapLiveStream]);

  const handlePickDesktopCamera = async () => {
    if (cameraSource === 'desktop') return;
    setCameraSource('desktop');
    setQrOpen(false);
    await mcSession.disconnect();
    await reacquireDesktopCamera();
  };

  const handlePickMobileCamera = async () => {
    if (cameraSource === 'mobile') return;
    setMobileDisconnected(false);
    setCameraSource('mobile');
    setQrOpen(true);
    await mcSession.start();
  };

  const handleQrClose = async () => {
    setQrOpen(false);
    // If the user closed the modal before pairing, drop back to desktop.
    if (mcSession.status !== MC_STATUS.CONNECTED) {
      await mcSession.disconnect();
      setCameraSource('desktop');
      await reacquireDesktopCamera();
    }
  };

  const handleQrRetry = async () => {
    await mcSession.disconnect();
    await mcSession.start();
  };

  // Acknowledge the phone disconnect: tear the session down and fall back to
  // the desktop camera so the user can keep recording in the browser.
  const handleMobileDisconnectAck = async () => {
    setMobileDisconnected(false);
    setQrOpen(false);
    setCameraSource('desktop');
    await mcSession.disconnect();
    await reacquireDesktopCamera();
  };

  // Reconnect the phone after a drop: dismiss the disconnected modal and
  // reopen the QR so a phone can pair again. Stay on the 'mobile' source —
  // a fresh session() is started (new sessionId → new QR), and the
  // CONNECTED effect swaps in the new phone's stream once it pairs.
  const handleMobileReconnect = async () => {
    setMobileDisconnected(false);
    setCameraSource('mobile');
    setQrOpen(true);
    await mcSession.disconnect();
    await mcSession.start();
  };

  // If the mobile session drops (phone tapped Disconnect, closed the tab, or
  // lost the network), surface a modal instead of leaving the last decoded
  // frame frozen on screen. The user acknowledges, then we swap back to the
  // desktop camera. We trigger on a real DISCONNECTED/FAILED WebRTC state;
  // the explicit phone "disconnect" command (handled in the command router)
  // catches the intentional case faster than the connection timeout would.
  useEffect(() => {
    if (cameraSource !== 'mobile') return;
    if (mcSession.status === MC_STATUS.DISCONNECTED || mcSession.status === MC_STATUS.FAILED) {
      setMobileDisconnected(true);
    }
  }, [cameraSource, mcSession.status]);

  // ── Revoke slot blob URLs on unmount. We read via ref so the cleanup sees the
  // latest slots without the effect re-running on every state change. ──
  const slotsForCleanupRef = useRef(slots);
  useEffect(() => { slotsForCleanupRef.current = slots; }, [slots]);
  useEffect(() => () => {
    slotsForCleanupRef.current.forEach((s) => { if (s.url) URL.revokeObjectURL(s.url); });
  }, []);

  // ── Commentary refs sync: the draw loop reads ONLY from refs, so any
  // state change (slot switch, media navigation, PIP edit) must mirror into
  // the refs. Decoupling like this lets us avoid rebuilding the canvas
  // stream when only commentary state shifts. ──
  useEffect(() => {
    const slot = slots[currentSlot];
    commentaryActiveRef.current = (slot && slot.commentary) || null;
    if (slot && slot.commentary && slot.commentary.pipConfig) {
      pipConfigRef.current = { ...slot.commentary.pipConfig };
    }
  }, [currentSlot, slots]);

  useEffect(() => {
    commentaryMediaIndexRef.current = commentaryMediaIndex;
  }, [commentaryMediaIndex]);

  // Reset media index when switching slots so the new slot starts at item 0.
  useEffect(() => {
    setCommentaryMediaIndex(0);
    setCommentaryIsPlaying(false);
    setCommentaryVideoTime(0);
    setCommentaryVideoDuration(0);
  }, [currentSlot]);
  // Reset playback time when switching to a different media item.
  useEffect(() => {
    setCommentaryVideoTime(0);
    setCommentaryVideoDuration(0);
  }, [commentaryMediaIndex]);

  // Revoke every cached blob URL on unmount.
  useEffect(() => () => {
    commentaryMediaUrlsRef.current.forEach((url) => {
      try { URL.revokeObjectURL(url); } catch (e) { /* ignore */ }
    });
    commentaryMediaUrlsRef.current.clear();
  }, []);

  // ── Tear down composite draw loop on unmount ──
  useEffect(() => () => {
    if (drawLoopIdRef.current !== null) {
      cancelAnimationFrame(drawLoopIdRef.current);
      drawLoopIdRef.current = null;
    }
    if (compositeStreamRef.current) {
      compositeStreamRef.current.getVideoTracks().forEach((t) => {
        try { t.stop(); } catch (e) { /* noop */ }
      });
      compositeStreamRef.current = null;
    }
  }, []);

  // ── Push current slot's subject into the rAF subject animator ──
  useEffect(() => {
    const next = getSubject(liveQuestions[currentSlot]);
    if (subjectAnimRef.current.target !== next) {
      subjectAnimRef.current.target = next;
    }
  }, [currentSlot, liveQuestions]);

  // ── Subject overlay drawing ──
  const drawSubjectOverlay = (ctx, text, alpha, canvasW, canvasH) => {
    if (!text || alpha <= 0.001) return;
    ctx.save();
    ctx.globalAlpha = alpha;
    const fontSize = Math.max(18, Math.round(canvasH * 0.038));
    ctx.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif`;
    ctx.textBaseline = 'middle';
    const rightPad = fontSize * 1.5;
    const padY = fontSize * 0.55;
    const fadeRunway = fontSize * 2.8;
    const textWidth = ctx.measureText(text).width;
    const boxH = fontSize * 1.2 + padY * 2;
    const boxW = textWidth + rightPad + fadeRunway;
    const margin = Math.max(12, Math.round(canvasH * 0.035));
    const boxX = canvasW - boxW - margin;
    const boxY = canvasH - boxH - margin;

    const grad = ctx.createLinearGradient(boxX, 0, boxX + boxW, 0);
    grad.addColorStop(0, 'rgba(15, 17, 22, 0)');
    grad.addColorStop(0.45, 'rgba(15, 17, 22, 0.45)');
    grad.addColorStop(1, 'rgba(15, 17, 22, 0.78)');

    const r = boxH / 2;
    ctx.beginPath();
    ctx.moveTo(boxX + r, boxY);
    ctx.lineTo(boxX + boxW - r, boxY);
    ctx.quadraticCurveTo(boxX + boxW, boxY, boxX + boxW, boxY + r);
    ctx.lineTo(boxX + boxW, boxY + boxH - r);
    ctx.quadraticCurveTo(boxX + boxW, boxY + boxH, boxX + boxW - r, boxY + boxH);
    ctx.lineTo(boxX + r, boxY + boxH);
    ctx.quadraticCurveTo(boxX, boxY + boxH, boxX, boxY + boxH - r);
    ctx.lineTo(boxX, boxY + r);
    ctx.quadraticCurveTo(boxX, boxY, boxX + r, boxY);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();

    ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
    ctx.shadowBlur = 6;
    ctx.shadowOffsetY = 1;
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'right';
    ctx.fillText(text, boxX + boxW - rightPad, boxY + boxH / 2);
    ctx.restore();
  };

  // ── Build composite stream once on permission grant; lives until unmount ──
  const buildCompositeStream = (canvasEl, sourceVideoEl) => {
    if (!streamRef.current || !sourceVideoEl) return null;

    const videoTrack = streamRef.current.getVideoTracks()[0];
    const settings = (videoTrack && videoTrack.getSettings && videoTrack.getSettings()) || {};
    const w = settings.width || 1280;
    const h = settings.height || 720;
    // eslint-disable-next-line no-param-reassign
    canvasEl.width = w;
    // eslint-disable-next-line no-param-reassign
    canvasEl.height = h;
    // Remember webcam-derived dims so resizeCompositeCanvas() can restore
    // them when leaving commentary mode.
    webcamCanvasDimsRef.current = { w, h };

    const ctx = canvasEl.getContext('2d');
    const initial = getSubject(liveQuestions[0]);
    subjectAnimRef.current = { displayed: initial, target: initial, alpha: 1 };
    lastFrameTimeRef.current = 0;

    // Contain-fit: scale source to fit ENTIRELY inside the canvas without
    // cropping. Letterboxes with the canvas background (black) on the axis
    // that doesn't fill. Uses the closure `ctx` so we avoid mutating a
    // context passed as a parameter (no-param-reassign).
    const drawContainFit = (src, srcW, srcH, dstW, dstH) => {
      if (!srcW || !srcH) return;
      const fit = Math.min(dstW / srcW, dstH / srcH);
      const dw = srcW * fit;
      const dh = srcH * fit;
      const dx = (dstW - dw) / 2;
      const dy = (dstH - dh) / 2;
      ctx.drawImage(src, dx, dy, dw, dh);
    };
    // Path generator for the PIP shape. shape ∈ {'circle','squircle','rect'}.
    // For 'circle': we draw an ELLIPSE filling the full ww×hh bounding box
    // (not an inscribed circle). The setPipConfig() constraint locks
    // cfg.h = cfg.w × canvasAspect when shape is circle, which forces the
    // CSS-rendered bounding box to be a square — and the ellipse + the
    // overlay's border-radius:50% then both resolve to a perfect circle on
    // screen, even when canvas pixel buffer aspect ≠ CSS aspect.
    const tracePipShape = (x, y, ww, hh, shape) => {
      ctx.beginPath();
      if (shape === 'circle') {
        const cx = x + ww / 2;
        const cy = y + hh / 2;
        ctx.ellipse(cx, cy, ww / 2, hh / 2, 0, 0, Math.PI * 2);
      } else if (shape === 'rect') {
        ctx.rect(x, y, ww, hh);
      } else {
        // squircle / rounded rect — pleasant default
        const r = Math.min(24, ww * 0.18, hh * 0.18);
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + ww, y, x + ww, y + hh, r);
        ctx.arcTo(x + ww, y + hh, x, y + hh, r);
        ctx.arcTo(x, y + hh, x, y, r);
        ctx.arcTo(x, y, x + ww, y, r);
        ctx.closePath();
      }
    };
    // Draw the webcam as a clipped, mirror-flipped picture-in-picture.
    const drawPip = (webcam, dstW, dstH, cfg) => {
      const px = (cfg.x || 0) * dstW;
      const py = (cfg.y || 0) * dstH;
      const pw = (cfg.w || 0.22) * dstW;
      const ph = (cfg.h || 0.30) * dstH;
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.45)';
      ctx.shadowBlur = 18;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 6;
      ctx.fillStyle = '#000';
      tracePipShape(px, py, pw, ph, cfg.shape || 'squircle');
      ctx.fill();
      ctx.restore();
      ctx.save();
      tracePipShape(px, py, pw, ph, cfg.shape || 'squircle');
      ctx.clip();
      const sw = webcam.videoWidth || pw;
      const sh = webcam.videoHeight || ph;
      const fit = Math.max(pw / sw, ph / sh);
      const dw = sw * fit;
      const dh = sh * fit;
      const dx = px + (pw - dw) / 2;
      const dy = py + (ph - dh) / 2;
      ctx.translate(dx + dw, dy);
      ctx.scale(-1, 1);
      ctx.drawImage(webcam, 0, 0, dw, dh);
      ctx.restore();
      ctx.save();
      ctx.strokeStyle = 'rgba(255,255,255,0.85)';
      ctx.lineWidth = 2;
      tracePipShape(px, py, pw, ph, cfg.shape || 'squircle');
      ctx.stroke();
      ctx.restore();
    };

    const FADE_MS = 250;
    // Black fill so letterbox bars are visible behind aspect-fit video.
    const drawFrame = (now) => {
      if (drawLoopIdRef.current === null) return;
      // Read canvas dims FRESH each frame so the draw loop adapts when
      // resizeCompositeCanvas() bumps the resolution (commentary-quality
      // toggle). Shadows the outer captured w/h.
      // eslint-disable-next-line no-shadow
      const w = canvasEl.width;
      // eslint-disable-next-line no-shadow
      const h = canvasEl.height;
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, w, h);

      const commentary = commentaryActiveRef.current;
      const isCommentary = !!(commentary && commentary.mediaList && commentary.mediaList.length);

      if (isCommentary) {
        // Commentary mode: active media fills the canvas; webcam shrinks to PIP.
        const idx = Math.min(
          Math.max(0, commentaryMediaIndexRef.current || 0),
          commentary.mediaList.length - 1,
        );
        const item = commentary.mediaList[idx];
        try {
          if (item && item.type === 'video') {
            const vid = commentaryVideoElRef.current;
            if (vid && vid.readyState >= 2) {
              drawContainFit(vid, vid.videoWidth, vid.videoHeight, w, h);
            }
          } else if (item && item.type === 'image') {
            const img = commentaryImageElRef.current;
            if (img && img.complete && img.naturalWidth) {
              drawContainFit(img, img.naturalWidth, img.naturalHeight, w, h);
            }
          }
        } catch (e) { /* noop */ }
        try {
          if (sourceVideoEl.readyState >= 2) {
            drawPip(sourceVideoEl, w, h, pipConfigRef.current);
          }
        } catch (e) { /* noop */ }
      } else {
        // Talking-head mode (default). Branches further on whether a
        // guest is connected AND the user picked side-by-side layout.
        const guestVidEl = guestDrawVideoRef.current;
        const hasGuestFrame = guestVidEl && guestVidEl.readyState >= 2
          && guestVidEl.videoWidth > 0 && guestVidEl.videoHeight > 0;
        // Stay in side-by-side even with no guest frame when the guest has
        // their camera off — we paint their avatar into the half instead.
        const sideBySide = guestLayoutRef.current === 'side-by-side'
          && (hasGuestFrame || guestCamOffRef.current);

        if (sideBySide) {
          // Split the canvas in two vertical halves. Host on the left,
          // guest on the right, each contain-fit into its half with
          // letterboxing where aspect doesn't match. A 1px gutter line
          // is intentionally omitted — the black canvas background
          // already shows through letterbox bars.
          const halfW = Math.floor(w / 2);
          const drawTileContain = (el, dx, dy, dw, dh) => {
            const sw = el.videoWidth || dw;
            const sh = el.videoHeight || dh;
            const fit = Math.min(dw / sw, dh / sh);
            const tw = sw * fit;
            const th = sh * fit;
            const tx = dx + (dw - tw) / 2;
            const ty = dy + (dh - th) / 2;
            try { ctx.drawImage(el, tx, ty, tw, th); } catch (e) { /* noop */ }
          };
          // A camera-off track keeps emitting black frames, so instead of
          // painting black we draw the same letter-avatar + name the DOM
          // tiles show. No DOM tile exists in side-by-side — this is the
          // only place the avatar can appear (and it's baked into the file).
          const drawAvatarTile = (dx, dy, dw, dh, name) => {
            ctx.save();
            ctx.fillStyle = '#14161d';
            ctx.fillRect(dx, dy, dw, dh);
            const cx = dx + dw / 2;
            const cy = dy + dh / 2;
            const r = Math.max(26, Math.min(dw, dh) * 0.16);
            const grad = ctx.createLinearGradient(cx - r, cy - r, cx + r, cy + r);
            grad.addColorStop(0, '#5aa5ef');
            grad.addColorStop(1, '#3f78c9');
            ctx.beginPath();
            ctx.arc(cx, cy - r * 0.45, r, 0, Math.PI * 2);
            ctx.fillStyle = grad;
            ctx.fill();
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = '#fff';
            ctx.font = `700 ${Math.floor(r)}px sans-serif`;
            ctx.fillText(((name || '?').trim().charAt(0) || '?').toUpperCase(), cx, cy - r * 0.45);
            ctx.fillStyle = '#e6e7ea';
            ctx.font = `700 ${Math.floor(r * 0.42)}px sans-serif`;
            ctx.fillText(name || 'Guest', cx, cy + r * 0.95);
            ctx.restore();
          };
          if (hostCamOffRef.current) {
            drawAvatarTile(0, 0, halfW, h, hostNameRef.current);
          } else {
            try {
              if (sourceVideoEl.readyState >= 2) {
                drawTileContain(sourceVideoEl, 0, 0, halfW, h);
              }
            } catch (e) { /* noop */ }
          }
          if (guestCamOffRef.current) {
            drawAvatarTile(halfW, 0, w - halfW, h, guestNameRef.current);
          } else {
            drawTileContain(guestVidEl, halfW, 0, w - halfW, h);
          }
        } else {
          // Solo host fills the canvas — original behavior. (PIP mode
          // when guest is connected uses this branch + a DOM PIP
          // overlay on top of the canvas.)
          try {
            if (sourceVideoEl.readyState >= 2) {
              const sw = sourceVideoEl.videoWidth || w;
              const sh = sourceVideoEl.videoHeight || h;
              const fit = Math.min(w / sw, h / sh);
              const dw = sw * fit;
              const dh = sh * fit;
              const dx = (w - dw) / 2;
              const dy = (h - dh) / 2;
              ctx.drawImage(sourceVideoEl, dx, dy, dw, dh);
            }
          } catch (e) { /* noop */ }
        }
      }

      const last = lastFrameTimeRef.current || now;
      const dt = Math.min(now - last, 100);
      lastFrameTimeRef.current = now;

      const anim = subjectAnimRef.current;
      if (anim.target !== anim.displayed) {
        anim.alpha -= dt / FADE_MS;
        if (anim.alpha <= 0) {
          anim.alpha = 0;
          anim.displayed = anim.target;
        }
      } else if (anim.alpha < 1) {
        anim.alpha += dt / FADE_MS;
        if (anim.alpha > 1) anim.alpha = 1;
      }

      // Subject overlay temporarily disabled — uncomment to burn the subject
      // label into the recorded video again.
      // drawSubjectOverlay(ctx, anim.displayed, anim.alpha, w, h);
      drawLoopIdRef.current = requestAnimationFrame(drawFrame);
    };

    let canvasStream;
    try {
      canvasStream = canvasEl.captureStream(30);
    } catch (err) {
      return null;
    }
    // Audio graph: in talking-head mode we just attach the mic track. In
    // commentary mode we route mic + active source video through a Web
    // Audio mixer so the captured stream has both (source ducked low).
    // The mixer is built lazily and torn down on unmount. When commentary
    // isn't active, the mixer's source-gain is muted (gain = 0), so it
    // costs nothing.
    try {
      const AudioCtxCtor = window.AudioContext || window.webkitAudioContext;
      if (AudioCtxCtor && streamRef.current.getAudioTracks().length) {
        const audioCtx = audioContextRef.current || new AudioCtxCtor();
        audioContextRef.current = audioCtx;
        const dest = audioMixDestRef.current || audioCtx.createMediaStreamDestination();
        audioMixDestRef.current = dest;
        // Build mic chain once. If the underlying mic stream changed (e.g.
        // phone camera handoff), tear down the old source node and rewire.
        if (!micSourceNodeRef.current
          || micSourceNodeRef.current.mediaStream !== streamRef.current) {
          if (micSourceNodeRef.current) {
            try { micSourceNodeRef.current.disconnect(); } catch (e) { /* noop */ }
          }
          micSourceNodeRef.current = audioCtx.createMediaStreamSource(streamRef.current);
          const micGain = audioCtx.createGain();
          micGain.gain.value = 1.0;
          micGainNodeRef.current = micGain;
          micSourceNodeRef.current.connect(micGain);
          micGain.connect(dest);
        }
        // Source-video gain nodes — built once, both fed by whichever
        // source video element is currently active. `srcGain` feeds the
        // RECORDING (ducked 0.2 under mic). `monitorGain` feeds the
        // SPEAKERS so the user can hear the video while commentating;
        // ramped a bit louder because there's no mic competing on this
        // path.
        if (!sourceGainNodeRef.current) {
          const srcGain = audioCtx.createGain();
          srcGain.gain.value = 0.0;
          sourceGainNodeRef.current = srcGain;
          srcGain.connect(dest);
        }
        if (!monitorGainNodeRef.current) {
          const monitorGain = audioCtx.createGain();
          monitorGain.gain.value = 0.0;
          monitorGainNodeRef.current = monitorGain;
          monitorGain.connect(audioCtx.destination);
        }
        const mixedTrack = dest.stream.getAudioTracks()[0];
        if (mixedTrack) canvasStream.addTrack(mixedTrack);
      } else {
        // Fallback: no Web Audio support — use the raw mic track.
        const audioTrack = streamRef.current.getAudioTracks()[0];
        if (audioTrack) canvasStream.addTrack(audioTrack);
      }
    } catch (e) {
      // Last-ditch fallback if anything in the graph throws.
      const audioTrack = streamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        try { canvasStream.addTrack(audioTrack); } catch (er) { /* noop */ }
      }
    }

    drawLoopIdRef.current = requestAnimationFrame(drawFrame);
    return canvasStream;
  };

  // ── Guest WebRTC helpers ────────────────────────────────────────
  // Compute the audio track to send out to the guest. Preference:
  //   1. the mixed track from audioMixDestRef (mic + TTS + source video)
  //   2. raw mic track (fallback if mix isn't built yet)
  // The mix is built inside buildCompositeStream which runs once the
  // camera permission is granted, so by the time the user clicks Add
  // Guest the mix is almost always ready.
  const getGuestOutgoingAudioTrack = () => {
    const dest = audioMixDestRef.current;
    if (dest && dest.stream) {
      const t = dest.stream.getAudioTracks()[0];
      if (t) return t;
    }
    if (streamRef.current) {
      return streamRef.current.getAudioTracks()[0] || null;
    }
    return null;
  };

  // Tear down the guest call: close PC, close WS, close room on the
  // server. Idempotent so it's safe to call from multiple paths
  // (user clicks Cancel, unmount, peer-left, error). NOT a useCallback
  // on purpose — putting it in a useEffect dep array creates a cleanup
  // loop with setGuestRoomId(null), which used to fire closeRoom
  // immediately after createRoom resolved. Reads the room id from a
  // ref so the callers don't need to be re-bound when state changes.
  const tearDownGuestCall = () => {
    guestDcRef.current = null;
    const pc = guestPcRef.current;
    if (pc) {
      try { pc.close(); } catch (e) { /* noop */ }
      guestPcRef.current = null;
    }
    const ws = guestWsRef.current;
    if (ws) {
      try { ws.close(); } catch (e) { /* noop */ }
      guestWsRef.current = null;
    }
    const roomId = guestRoomIdRef.current;
    if (roomId) {
      try { closeGuestRoom(roomId); } catch (e) { /* noop */ }
      guestRoomIdRef.current = null;
    }
    setGuestStream(null);
    setGuestRoomId(null);
    setGuestStatus('idle');
    setGuestLinkCopied(false);
    setGuestLayout('pip'); // reset so next session opens in default
  };

  // Stop guest call on unmount so the WS doesn't dangle and the room
  // gets closed on the server. Mount-once on purpose — empty deps. We
  // read from refs inside so we don't capture stale state.
  useEffect(() => () => {
    const pc = guestPcRef.current;
    if (pc) { try { pc.close(); } catch (e) { /* noop */ } }
    const ws = guestWsRef.current;
    if (ws) { try { ws.close(); } catch (e) { /* noop */ } }
    const roomId = guestRoomIdRef.current;
    if (roomId) { try { closeGuestRoom(roomId); } catch (e) { /* noop */ } }
  }, []);

  // Bind the remote stream into the PIP <video> element. Uses a ref-
  // callback so the binding survives any future remounts of the PIP
  // overlay (currently it's stable but the pattern is cheap insurance).
  const attachGuestVideo = useCallback((el) => {
    guestVideoElRef.current = el;
    if (el && guestStream && el.srcObject !== guestStream) {
      // eslint-disable-next-line no-param-reassign
      el.srcObject = guestStream;
    }
  }, [guestStream]);

  // Create the room, open WS as host, wait for the guest. The PC is
  // set up later (when peer-joined arrives) so we don't burn ICE
  // candidates on a call that may never start.
  const openGuestInvite = useCallback(async () => {
    if (!GUEST_INVITE_ENABLED) return;
    // Allow starting fresh from idle OR from a finished call ('ended').
    // Block only while a session is mid-flight.
    if (guestStatus === 'inviting' || guestStatus === 'connecting' || guestStatus === 'connected') return;
    // Restarting after a previous guest left? Drop the stale room/WS first
    // so we mint a brand-new link instead of reusing the dead one.
    if (guestStatus === 'ended') tearDownGuestCall();
    setGuestStatus('inviting');
    setGuestPopupOpen(true);
    // CRITICAL: resume the AudioContext now, while we're still in the
    // synchronous part of a user-gesture handler. The mixed audio that
    // feeds the WebRTC outgoing track (mic + TTS + source video) flows
    // through this context; if it's still in 'suspended' state when we
    // start the call, the guest hears nothing because the destination
    // node produces silent samples until the context is running. The
    // recording flow resumes the context lazily on Start Recording,
    // but the guest call can start BEFORE the user ever hits Record.
    try {
      const ctx = audioContextRef.current;
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().catch(() => { /* noop */ });
      }
    } catch (e) { /* noop */ }
    let room;
    let ice;
    try {
      [room, ice] = await Promise.all([
        createGuestRoom({ source: 'GiftRecorder.guestInvite' }),
        getGuestIceServers().catch(() => null),
      ]);
    } catch (e) {
      setGuestStatus('idle');
      setGuestPopupOpen(false);
      return;
    }
    setGuestRoomId(room.id);
    setGuestIceServers((ice && ice.iceServers) || (room.ice_servers || []));

    const sock = new WebSocket(getGuestRoomSignalingUrl(room.id, 'host'));
    guestWsRef.current = sock;

    sock.onmessage = async (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch (e) { return; }
      if (!msg || !msg.type) return;
      if (msg.type === 'peer-joined' && msg.role === 'guest') {
        setGuestStatus('connecting');
        setGuestPopupOpen(false);
        // Build the PC now that we know someone's listening for our offer.
        const pc = new RTCPeerConnection({ iceServers: (ice && ice.iceServers) || [] });
        guestPcRef.current = pc;

        // Belt-and-suspenders: resume the AudioContext again on the
        // peer-joined event. By the time the guest WS joins, the user
        // gesture from "Add Guest" has propagated; this catches the
        // edge case where the first resume() raced with a still-cold
        // context.
        try {
          const ctx2 = audioContextRef.current;
          if (ctx2 && ctx2.state === 'suspended') {
            ctx2.resume().catch(() => { /* noop */ });
          }
        } catch (e) { /* noop */ }

        // CRITICAL: both tracks (video + audio) must share the SAME
        // outgoing MediaStream. The guest's `pc.ontrack` fires once
        // per received track; ev.streams[0] is whatever stream we
        // bundled the track into. The guest's handler does
        // setRemoteStream(ev.streams[0]) which means it can only hold
        // ONE stream reference — if video and audio arrive in
        // different streams, the second one wins and the guest's
        // <video> is bound to a stream with no video track → black.
        // Putting both tracks in the same MediaStream means ontrack
        // fires twice but always with the same stream, so the guest's
        // setRemoteStream is idempotent and the stream has BOTH
        // tracks. (This is the standard pattern for adding multiple
        // tracks to a single PC.)
        const outgoingStream = new MediaStream();
        const camStream = streamRef.current;
        if (camStream) {
          const v = camStream.getVideoTracks()[0];
          if (v) {
            try { outgoingStream.addTrack(v); } catch (e) { /* noop */ }
            pc.addTrack(v, outgoingStream);
          }
        }
        // Audio — the mixed track (mic + TTS + source video) so the
        // guest hears the question being read aloud AND the host.
        const audioTrack = getGuestOutgoingAudioTrack();
        // eslint-disable-next-line no-console
        console.log('[guest-call] outgoing audio track', {
          haveMixedDest: !!audioMixDestRef.current,
          audioCtxState: audioContextRef.current && audioContextRef.current.state,
          trackId: audioTrack && audioTrack.id,
          trackKind: audioTrack && audioTrack.kind,
          trackEnabled: audioTrack && audioTrack.enabled,
          trackReadyState: audioTrack && audioTrack.readyState,
        });
        if (audioTrack) {
          try { outgoingStream.addTrack(audioTrack); } catch (e) { /* noop */ }
          pc.addTrack(audioTrack, outgoingStream);
        } else if (camStream) {
          // Last-resort fallback: if the mix track is somehow missing,
          // send the raw mic track so the guest at least hears the host.
          // (TTS won't be audible to the guest in this state.)
          const a = camStream.getAudioTracks()[0];
          if (a) {
            // eslint-disable-next-line no-console
            console.warn('[guest-call] mix track missing — falling back to raw mic');
            try { outgoingStream.addTrack(a); } catch (e) { /* noop */ }
            pc.addTrack(a, outgoingStream);
          }
        }

        // Metadata data channel (host is the offerer, so we create it).
        // Carries mic/camera state both ways — the guest's lands on the
        // PIP badges; ours is sent on open + whenever the host toggles.
        try {
          const dc = pc.createDataChannel('meta');
          guestDcRef.current = dc;
          dc.onopen = () => {
            const { muted, camOff, name } = hostMediaRef.current;
            try { dc.send(JSON.stringify({ type: 'media-state', audioEnabled: !muted, videoEnabled: !camOff, name })); } catch (e) { /* noop */ }
          };
          dc.onmessage = (e) => {
            try {
              const m = JSON.parse(e.data);
              if (m && m.type === 'media-state') {
                if (typeof m.audioEnabled === 'boolean') setGuestMuted(!m.audioEnabled);
                if (typeof m.videoEnabled === 'boolean') setGuestCamOff(!m.videoEnabled);
                if (typeof m.name === 'string') setGuestName(m.name);
              }
            } catch (err) { /* ignore */ }
          };
        } catch (e) { /* noop */ }

        pc.onicecandidate = (ev) => {
          if (ev.candidate && sock.readyState === WebSocket.OPEN) {
            try {
              sock.send(JSON.stringify({ type: 'ice', payload: ev.candidate.toJSON() }));
            } catch (e) { /* noop */ }
          }
        };
        pc.ontrack = (ev) => {
          const [s] = ev.streams;
          setGuestStream(s || null);
        };
        pc.onconnectionstatechange = () => {
          const s = pc.connectionState;
          if (s === 'connected') setGuestStatus('connected');
          else if (s === 'failed' || s === 'disconnected') setGuestStatus('ended');
        };

        try {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          if (sock.readyState === WebSocket.OPEN) {
            sock.send(JSON.stringify({ type: 'offer', payload: offer }));
          }
        } catch (e) {
          setGuestStatus('ended');
        }
      } else if (msg.type === 'answer') {
        const pc = guestPcRef.current;
        if (pc) {
          try { await pc.setRemoteDescription(msg.payload); } catch (e) { /* noop */ }
        }
      } else if (msg.type === 'ice') {
        const pc = guestPcRef.current;
        if (pc && pc.remoteDescription) {
          try { await pc.addIceCandidate(msg.payload); } catch (e) { /* noop */ }
        }
      } else if (msg.type === 'media-state') {
        // Guest told us their mic/camera state — reflect on the PIP.
        if (typeof msg.audioEnabled === 'boolean') setGuestMuted(!msg.audioEnabled);
        if (typeof msg.videoEnabled === 'boolean') setGuestCamOff(!msg.videoEnabled);
        if (typeof msg.name === 'string') setGuestName(msg.name);
      } else if (msg.type === 'peer-left' && msg.role === 'guest') {
        setGuestStatus('ended');
        setGuestStream(null);
        setGuestMuted(false);
        setGuestCamOff(false);
        setGuestName('');
        guestDcRef.current = null;
        const pc = guestPcRef.current;
        if (pc) {
          try { pc.close(); } catch (e) { /* noop */ }
          guestPcRef.current = null;
        }
      }
    };
    sock.onclose = () => { guestWsRef.current = null; };
  }, [guestStatus]);

  // User-facing close from the popup (before guest joined) — drops the
  // room and shuts the WS. Plain fn, not useCallback (tearDownGuestCall
  // isn't memoized; see comment on it for why).
  const cancelGuestInvite = () => {
    setGuestPopupOpen(false);
    tearDownGuestCall();
  };

  // The shareable URL we put in the popup + clipboard.
  const guestInviteLink = guestRoomId
    ? `${window.location.origin}${Pathname.getInterviewJoinPath(guestRoomId)}`
    : '';

  const handleCopyGuestLink = useCallback(async () => {
    if (!guestInviteLink) return;
    try {
      await navigator.clipboard.writeText(guestInviteLink);
      setGuestLinkCopied(true);
      setTimeout(() => setGuestLinkCopied(false), 1800);
    } catch (e) {
      // Fallback for older Safari — select+execCommand.
      const ta = document.createElement('textarea');
      ta.value = guestInviteLink;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setGuestLinkCopied(true); } catch (err) { /* noop */ }
      document.body.removeChild(ta);
      setTimeout(() => setGuestLinkCopied(false), 1800);
    }
  }, [guestInviteLink]);

  useEffect(() => {
    if (permissionState !== 'granted') return;
    if (drawLoopIdRef.current !== null) return; // already running
    const canvasEl = canvasRef.current;
    const sourceVideo = liveVideoRef.current;
    if (!canvasEl || !sourceVideo) return;
    compositeStreamRef.current = buildCompositeStream(canvasEl, sourceVideo);
  }, [permissionState]);

  // ── Splash concat (kept for re-enable; unused while ENABLE_SPLASH_INTRO=false) ──
  const ensureSplashReady = async () => {
    const v = splashVideoRef.current;
    if (!v) return;
    if (v.readyState < 2) {
      await new Promise((resolve) => {
        const done = () => {
          v.removeEventListener('loadeddata', done);
          v.removeEventListener('canplay', done);
          v.removeEventListener('error', done);
          resolve();
        };
        v.addEventListener('loadeddata', done);
        v.addEventListener('canplay', done);
        v.addEventListener('error', done);
        setTimeout(done, 4000);
      });
    }
    try { v.currentTime = 0; } catch (e) { /* noop */ }
    try { v.muted = true; const p = v.play(); if (p && p.catch) p.catch(() => {}); } catch (e) { /* noop */ }
  };

  // eslint-disable-next-line no-unused-vars
  const concatSplashWithRecording = async (originalBlob, mimeType) => {
    await ensureSplashReady();
    const sourceUrl = URL.createObjectURL(originalBlob);
    const sourceVideo = document.createElement('video');
    sourceVideo.src = sourceUrl;
    sourceVideo.playsInline = true;
    sourceVideo.preload = 'auto';
    sourceVideo.crossOrigin = 'anonymous';

    await new Promise((resolve, reject) => {
      sourceVideo.onloadedmetadata = () => resolve();
      sourceVideo.onerror = () => reject(new Error('Could not load recorded video'));
    });

    if (!Number.isFinite(sourceVideo.duration)) {
      await new Promise((resolve) => {
        const onTu = () => {
          if (Number.isFinite(sourceVideo.duration)) {
            sourceVideo.removeEventListener('timeupdate', onTu);
            try { sourceVideo.currentTime = 0; } catch (e) { /* noop */ }
            resolve();
          }
        };
        sourceVideo.addEventListener('timeupdate', onTu);
        try { sourceVideo.currentTime = 1e6; } catch (e) { resolve(); }
      });
    }

    const w = sourceVideo.videoWidth || 1280;
    const h = sourceVideo.videoHeight || 720;
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#000'; ctx.fillRect(0, 0, w, h);

    let canvasStream;
    try { canvasStream = canvas.captureStream(24); } catch (err) {
      URL.revokeObjectURL(sourceUrl); throw err;
    }

    let audioCtx = null; let audioBuffer = null; let audioBufferSrc = null;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        audioCtx = new Ctx();
        if (audioCtx.state === 'suspended') { try { await audioCtx.resume(); } catch (e) { /* noop */ } }
        try {
          const arrayBuffer = await originalBlob.arrayBuffer();
          audioBuffer = await audioCtx.decodeAudioData(arrayBuffer.slice(0));
        } catch (e) { audioBuffer = null; }
        const audioDest = audioCtx.createMediaStreamDestination();
        if (audioBuffer) {
          audioBufferSrc = audioCtx.createBufferSource();
          audioBufferSrc.buffer = audioBuffer;
          audioBufferSrc.connect(audioDest);
          sourceVideo.muted = true;
        } else {
          try {
            const audioSrc = audioCtx.createMediaElementSource(sourceVideo);
            audioSrc.connect(audioDest);
          } catch (e) { /* noop */ }
        }
        audioDest.stream.getAudioTracks().forEach((t) => {
          try { canvasStream.addTrack(t); } catch (e) { /* noop */ }
        });
      }
    } catch (e) { /* silent fallback */ }

    const recorder = mimeType
      ? new MediaRecorder(canvasStream, { mimeType })
      : new MediaRecorder(canvasStream);
    const chunks = [];
    recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
    const finalBlobPromise = new Promise((resolve) => {
      recorder.onstop = () => {
        const baseType = (mimeType || 'video/webm').split(';')[0];
        resolve(new Blob(chunks, { type: baseType }));
      };
    });
    recorder.start(100);

    const drawSplashCentered = (target, source, cw, ch, alpha = 1) => {
      if (!source) return;
      const sw = source.videoWidth || source.naturalWidth || 0;
      const sh = source.videoHeight || source.naturalHeight || 0;
      if (!sw || !sh) return;
      const fit = Math.min(cw / sw, ch / sh) * 0.6;
      const dw = sw * fit; const dh = sh * fit;
      const dx = (cw - dw) / 2; const dy = (ch - dh) / 2;
      target.save();
      // eslint-disable-next-line no-param-reassign
      target.globalAlpha = alpha;
      try { target.drawImage(source, dx, dy, dw, dh); } catch (e) { /* noop */ }
      target.restore();
    };
    const splashVideo = splashVideoRef.current;
    await new Promise((resolve) => {
      let phaseLocal = 'splash'; let phaseStart = performance.now();
      const tick = () => {
        const now = performance.now();
        const elapsed = now - phaseStart;
        if (phaseLocal === 'splash') {
          ctx.fillStyle = '#000'; ctx.fillRect(0, 0, w, h);
          drawSplashCentered(ctx, splashVideo, w, h, 1);
          if (elapsed >= SPLASH_DURATION_MS) {
            phaseLocal = 'fade'; phaseStart = now;
            try { if (splashVideo) splashVideo.pause(); } catch (e) { /* noop */ }
            sourceVideo.play().catch(() => {});
            if (audioBufferSrc) { try { audioBufferSrc.start(); } catch (e) { /* noop */ } }
          }
        } else if (phaseLocal === 'fade') {
          const progress = Math.min(1, elapsed / SPLASH_FADE_MS);
          ctx.fillStyle = '#000'; ctx.fillRect(0, 0, w, h);
          if (sourceVideo.readyState >= 2) {
            ctx.save(); ctx.globalAlpha = progress;
            ctx.drawImage(sourceVideo, 0, 0, w, h); ctx.restore();
          }
          drawSplashCentered(ctx, splashVideo, w, h, 1 - progress);
          if (elapsed >= SPLASH_FADE_MS) phaseLocal = 'video';
        } else {
          if (sourceVideo.readyState >= 2) ctx.drawImage(sourceVideo, 0, 0, w, h);
          if (sourceVideo.ended) {
            requestAnimationFrame(() => {
              try { recorder.stop(); } catch (e) { /* noop */ }
              try { sourceVideo.pause(); } catch (e) { /* noop */ }
              try { if (audioCtx) audioCtx.close(); } catch (e) { /* noop */ }
              URL.revokeObjectURL(sourceUrl);
              resolve();
            });
            return;
          }
        }
        requestAnimationFrame(tick);
      };
      tick();
    });

    return finalBlobPromise;
  };

  // ── Helpers ──
  const captureCanvasThumbnail = () => {
    const canvas = canvasRef.current;
    if (!canvas) return '';
    try { return canvas.toDataURL('image/jpeg', 0.7); } catch (e) { return ''; }
  };

  const fileExtFor = (mimeType) => ((mimeType || '').includes('mp4') ? 'mp4' : 'webm');

  // ── Recording lifecycle ──
  // ── Question voiceover (OpenAI TTS) ──
  // Browser SpeechSynthesis is too flaky (silent on a fair chunk of Chrome
  // installs even though the API claims to be speaking), so we delegate to
  // OpenAI's /v1/audio/speech. The MP3 blob is fetched while the 3-second
  // countdown is running and played back via an <audio> element the moment
  // the recorder transitions PREP → RECORDING. The mic captures the speaker
  // audio so the question lands inside the recorded clip.
  const audioElRef = useRef(null); // current <audio> playing the voiceover
  // Karaoke-style word highlight while TTS plays. The OpenAI TTS endpoint
  // doesn't return word-level timestamps, so we estimate per-word durations
  // by character-share of the audio's total duration. Good enough for
  // 5-15 word prompts; never tries to chase phoneme accuracy.
  const [ttsHighlightIndex, setTtsHighlightIndex] = useState(-1);
  // True while the TTS audio is actively playing. Drives a "reading"
  // emphasis on the whole question (brighter text + active word chip)
  // so the user clearly notices the prompt is being read out. Falls
  // back to normal style as soon as playback pauses / ends / is stopped.
  const [isReadingAloud, setIsReadingAloud] = useState(false);
  // ── "Reading aloud" stage overlay ──
  // While the question is read aloud we lift it into a centered, dimmed-glass
  // overlay that covers the WHOLE stage (camera + bottom controls) so the user
  // just listens. When the read-out ends the overlay runs a FLIP: the question
  // flies up into its normal top-panel position as the glass dissolves,
  // revealing the live camera + controls again.
  //   'hidden' → not shown
  //   'in'     → reading, glass + centered question on screen
  //   'out'    → flying to its resting spot while the glass dissolves
  const [readingStage, setReadingStage] = useState('hidden');
  // Mirror so the overlay's safety-timeout can read the latest playing flag
  // without re-subscribing.
  const isReadingAloudRef = useRef(false);
  useEffect(() => { isReadingAloudRef.current = isReadingAloud; }, [isReadingAloud]);
  const readingContentRef = useRef(null); // centered pill + question wrapper
  const readingQuestionRef = useRef(null); // centered question text (size source)
  const promptQuestionRef = useRef(null); // resting top-panel question (FLIP target)
  const ttsWordScheduleRef = useRef([]); // [{ start, end }] in seconds
  const ttsHighlightTextRef = useRef(''); // the text the current schedule was built for
  // Persistent TTS audio element + its Web Audio source node. createMediaElementSource
  // can be called at most once per HTMLMediaElement, so we keep a single element
  // and reuse it across recordings — only `audio.src` changes per question.
  // Routing the element through Web Audio lets us split its output to BOTH
  // the speakers (so the user hears it) AND the same MediaStreamDestination
  // that feeds the MediaRecorder (so the voice is baked into the recorded
  // video's audio track).
  const ttsAudioElRef = useRef(null);
  const ttsSourceNodeRef = useRef(null);
  const ttsGainNodeRef = useRef(null);
  // Karaoke highlight is driven by a rAF loop (not the ~4Hz `timeupdate`
  // event, which lagged visibly behind a fast voice). A small look-ahead
  // nudges the chip slightly ahead of the audio cursor to counter render
  // latency so the highlight reads as in-sync with the speech.
  const ttsRafRef = useRef(null);
  const lastHighlightIdxRef = useRef(-1);
  const TTS_HIGHLIGHT_LEAD_S = 0.16;
  // ms to wait after RECORDING starts before the voice begins, so the
  // recording has a beat of breathing room and the clip doesn't open with
  // a hard cut into TTS.
  const TTS_START_DELAY_MS = 300;
  const cachedSpeechUrlRef = useRef(null); // resolved blob: URL (once fetch settles)
  const cachedSpeechPromiseRef = useRef(null); // Promise<blobUrl> while in flight
  const cachedSpeechForRef = useRef({ slot: -1, text: '' }); // identity tag
  const speechAbortRef = useRef(null); // AbortController of the in-flight fetch

  // Cancel the karaoke rAF loop.
  const stopHighlightLoop = () => {
    if (ttsRafRef.current !== null) {
      cancelAnimationFrame(ttsRafRef.current);
      ttsRafRef.current = null;
    }
  };

  // Drive the active-word chip every animation frame off the audio's live
  // currentTime (+ a small look-ahead), re-rendering only when the word
  // actually changes. Far tighter sync than the ~4Hz `timeupdate` event.
  const startHighlightLoop = (audio) => {
    stopHighlightLoop();
    lastHighlightIdxRef.current = -1;
    const tick = () => {
      const schedule = ttsWordScheduleRef.current;
      if (schedule.length && audio) {
        const t = audio.currentTime + TTS_HIGHLIGHT_LEAD_S;
        let idx = -1;
        for (let i = 0; i < schedule.length; i += 1) {
          if (t >= schedule[i].start && t < schedule[i].end) { idx = i; break; }
        }
        // Past the final word → hold the last word lit until the audio
        // actually ends, rather than blinking off early.
        if (idx === -1 && t >= schedule[schedule.length - 1].end) {
          idx = schedule.length - 1;
        }
        if (idx !== lastHighlightIdxRef.current) {
          lastHighlightIdxRef.current = idx;
          setTtsHighlightIndex(idx);
        }
      }
      ttsRafRef.current = requestAnimationFrame(tick);
    };
    ttsRafRef.current = requestAnimationFrame(tick);
  };

  const stopSpeaking = () => {
    // Clear karaoke highlight regardless of which audio is in flight.
    stopHighlightLoop();
    lastHighlightIdxRef.current = -1;
    ttsWordScheduleRef.current = [];
    ttsHighlightTextRef.current = '';
    setTtsHighlightIndex(-1);
    setIsReadingAloud(false);
    // Stop playback.
    const el = audioElRef.current;
    if (el) {
      try { el.pause(); } catch (e) { /* noop */ }
      audioElRef.current = null;
    }
    // Abort any in-flight fetch.
    if (speechAbortRef.current) {
      try { speechAbortRef.current.abort(); } catch (e) { /* noop */ }
      speechAbortRef.current = null;
    }
    // Revoke + drop the cached blob URL so we don't leak.
    if (cachedSpeechUrlRef.current) {
      try { URL.revokeObjectURL(cachedSpeechUrlRef.current); } catch (e) { /* noop */ }
      cachedSpeechUrlRef.current = null;
    }
    cachedSpeechPromiseRef.current = null;
    cachedSpeechForRef.current = { slot: -1, text: '' };
  };

  // Kick off a TTS request for `text` and stash both the in-flight Promise
  // and (when settled) the resolved blob URL. Idempotent: a duplicate call
  // for the same (slot, text) returns the existing Promise so playSpeech()
  // never aborts an already-in-flight fetch to start a fresh one (which was
  // the bug that doubled wait time when prefetch hadn't yet completed by
  // the time recording started).
  const prefetchSpeech = (slot, text) => {
    if (!text) return null;
    const cur = cachedSpeechForRef.current;
    if (cur.slot === slot && cur.text === text && cachedSpeechPromiseRef.current) {
      return cachedSpeechPromiseRef.current; // already fetching / fetched
    }
    // Different slot/text — drop the old cache and the in-flight fetch.
    if (cachedSpeechUrlRef.current) {
      try { URL.revokeObjectURL(cachedSpeechUrlRef.current); } catch (e) { /* noop */ }
      cachedSpeechUrlRef.current = null;
    }
    if (speechAbortRef.current) {
      try { speechAbortRef.current.abort(); } catch (e) { /* noop */ }
    }
    const ctrl = new AbortController();
    speechAbortRef.current = ctrl;
    cachedSpeechForRef.current = { slot, text };
    // eslint-disable-next-line no-console
    console.log('[REC_TTS] prefetch → slot=', slot, '· textLen=', text.length);
    const t0 = Date.now();
    const promise = fetchSpeechBlobUrl(text, { signal: ctrl.signal })
      .then((url) => {
        // Bail if the user moved on while we were fetching.
        if (speechAbortRef.current !== ctrl) {
          if (url) try { URL.revokeObjectURL(url); } catch (e) { /* noop */ }
          return null;
        }
        cachedSpeechUrlRef.current = url;
        speechAbortRef.current = null;
        // eslint-disable-next-line no-console
        console.log('[REC_TTS] prefetch ready · slot=', slot, '· ms=', Date.now() - t0);
        return url;
      })
      .catch((err) => {
        if (err && err.name === 'AbortError') return null;
        speechAbortRef.current = null;
        // eslint-disable-next-line no-console
        console.log('[REC_TTS] prefetch error:', err && err.message);
        return null;
      });
    cachedSpeechPromiseRef.current = promise;
    return promise;
  };

  // Lazily wire the persistent TTS <audio> element into the Web Audio
  // mixer. The mixer's destination feeds the MediaRecorder (set up in
  // buildCompositeStream), so anything we connect here gets recorded.
  // Also connects to audioCtx.destination so the user still hears it
  // through the speakers. Returns the audio element either way.
  const ensureTtsAudioWired = () => {
    if (!ttsAudioElRef.current) {
      const el = document.createElement('audio');
      el.preload = 'auto';
      el.volume = 1;
      ttsAudioElRef.current = el;
    }
    const audio = ttsAudioElRef.current;
    const audioCtx = audioContextRef.current;
    if (audioCtx && !ttsSourceNodeRef.current) {
      try {
        const source = audioCtx.createMediaElementSource(audio);
        const gain = audioCtx.createGain();
        gain.gain.value = 1.0;
        source.connect(gain);
        // Speakers (so the user hears the voice).
        gain.connect(audioCtx.destination);
        // Recording stream (so the voice is baked into the video's audio
        // track). Mic + TTS get mixed together at this destination.
        if (audioMixDestRef.current) gain.connect(audioMixDestRef.current);
        ttsSourceNodeRef.current = source;
        ttsGainNodeRef.current = gain;
        // eslint-disable-next-line no-console
        console.log('[REC_TTS] wired TTS element → speakers + recording mix');
      } catch (e) {
        // createMediaElementSource throws if already attached — safe to ignore.
        // eslint-disable-next-line no-console
        console.log('[REC_TTS] createMediaElementSource threw:', e && e.message);
      }
    }
    return audio;
  };

  // Play the question's audio. If the prefetch is still in flight, waits
  // on the same Promise (does NOT start a competing fetch).
  // Estimate per-word time-slices across the audio's total duration,
  // weighted by each word's character count (longer words take more
  // time). Returns [{ start, end }] in seconds. Empty array if either
  // input is missing.
  const buildWordSchedule = (text, durationSec) => {
    const words = (text || '').split(/\s+/).filter(Boolean);
    if (words.length === 0 || !Number.isFinite(durationSec) || durationSec <= 0) return [];
    const totalChars = words.reduce((acc, w) => acc + Math.max(w.length, 1), 0);
    let acc = 0;
    return words.map((w) => {
      const share = Math.max(w.length, 1) / totalChars;
      const dur = share * durationSec;
      const start = acc;
      acc += dur;
      return { start, end: acc };
    });
  };

  const playSpeech = (slot, text) => {
    if (!text) return;
    const startPlayback = (url) => {
      if (!url) return;
      try {
        const audio = ensureTtsAudioWired();
        audio.src = url;
        // Reset highlight state for the new utterance — the schedule is
        // built once metadata is loaded (duration becomes finite).
        ttsWordScheduleRef.current = [];
        ttsHighlightTextRef.current = '';
        setTtsHighlightIndex(-1);
        setIsReadingAloud(false);
        audio.onloadedmetadata = () => {
          const dur = audio.duration;
          if (Number.isFinite(dur) && dur > 0) {
            ttsWordScheduleRef.current = buildWordSchedule(text, dur);
            ttsHighlightTextRef.current = text;
          }
        };
        // Karaoke highlight is driven by the rAF loop (startHighlightLoop),
        // not `timeupdate` — the event's ~4Hz cadence trailed the voice.
        audio.onpause = () => {
          stopHighlightLoop();
          lastHighlightIdxRef.current = -1;
          setTtsHighlightIndex(-1);
          setIsReadingAloud(false);
        };
        audio.onplay = () => {
          setIsReadingAloud(true);
          startHighlightLoop(audio);
          // eslint-disable-next-line no-console
          console.log('[REC_TTS] audio play');
        };
        audio.onended = () => {
          stopHighlightLoop();
          lastHighlightIdxRef.current = -1;
          setTtsHighlightIndex(-1);
          setIsReadingAloud(false);
          // eslint-disable-next-line no-console
          console.log('[REC_TTS] audio ended');
        };
        // eslint-disable-next-line no-console
        audio.onerror = () => console.log('[REC_TTS] audio error');
        audioElRef.current = audio;
        // 300ms delay so the clip doesn't open with an immediate voice cut
        // and the mic settles before the TTS begins.
        setTimeout(() => {
          // If the user already moved on (Reset/Skip/Back), stopSpeaking
          // null-ed audioElRef — bail.
          if (audioElRef.current !== audio) return;
          // Recover from a suspended AudioContext (e.g. after long idle).
          const ctx = audioContextRef.current;
          if (ctx && ctx.state === 'suspended') {
            try { ctx.resume(); } catch (e) { /* noop */ }
          }
          audio.play().catch((err) => {
            // eslint-disable-next-line no-console
            console.log('[REC_TTS] play() rejected:', err && err.message);
          });
        }, TTS_START_DELAY_MS);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('[REC_TTS] play exception:', e);
      }
    };

    // Fast path: cache is ready.
    const cur = cachedSpeechForRef.current;
    const cachedMatches = cur.slot === slot && cur.text === text;
    if (cachedMatches && cachedSpeechUrlRef.current) {
      // eslint-disable-next-line no-console
      console.log('[REC_TTS] play from cache · slot=', slot);
      startPlayback(cachedSpeechUrlRef.current);
      return;
    }
    // Slow path: attach to in-flight fetch (or start one) and play on resolve.
    const promise = prefetchSpeech(slot, text);
    if (!promise) return;
    const startedSlot = slot;
    const startedText = text;
    // eslint-disable-next-line no-console
    console.log('[REC_TTS] play waiting on fetch · slot=', slot);
    promise.then((url) => {
      // Only play if the user is still on the same slot/text by the time
      // the fetch resolves (otherwise stopSpeaking already revoked the url).
      const c = cachedSpeechForRef.current;
      if (c.slot !== startedSlot || c.text !== startedText) return;
      startPlayback(url);
    });
  };

  // Cancel any in-flight TTS on unmount.
  useEffect(() => () => stopSpeaking(), []);

  // ── Reading-overlay driver ──
  // Show the glass the INSTANT recording starts (the question is about to be
  // read). We can't wait for the TTS audio's onplay — there's a ~300ms start
  // delay plus fetch/decode/play latency, which left a visible gap where the
  // camera + buttons flashed after the countdown ended. A safety timeout
  // clears it if the audio never actually starts (fetch failed, askAloud off).
  useEffect(() => {
    if (phase !== PHASE.RECORDING || !askAloudRef.current) return undefined;
    setReadingStage('in');
    const t = setTimeout(() => {
      if (!isReadingAloudRef.current) {
        setReadingStage((prev) => (prev === 'in' ? 'out' : prev));
      }
    }, 6000);
    return () => clearTimeout(t);
  }, [phase]);

  // Mirror the TTS playing flag into the overlay stage. Turning ON keeps it
  // 'in'; turning OFF only matters if we were mid-read ('in') — then we kick
  // off the 'out' fly-to-place animation. (Ignoring OFF while already
  // hidden/out keeps a stray stopSpeaking() from re-triggering it.)
  useEffect(() => {
    if (isReadingAloud) {
      setReadingStage('in');
    } else {
      setReadingStage((prev) => (prev === 'in' ? 'out' : prev));
    }
  }, [isReadingAloud]);

  // ── Reading-overlay FLIP ──
  // On 'out', translate+scale the centered question into its resting top-panel
  // slot (measured live so it lands correctly at any viewport size) while the
  // glass dissolves via CSS, then unmount the overlay once it settles.
  useEffect(() => {
    if (readingStage !== 'out') return undefined;
    const el = readingContentRef.current;
    const flyQ = readingQuestionRef.current;
    const target = promptQuestionRef.current;
    let anim = null;
    if (el && target && typeof el.animate === 'function') {
      try {
        // Translate by the QUESTION's center → resting question's center so it
        // lands on its slot (the wrapper also holds the pill, so its own
        // center sits a touch higher).
        const from = (flyQ || el).getBoundingClientRect();
        const to = target.getBoundingClientRect();
        const dx = (to.left + to.width / 2) - (from.left + from.width / 2);
        const dy = (to.top + to.height / 2) - (from.top + from.height / 2);
        // Shrink by FONT-SIZE ratio (not width) so the glyphs arrive at the
        // exact resting text size. Width ratio doesn't work here — the resting
        // question's container is wide, so it would clamp to ~1 (no shrink).
        const fly = parseFloat((typeof window !== 'undefined'
          && window.getComputedStyle(flyQ || el).fontSize)) || 1;
        const rest = parseFloat((typeof window !== 'undefined'
          && window.getComputedStyle(target).fontSize)) || fly;
        const scale = Math.max(0.15, Math.min(1, rest / fly));
        // Scale about the QUESTION's center (not the wrapper's), so shrinking
        // keeps the text locked on the point we translate it to — it lands
        // dead-on its slot instead of creeping up as it gets smaller.
        const wrap = el.getBoundingClientRect();
        el.style.transformOrigin = `${(from.left + from.width / 2) - wrap.left}px ${(from.top + from.height / 2) - wrap.top}px`;
        anim = el.animate(
          [
            { transform: 'translate(0px, 0px) scale(1)', opacity: 1, offset: 0 },
            // Stay fully opaque through the travel so it reads as the SAME
            // question gliding — not a fade. Only the last leg dips so it
            // merges into the resting copy fading in beneath it.
            { opacity: 1, offset: 0.72 },
            { transform: `translate(${dx}px, ${dy}px) scale(${scale})`, opacity: 0, offset: 1 },
          ],
          // easeOutExpo-style curve: quick, confident launch that decelerates
          // into a soft landing, so the question glides up and settles rather
          // than snapping or drifting.
          { duration: 760, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' },
        );
      } catch (e) { /* noop — the CSS dissolve still runs */ }
    }
    const t = setTimeout(() => setReadingStage('hidden'), 800);
    return () => {
      clearTimeout(t);
      if (anim) { try { anim.cancel(); } catch (e) { /* noop */ } }
    };
  }, [readingStage]);

  // No-op kept around so the existing handleStartFlow / handleRerecord /
  // confirmSwitchSave / confirmSwitchDump call sites compile unchanged. The
  // browser-TTS user-gesture priming hack isn't needed for the OpenAI path.
  const primeSpeechSynthesis = () => {};

  // Resize the composite canvas to (w, h) and swap the captureStream's
  // video track so the MediaRecorder picks up the new dimensions on its
  // next start(). The audio mixer (mic + TTS + commentary source) is on
  // the same MediaStream but uses its own tracks — we only touch video.
  // No-op if dims already match.
  const resizeCompositeCanvas = (targetW, targetH) => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) return;
    if (canvasEl.width === targetW && canvasEl.height === targetH) return;
    canvasEl.width = targetW;
    canvasEl.height = targetH;
    const composite = compositeStreamRef.current;
    if (!composite) return;
    try {
      const fresh = canvasEl.captureStream(30);
      const newVid = fresh.getVideoTracks()[0];
      const oldVid = composite.getVideoTracks()[0];
      if (oldVid) {
        try { composite.removeTrack(oldVid); } catch (e) { /* noop */ }
        try { oldVid.stop(); } catch (e) { /* noop */ }
      }
      if (newVid) composite.addTrack(newVid);
    } catch (e) { /* noop */ }
  };

  // Decide the canvas dims to use when recording `slotIndex`. Commentary
  // slots get the quality-cap dims (default 1080p); talking-head slots
  // revert to the webcam-derived dims that buildCompositeStream picked.
  // Reads from refs so this works even when invoked from a stale closure.
  const computeTargetCanvasDims = (slotIndex) => {
    const slot = (slotsRef.current || [])[slotIndex];
    const hasCommentary = !!(slot && slot.commentary
      && slot.commentary.mediaList && slot.commentary.mediaList.length);
    if (hasCommentary) {
      const capH = commentaryQualityCapRef.current || 1080;
      const capW = Math.round((capH * 16) / 9);
      return { w: capW, h: capH };
    }
    return webcamCanvasDimsRef.current;
  };

  const beginRecordingForSlot = (slotIndex) => {
    if (slotIndex < 0 || slotIndex >= liveQuestions.length) return;
    setSlots((prev) => prev.map((s, i) => (i === slotIndex
      ? { ...s, status: SLOT_STATUS.RECORDING }
      : (s.status === SLOT_STATUS.RECORDING ? { ...s, status: SLOT_STATUS.EMPTY } : s))));
    setCurrentSlot(slotIndex);
    setElapsedSeconds(0);
    setDurationCap(DEFAULT_DURATION_CAP_S);
    setWarningDismissed(false);
    setRecordingPaused(false);
    // Critical order: prepCountdown MUST be set before phase. Outside React
    // event handlers (e.g. confirmSwitchDump's setTimeout, onstop callbacks)
    // these updates are not batched in React 16, so setPhase(PREP) on its
    // own would let the prep ticker fire with the stale prepCountdown=0 and
    // skip straight to RECORDING — no 3-2-1 visible.
    setPrepCountdown(PREP_COUNTDOWN_S);
    setPhase(PHASE.PREP);
    // Mobile: recording always happens inside the fullscreen camera modal,
    // regardless of which path kicked it off (tile tap, review retake,
    // re-record, switch). Opening here covers every entry point.
    if (isMobile) setMobileSessionOpen(true);
  };

  const startActualRecording = useCallback((slotIndex) => {
    if (!streamRef.current) return;

    // ── Guard against overlapping MediaRecorders ──────────────────────────
    // The prep-countdown effect and the re-record flow can fire
    // startActualRecording() multiple times for the SAME slot in quick
    // succession. Previously every call constructed a fresh MediaRecorder and
    // overwrote mediaRecorderRef.current WITHOUT stopping the old one. The
    // recorder that was actually capturing video got orphaned, and the one we
    // kept (the last) was stopped a split-second after starting → it captured
    // 0 chunks → a 0-byte file. The upload backend then rejected that file with
    // HTTP 400 ("file_size: Ensure this value is greater than or equal to 1").
    const activeRecorder = mediaRecorderRef.current;
    if (activeRecorder && activeRecorder.state !== 'inactive') {
      // A recorder is still live. Calling start() here would build a SECOND
      // MediaRecorder on the same stream and orphan the one that's actually
      // capturing video. Keep the live recorder and ignore this redundant call.
      // In normal flow a slot's recorder is always stopped (state → 'inactive',
      // synchronously) before the next slot starts, so this only triggers for
      // rapid same-slot re-fires — which is exactly the case that produced the
      // empty 0-byte clips.
      return;
    }

    discardCurrentRef.current = false;
    recordingTargetRef.current = slotIndex;
    // Resize canvas + swap video track to match this slot's target dims
    // (commentary quality cap when commentary is active; webcam dims
    // otherwise). MUST happen before MediaRecorder is constructed —
    // changing the canvas after start() doesn't propagate to the encoder.
    try {
      const target = computeTargetCanvasDims(slotIndex);
      if (target) resizeCompositeCanvas(target.w, target.h);
    } catch (e) { /* noop */ }
    // Chunks live in a buffer OWNED by this recorder's closure — NOT a shared
    // ref. MediaRecorder.onstop is async; when one clip stops and the next
    // startActualRecording() runs before the previous onstop fires, a shared
    // buffer lets the two recorders trample each other's chunks — the new
    // clip loses its first chunk (the EBML header → "could not open" /
    // unplayable) and the old clip loses its flush chunk (truncated tail).
    // A per-recorder local array makes chunk attribution race-proof.
    const chunks = [];

    const recordingStream = compositeStreamRef.current || streamRef.current;
    const mimeType = pickSupportedMimeType();
    recordedMimeRef.current = mimeType;
    let recorder;
    try {
      recorder = mimeType
        ? new MediaRecorder(recordingStream, { mimeType })
        : new MediaRecorder(recordingStream);
    } catch (err) {
      setPermissionError('Recording is not supported in this browser.');
      return;
    }

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunks.push(e.data);
    };
    recorder.onstop = async () => {
      const baseType = (mimeType || 'video/webm').split(';')[0];
      const targetSlot = recordingTargetRef.current;
      if (rerecordRef.current) {
        // Re-record path: throw away chunks; the new prep+recording for this
        // same slot was already kicked off in handleRerecord.
        rerecordRef.current = false;
        return;
      }
      if (discardCurrentRef.current) {
        // Caller-driven discard (removeSlot, handleSkipCurrent, switch-dump).
        // Drop bytes; the caller owns slot bookkeeping + navigation.
        discardCurrentRef.current = false;
        return;
      }

      const rawBlob = new Blob(chunks, { type: baseType });

      // Splash concat is gated by feature flag — currently bypassed
      let finalBlob = rawBlob;
      if (ENABLE_SPLASH_INTRO) {
        try {
          finalBlob = await concatSplashWithRecording(rawBlob, mimeType);
        } catch (e) {
          finalBlob = rawBlob;
        }
      }

      const url = URL.createObjectURL(finalBlob);
      const ext = fileExtFor(baseType);
      // Filename = the slot's subject so the upload pipeline picks it up as
      // the video's title (handleFileSelect derives title from filename).
      // Strip filesystem-unsafe chars but keep human-readable wording.
      // IMPORTANT: read from liveQuestionsRef (not the `liveQuestions`
      // state variable). startActualRecording is wrapped in useCallback([])
      // — empty deps — so the `liveQuestions` closure captured here is the
      // FIRST-RENDER array (the original preset prompts). After the user
      // deletes presets and adds a custom question, the state has changed
      // but this closure hadn't, so it would name the recording with the
      // *original* preset's subject (e.g. "How you met") instead of the
      // newly added custom subject. The ref is updated on every commit, so
      // it always reflects the latest array.
      const liveQs = liveQuestionsRef.current;
      const subject = getSubject(liveQs[targetSlot]) || `Clip ${targetSlot + 1}`;
      // eslint-disable-next-line no-control-regex
      const safeSubject = subject.replace(/[\\/:*?"<>|\x00-\x1f]/g, '').trim() || `Clip ${targetSlot + 1}`;
      const filename = `${safeSubject}.${ext}`;
      const file = new File([finalBlob], filename, { type: baseType });
      const thumbnail = captureCanvasThumbnail();
      // Carry the real captured frame on the File itself. `onComplete` only
      // passes File objects back to the host (PersonalVideoMessageFlow), so
      // attaching the thumbnail here lets the receiver-flow review/confirm grid
      // and the gift-content upload card show an actual frame instead of an
      // iOS-Safari black <video> poster. The canvas is the live composite the
      // user just recorded from, so this frame is never black on iOS.
      try { file.thumbnail = thumbnail; } catch (e) { /* non-extensible — ignore */ }

      // eslint-disable-next-line no-use-before-define
      flyClipIntoSlot(targetSlot, file, url, thumbnail);
    };

    mediaRecorderRef.current = recorder;
    recorder.start(1000);
  }, []);

  // Remove a slot + its question entirely (replaces the old "skip" behaviour).
  // Handles every phase: IDLE (no flow yet), PREP / RECORDING (active),
  // REVIEW (after flow complete). If the deleted slot was actively
  // recording, drop the in-flight chunks and re-enter PREP for whatever
  // slot now sits at the same index.
  const removeSlot = (slotIndex) => {
    const wasActive = slotIndex === currentSlot
      && (phase === PHASE.PREP || phase === PHASE.RECORDING);

    if (wasActive && phase === PHASE.RECORDING) {
      discardCurrentRef.current = true;
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch (e) { /* noop */ }
    }

    let pendingNewSlots = null;
    setLiveQuestions((prev) => prev.filter((_, i) => i !== slotIndex));
    setSlots((prev) => {
      const newSlots = prev.filter((_, i) => i !== slotIndex);
      pendingNewSlots = newSlots;
      return newSlots;
    });

    // Defer post-removal navigation until React commits the array changes.
    setTimeout(() => {
      const newSlots = pendingNewSlots || [];
      // No slots left — nothing to record. Drop back to IDLE so the user
      // can add a new question via the modal.
      if (newSlots.length === 0) {
        setPhase(PHASE.IDLE);
        setCurrentSlot(0);
        return;
      }

      if (wasActive) {
        // The deleted slot was the active one. Whatever was at the next
        // index has shifted into this slot — record it now.
        const target = Math.min(slotIndex, newSlots.length - 1);
        if (newSlots[target] && newSlots[target].status === SLOT_STATUS.EMPTY) {
          // eslint-disable-next-line no-use-before-define
          beginRecordingForSlot(target);
        } else {
          // No empty slot here — find the next empty further along.
          const nextEmpty = newSlots.findIndex((s) => s.status === SLOT_STATUS.EMPTY);
          if (nextEmpty === -1) {
            setPhase(PHASE.REVIEW);
            setCurrentSlot(Math.max(0, newSlots.length - 1));
          } else {
            // eslint-disable-next-line no-use-before-define
            beginRecordingForSlot(nextEmpty);
          }
        }
        return;
      }

      // Inactive deletion. Clamp currentSlot if it now overshoots the array.
      if (slotIndex < currentSlot) {
        setCurrentSlot((c) => Math.max(0, c - 1));
      } else if (currentSlot >= newSlots.length) {
        setCurrentSlot(Math.max(0, newSlots.length - 1));
      }

      // Deleting a trailing EMPTY slot from IDLE can leave every remaining slot
      // settled (DONE/SKIPPED). On web the user then clicks "Start Recording"
      // and handleStartFlow's first===-1 check jumps to REVIEW; on mobile there
      // is no such click (tapping a tile just reopens the camera), so the flow
      // would get stuck on the question tiles. Mobile-only: mirror
      // advanceFromSlot here — once nothing is left to record and at least one
      // clip exists, advance to REVIEW so the user can reach "Attach Clips".
      // Web keeps its existing behaviour (Start Recording drives the jump).
      // (skipReview's own effect drives the receiver/tile-hub flow, untouched.)
      if (isMobile && phase === PHASE.IDLE && !skipReview) {
        const hasEmpty = newSlots.some((s) => s.status === SLOT_STATUS.EMPTY);
        const hasDone = newSlots.some((s) => s.status === SLOT_STATUS.DONE);
        if (!hasEmpty && hasDone) {
          setPhase(PHASE.REVIEW);
          setCurrentSlot(Math.max(0, newSlots.length - 1));
        }
      }
    }, 0);
  };

  // Mark a slot SKIPPED — keeps the question in the list but flags the clip
  // as intentionally skipped. Used by the Skip button and the switch-dump
  // flow (when the user abandons an in-flight clip but wants the slot to
  // remain so they can come back to it).
  const markSlotSkipped = (slotIndex, { advance = true } = {}) => {
    if (slotIndex < 0) return;
    let pendingNewSlots = null;
    setSlots((prev) => {
      const newSlots = prev.map((s, i) => (i === slotIndex
        ? { ...s, status: SLOT_STATUS.SKIPPED, file: null, url: '', thumbnail: '' }
        : s));
      pendingNewSlots = newSlots;
      return newSlots;
    });
    if (advance) {
      setTimeout(() => {
        const newSlots = pendingNewSlots || [];
        // eslint-disable-next-line no-use-before-define
        advanceFromSlot(slotIndex, newSlots);
      }, 0);
    }
  };

  // Reset a slot to EMPTY without removing it — used by the switch-dump
  // flow (abandon in-flight clip) and as a phase-independent retake
  // primitive when handleRetakeSlot's guard would block us.
  const resetSlotToEmpty = (slotIndex) => {
    if (slotIndex < 0) return;
    setSlots((prev) => prev.map((s, i) => {
      if (i !== slotIndex) return s;
      if (s.url) URL.revokeObjectURL(s.url);
      return { ...s, status: SLOT_STATUS.EMPTY, file: null, url: '', thumbnail: '' };
    }));
  };

  // Drop a recorded clip into a slot. The slot's own thumbnail does the FLIP:
  // it appears initially at the camera position (via transform offset) then
  // transitions to identity (which is the slot's natural position). Because
  // the element is anchored inside the slot, the end position is guaranteed
  // to match the slot — no risk of containing-block drift.
  const flyClipIntoSlot = (slotIndex, file, url, thumbnail) => {
    const stageEl = stageRef.current;
    const slotEl = slotStripRef.current
      && slotStripRef.current.querySelector(`[data-slot-index="${slotIndex}"]`);
    const fromRect = stageEl ? stageEl.getBoundingClientRect() : null;
    const toRect = slotEl ? slotEl.getBoundingClientRect() : null;

    // TEMP debug — verify the right slot is being measured. Remove after fix verified.
    // eslint-disable-next-line no-console
    console.log('[FLIP] slotIndex=', slotIndex,
      'slotEl?.dataset.slotIndex=', slotEl ? slotEl.dataset.slotIndex : 'null',
      'toRect.top=', toRect ? Math.round(toRect.top) : 'null',
      'allSlotTops=', slotStripRef.current
        ? Array.from(slotStripRef.current.querySelectorAll('[data-slot-index]'))
          .map((el) => `${el.dataset.slotIndex}:${Math.round(el.getBoundingClientRect().top)}`)
          .join(' ')
        : 'no-strip');

    // Skip the FLIP on mobile: the camera lives in a fullscreen modal that
    // closes the instant the clip lands, so animating a thumbnail flying from
    // (former) fullscreen down to a tile reads as a jarring whole-screen zoom.
    const canAnimate = !isMobile && !!(fromRect && toRect && thumbnail);

    // Compute the transform that places the thumbnail visually at the camera.
    // Inverse of the previous approach: we offset FROM the slot back TO the
    // camera, then transition to identity.
    let initialTransform = null;
    if (canAnimate) {
      const tx = fromRect.left - toRect.left;
      const ty = fromRect.top - toRect.top;
      const sx = fromRect.width / toRect.width;
      const sy = fromRect.height / toRect.height;
      initialTransform = `translate(${tx}px, ${ty}px) scale(${sx}, ${sy})`;
    }

    setSlots((prev) => {
      const newSlots = prev.map((s, i) => (i === slotIndex
        ? { ...s, status: SLOT_STATUS.DONE, file, url, thumbnail }
        : s));
      // eslint-disable-next-line no-use-before-define
      setTimeout(() => advanceFromSlot(slotIndex, newSlots), 0);
      return newSlots;
    });

    if (canAnimate) {
      // Render the slot thumbnail with the camera-offset transform first,
      // then on the next frame flip to identity to trigger the CSS transition.
      setFlyingClip({ slotIndex, transform: initialTransform });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setFlyingClip((curr) => (curr && curr.slotIndex === slotIndex
            ? { ...curr, transform: 'translate(0px, 0px) scale(1, 1)' }
            : curr));
        });
      });
      setTimeout(() => setFlyingClip(null), FLIP_ANIM_MS + 40);
    }
  };

  // Find next empty slot strictly after slotIndex; if none, REVIEW.
  // If `pendingTargetAfterClipRef` is set (switch-save flow), jump there
  // instead of scanning forward — the user picked an explicit destination.
  const advanceFromSlot = (slotIndex, slotsArr) => {
    // Mobile: the just-finished (or skipped) clip closes the camera modal so
    // the user returns to the tile list.
    if (isMobile) setMobileSessionOpen(false);
    if (pendingTargetAfterClipRef.current >= 0) {
      const target = pendingTargetAfterClipRef.current;
      pendingTargetAfterClipRef.current = -1;
      const targetSlot = slotsArr[target];
      if (!targetSlot) return;
      // Park on the chosen slot in IDLE — the user has to press Start
      // Recording to actually begin. Per global rule: clicks navigate,
      // they don't auto-record (including the post-Save/Throw landing).
      setCurrentSlot(target);
      setPhase(PHASE.IDLE);
      return;
    }
    // Finished one clip → advance to the next EMPTY slot but DO NOT
    // auto-start recording. The user clicks Start Recording manually on
    // each slot — this is the global "no auto record next question" rule
    // (especially important for commentary slots where every recording
    // needs media setup, but applied across the board for consistency).
    let next = slotsArr.findIndex((s, i) => i > slotIndex && s.status === SLOT_STATUS.EMPTY);
    if (next === -1) {
      // No empty slot AFTER this one. On mobile the user can record questions
      // out of order, so there may still be empties BEFORE this index — fall
      // back to any remaining empty so we only reach REVIEW once every
      // question is actually done/skipped. (Sequential desktop flow never has
      // earlier empties, so this fallback is a no-op there → web unchanged.)
      next = slotsArr.findIndex((s) => s.status === SLOT_STATUS.EMPTY);
    }
    if (next === -1) {
      // No more questions to record. Show the review only if at least one
      // clip exists; otherwise skip it entirely. (For the receiver flow,
      // skipReview's effect drives the exit, so we don't double-handle.)
      const recordedSomething = slotsArr.some((s) => s.status === SLOT_STATUS.DONE);
      if (!recordedSomething && !skipReview) {
        onCancel(buildSnapshot());
        return;
      }
      setPhase(PHASE.REVIEW);
      setCurrentSlot(slotIndex); // park currentSlot on the last filled slot
    } else {
      setPhase(PHASE.IDLE);
      setCurrentSlot(next);
    }
  };

  // ── Prep countdown ticker ──
  useEffect(() => {
    if (phase !== PHASE.PREP) return undefined;
    // No countdown configured → begin recording immediately.
    if (prepCountdown <= 0) {
      setPhase(PHASE.RECORDING);
      startActualRecording(currentSlot);
      return undefined;
    }
    if (prepCountdown === 1) {
      const t = setTimeout(() => {
        setPrepCountdown(0);
        setPhase(PHASE.RECORDING);
        startActualRecording(currentSlot);
      }, 800);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setPrepCountdown((c) => c - 1), 800);
    return () => clearTimeout(t);
  }, [phase, prepCountdown, currentSlot, startActualRecording]);

  // ── Pre-fetch the question's TTS audio as early as possible ──
  // We kick off the OpenAI TTS request the moment the user lands on a slot
  // (phase === IDLE) — long before they click Start — and again on PREP if
  // somehow the IDLE fetch was skipped or aborted. The prefetch helper is
  // idempotent on (slot, text), so this isn't double-charged. The goal is
  // to hide OpenAI's 2–4s round trip entirely behind the user reading the
  // question / clicking Start / the 3-2-1 countdown.
  useEffect(() => {
    if (phase !== PHASE.IDLE && phase !== PHASE.PREP) return;
    if (!askAloudRef.current) return;
    const item = (liveQuestionsRef.current || [])[currentSlot];
    const text = getQuestion(item);
    if (text) prefetchSpeech(currentSlot, text);
  }, [phase, currentSlot]);

  // ── Play the question audio once when recording actually starts ──
  // Watching the phase transition (instead of inlining the call inside the
  // countdown ticker) guarantees a single fire per PREP→RECORDING edge.
  useEffect(() => {
    if (phase !== PHASE.RECORDING) return;
    if (!askAloudRef.current) {
      // eslint-disable-next-line no-console
      console.log('[REC_TTS] phase→RECORDING but askAloud is off, skipping play');
      return;
    }
    const item = (liveQuestionsRef.current || [])[currentSlot];
    const text = getQuestion(item);
    // eslint-disable-next-line no-console
    console.log('[REC_TTS] phase→RECORDING · slot=', currentSlot, '· textLen=', text ? text.length : 0);
    if (text) playSpeech(currentSlot, text);
    // currentSlot intentionally excluded from deps — one fire per
    // PREP→RECORDING edge, not on every slot change inside RECORDING.
  }, [phase]);

  // ── Recording elapsed-seconds ticker (frozen while paused) ──
  useEffect(() => {
    if (phase !== PHASE.RECORDING || recordingPaused) return undefined;
    const t = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [phase, recordingPaused]);

  // ── Auto-stop when timer hits cap ──
  useEffect(() => {
    if (phase !== PHASE.RECORDING) return;
    if (elapsedSeconds >= durationCap) {
      // eslint-disable-next-line no-use-before-define
      handleFinishRecording();
    }
  }, [elapsedSeconds, durationCap, phase]);

  // ── User actions ──
  const handleStartFlow = () => {
    if (permissionState !== 'granted') return;
    // Prime TTS while we still have a user-gesture frame; the actual
    // question speak() fires later, after the prep countdown.
    primeSpeechSynthesis();
    // Respect the slot the user has parked on — clicking a slot just
    // selects it (no auto-record), so Start Recording must use that
    // slot rather than scanning forward for "first empty". This fixes
    // the bug where adding a new question, selecting that new last
    // slot, then pressing Start Recording started recording the first
    // empty slot (e.g. slot 1) instead of the selected new one.
    const cur = slots[currentSlot];
    if (cur) {
      if (cur.status === SLOT_STATUS.EMPTY || cur.status === SLOT_STATUS.SKIPPED) {
        beginRecordingForSlot(currentSlot);
        return;
      }
      if (cur.status === SLOT_STATUS.DONE) {
        // Pressing Start on a DONE slot means re-record. Inline the
        // wipe-then-begin sequence (handleRetakeSlot is declared later
        // in the file so we can't reference it from here directly).
        resetSlotToEmpty(currentSlot);
        setPlayingSlot(-1);
        beginRecordingForSlot(currentSlot);
        return;
      }
    }
    // Fall back: first empty slot anywhere (handles initial mount where
    // currentSlot is 0 / unset).
    const first = slots.findIndex((s) => s.status === SLOT_STATUS.EMPTY);
    if (first === -1) {
      setPhase(PHASE.REVIEW);
      return;
    }
    beginRecordingForSlot(first);
  };

  // Pause / resume the in-flight recording. MediaRecorder.pause() stops
  // emitting chunks (and the elapsed ticker freezes via recordingPaused);
  // resume() continues the same clip seamlessly. Any commentary source
  // video is paused/resumed in lockstep so narration stays in sync.
  const handlePauseResumeRecording = () => {
    if (phase !== PHASE.RECORDING) return;
    const rec = mediaRecorderRef.current;
    if (!rec) return;
    try {
      if (!recordingPaused && rec.state === 'recording') {
        rec.pause();
        setRecordingPaused(true);
        // Pause the commentary source video too so it doesn't drift ahead.
        const vid = commentaryVideoElRef.current;
        if (vid && !vid.paused) {
          try { vid.pause(); } catch (e) { /* ignore */ }
        }
      } else if (recordingPaused && rec.state === 'paused') {
        rec.resume();
        setRecordingPaused(false);
        // Resume commentary video if the active media is a video. Inlined
        // lookup (instead of getCurrentCommentary) since that helper is
        // defined later in the component.
        const slot = slots[currentSlot];
        const c = (slot && slot.commentary) || null;
        const item = c && c.mediaList && c.mediaList[commentaryMediaIndex];
        const vid = commentaryVideoElRef.current;
        if (item && item.type === 'video' && vid && vid.paused) {
          const p = vid.play();
          if (p && p.catch) p.catch(() => { /* ignore */ });
        }
      }
    } catch (e) { /* ignore */ }
  };

  const handleFinishRecording = () => {
    if (phase !== PHASE.RECORDING) return;
    // Clear paused state on stop so the next recording starts clean.
    setRecordingPaused(false);
    discardCurrentRef.current = false;
    stopSpeaking();
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    } catch (e) { /* noop */ }
    // Don't toggle phase here — onstop → flyClipIntoSlot → advanceFromSlot will move
    // straight to PREP for the next slot (or REVIEW), so we never flash IDLE.
  };

  // Delete the current question — same advance flow as before, but the slot
  // is removed entirely (not flagged SKIPPED). PREP and RECORDING share a
  // path through removeSlot which handles in-flight stop + re-entry.
  const handleDeleteCurrent = () => {
    if (phase === PHASE.PREP) {
      setPrepCountdown(0);
    }
    removeSlot(currentSlot);
  };

  // Delete from IDLE — removes the first empty slot. If we end up with no
  // slots, we stay in IDLE and the user can re-add via "+ Add Question".
  const handleDeleteFromIdle = () => {
    const first = slots.findIndex((s) => s.status === SLOT_STATUS.EMPTY);
    const target = first === -1 ? 0 : first;
    if (slots.length === 0) return;
    setCurrentSlot(target);
    removeSlot(target);
  };

  // Slot-strip ✕ button — works in any phase via removeSlot.
  const handleDeleteSlot = (slotIndex) => {
    removeSlot(slotIndex);
  };

  // Reorder slots via drag. Works in any phase: if the move shifts the
  // currently-recording slot, we update currentSlot + recordingTargetRef so
  // the in-flight clip still lands in the right slot when MediaRecorder
  // fires onstop.
  const handleSlotDragEnd = (result) => {
    if (!result || !result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;

    setLiveQuestions((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setSlots((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });

    const reindex = (idx) => {
      if (idx < 0) return idx;
      if (idx === from) return to;
      if (from < idx && to >= idx) return idx - 1;
      if (from > idx && to <= idx) return idx + 1;
      return idx;
    };
    setCurrentSlot((c) => reindex(c));
    if (recordingTargetRef.current >= 0) {
      recordingTargetRef.current = reindex(recordingTargetRef.current);
    }
    if (pendingTargetAfterClipRef.current >= 0) {
      pendingTargetAfterClipRef.current = reindex(pendingTargetAfterClipRef.current);
    }
  };

  // Skip the current slot — leaves the question in the list, flags it
  // SKIPPED, then advances to the next EMPTY (or REVIEW if none).
  // Distinct from Delete: the slot stays so the user can come back via click.
  const handleSkipCurrent = () => {
    if (phase === PHASE.RECORDING) {
      discardCurrentRef.current = true;
      stopSpeaking();
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch (e) { /* noop */ }
    } else if (phase === PHASE.PREP) {
      setPrepCountdown(0);
    } else {
      return;
    }
    // markSlotSkipped → advanceFromSlot drives the next phase (PREP for the
    // next empty slot, or REVIEW if none) — no need to bounce through IDLE.
    markSlotSkipped(currentSlot);
  };

  // Skip from IDLE. Skips the current slot if it's empty (else the first
  // remaining empty), then advances the view to the next empty slot so the
  // user isn't stuck on the same screen. When NO empty slots remain after
  // the skip: show the review if anything was recorded, otherwise skip the
  // review entirely and close the recorder. (Receiver flow leaves the exit
  // to skipReview's effect.)
  const handleSkipFromIdle = () => {
    let target = currentSlot;
    if (!slots[target] || slots[target].status !== SLOT_STATUS.EMPTY) {
      target = slots.findIndex((s) => s.status === SLOT_STATUS.EMPTY);
    }
    if (target === -1) return;

    markSlotSkipped(target, { advance: false });

    // Prefer the next empty AFTER target; fall back to any other empty.
    let nextEmpty = slots.findIndex((s, i) => i > target && s.status === SLOT_STATUS.EMPTY);
    if (nextEmpty === -1) {
      nextEmpty = slots.findIndex((s, i) => i !== target && s.status === SLOT_STATUS.EMPTY);
    }
    if (nextEmpty !== -1) {
      setCurrentSlot(nextEmpty);
      return;
    }
    // No empty slots remain.
    if (skipReview) return; // receiver: skipReview effect handles the exit
    const recordedSomething = slots.some((s) => s.status === SLOT_STATUS.DONE);
    if (recordedSomething) {
      setPhase(PHASE.REVIEW);
      setCurrentSlot(target);
    } else {
      onCancel(buildSnapshot());
    }
  };

  // Re-record the current slot from scratch — discard the in-flight clip
  // and restart the prep + recording for the same slot index.
  const handleRerecord = () => {
    if (phase !== PHASE.PREP && phase !== PHASE.RECORDING) return;
    stopSpeaking();
    primeSpeechSynthesis();
    if (phase === PHASE.RECORDING) {
      rerecordRef.current = true;
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch (e) { /* noop */ }
    }
    // Restart prep for the same slot. beginRecordingForSlot resets timers,
    // duration cap, and re-enters PHASE.PREP.
    beginRecordingForSlot(currentSlot);
  };

  // Reset — discard the in-flight (recording OR paused) clip and return the
  // slot to its EMPTY/IDLE state so the user sees "Start Recording" again
  // for this slot. Unlike Re-record, it does NOT auto-restart.
  const handleResetToIdle = () => {
    if (phase !== PHASE.PREP && phase !== PHASE.RECORDING) return;
    // discardCurrentRef tells recorder.onstop to drop the chunks; works for
    // both 'recording' and 'paused' MediaRecorder states.
    discardCurrentRef.current = true;
    stopSpeaking();
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    } catch (e) { /* noop */ }
    setPrepCountdown(0);
    setRecordingPaused(false);
    setElapsedSeconds(0);
    resetSlotToEmpty(currentSlot);
    setPhase(PHASE.IDLE);
  };

  // Mobile "Start Recording" inside the camera modal — begins recording the
  // slot the modal is parked on (currentSlot), unlike the desktop Start button
  // which scans for the first empty slot.
  const handleMobileStartRecording = () => {
    if (permissionState !== 'granted') return;
    primeSpeechSynthesis();
    beginRecordingForSlot(currentSlot);
  };

  // Mobile "Skip" inside the camera modal — reuse the desktop skip-from-idle
  // logic (marks the current question skipped, then advances to the next empty
  // slot OR to REVIEW when none remain), then close the camera modal. Using
  // the shared logic is what makes skipping the LAST question land on the
  // review/attach screen instead of bouncing back to the tile list.
  const handleMobileSkip = () => {
    if (currentSlot < 0 || currentSlot >= slots.length) return;
    handleSkipFromIdle();
    setMobileSessionOpen(false);
  };

  const handleExtend = (deltaSeconds) => {
    setDurationCap((d) => d + deltaSeconds);
    setWarningDismissed(true); // hide until the next 15s window opens
  };

  const handleRetakeSlot = (slotIndex) => {
    if (phase === PHASE.PREP || phase === PHASE.RECORDING) return;
    setSlots((prev) => prev.map((s, i) => {
      if (i !== slotIndex) return s;
      if (s.url) URL.revokeObjectURL(s.url);
      return { ...s, status: SLOT_STATUS.EMPTY, file: null, url: '', thumbnail: '' };
    }));
    setPlayingSlot(-1);
    beginRecordingForSlot(slotIndex);
  };

  // Click-to-switch handler — invoked when the user taps any slot in the
  // strip. Behaviour depends on the current phase:
  //   IDLE     → start / retake the tapped slot (or just park cursor on a
  //              SKIPPED slot so the user can choose to record it)
  //   PREP     → cancel countdown and re-prep on the new slot (no clip yet)
  //   RECORDING → open the keep/dump confirm modal — the in-flight clip
  //               must be decided before we jump
  //   REVIEW   → standard retake of the tapped slot
  const handleSwitchToSlot = (toSlot) => {
    if (toSlot < 0 || toSlot >= slots.length) return;
    if (toSlot === currentSlot && (phase === PHASE.PREP || phase === PHASE.RECORDING)) return;
    const target = slots[toSlot];
    if (!target) return;

    // Mobile: tapping a tile only OPENS the camera modal parked on that
    // slot (phase stays IDLE). Same intent on desktop now too — clicking
    // a slot just SELECTS it; the user has to press Start Recording to
    // actually begin. Never auto-start on a click.
    if (isMobile) {
      setCurrentSlot(toSlot);
      primeSpeechSynthesis();
      setPhase(PHASE.IDLE);
      setMobileSessionOpen(true);
      return;
    }

    if (phase === PHASE.IDLE || phase === PHASE.REVIEW) {
      // Just navigate — don't auto-start recording and don't auto-retake
      // a DONE slot. Re-record is reached via the explicit "Re-record"
      // button (PREP/RECORDING) or by clicking Start Recording while
      // parked on a DONE slot (which uses handleRetakeSlot internally
      // there).
      setCurrentSlot(toSlot);
      return;
    }
    if (phase === PHASE.PREP) {
      // Cancel the in-flight countdown and park the cursor on the new
      // slot in IDLE. No new prep / no auto-record.
      setPrepCountdown(0);
      stopSpeaking();
      setPhase(PHASE.IDLE);
      setCurrentSlot(toSlot);
      return;
    }
    if (phase === PHASE.RECORDING) {
      // Active recording — keep the existing Save/Throw modal so the
      // user explicitly decides what to do with the in-flight clip
      // before the cursor moves.
      setPendingSwitch({ fromSlot: currentSlot, toSlot });
    }
  };

  // Back button (sender / contributor flows): show confirmation if there's
  // anything that would be lost, otherwise bail immediately.
  const handleBackButton = () => {
    const hasRecordedClip = slots.some((s) => s.status === SLOT_STATUS.DONE);
    const hasInFlightClip = phase === PHASE.PREP || phase === PHASE.RECORDING;
    if (hasRecordedClip || hasInFlightClip) {
      setPendingBackExit(true);
      return;
    }
    if (onCancel) onCancel(buildSnapshot());
  };
  const cancelBackExit = () => setPendingBackExit(false);
  const confirmBackExit = () => {
    setPendingBackExit(false);
    stopSpeaking();
    // If a clip is in flight, drop it before exiting so onstop doesn't try
    // to land a blob into an unmounted slot grid.
    if (phase === PHASE.PREP || phase === PHASE.RECORDING) {
      discardCurrentRef.current = true;
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch (e) { /* noop */ }
    }
    if (onCancel) onCancel(buildSnapshot());
  };

  // Switch-confirm modal handlers
  const cancelSwitch = () => setPendingSwitch(null);

  const confirmSwitchSave = () => {
    if (!pendingSwitch) return;
    const { toSlot } = pendingSwitch;
    setPendingSwitch(null);
    primeSpeechSynthesis();
    // Override advanceFromSlot's next-empty scan — after the in-flight clip
    // lands, jump to the user's chosen destination instead.
    pendingTargetAfterClipRef.current = toSlot;
    handleFinishRecording();
  };

  const confirmSwitchDump = () => {
    if (!pendingSwitch) return;
    const { fromSlot, toSlot } = pendingSwitch;
    setPendingSwitch(null);
    // Discard the in-flight chunks. onstop sees discardCurrentRef and just
    // drops the bytes; we own the from-slot reset + the jump here.
    discardCurrentRef.current = true;
    stopSpeaking();
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    } catch (e) { /* noop */ }
    resetSlotToEmpty(fromSlot);
    // Park on the new slot in IDLE — user will press Start Recording.
    // Deferred to next tick so the recorder stop's onstop fires first and
    // the from-slot reset has committed before we switch the cursor.
    setTimeout(() => {
      setCurrentSlot(toSlot);
      setPhase(PHASE.IDLE);
    }, 0);
  };

  // Returns the section that owns a given slot index, by reading the slot's
  // own `section` tag. This is deletion-safe: removing a Beginning slot
  // doesn't reclassify a later Hustle slot, because the tag travels with the
  // slot regardless of its position in the array. Returns null for custom-
  // added slots and when sections prop isn't set.
  const getSectionForSlot = (slotIdx) => {
    if (!Array.isArray(sections)) return null;
    const slot = slots[slotIdx];
    if (!slot || !slot.section) return null;
    return sections.find((s) => s.title === slot.section) || null;
  };

  // ── Commentary helpers ───────────────────────────────────────────────
  // Stable blob URL per File so the hidden <video>/<img> elements don't
  // reload between renders. URLs are revoked on unmount.
  const getCommentaryMediaUrl = (file) => {
    if (!file) return '';
    const cache = commentaryMediaUrlsRef.current;
    if (!cache.has(file)) cache.set(file, URL.createObjectURL(file));
    return cache.get(file);
  };
  // Quick MIME sniff — File.type is empty for some clipboard pastes, fall
  // back to extension.
  const detectMediaType = (file) => {
    if (!file) return 'image';
    const t = (file.type || '').toLowerCase();
    if (t.startsWith('video/')) return 'video';
    if (t.startsWith('image/')) return 'image';
    const ext = (file.name || '').toLowerCase().split('.').pop();
    if (['mp4', 'mov', 'webm', 'mkv', 'm4v', 'avi'].includes(ext)) return 'video';
    return 'image';
  };
  // Returns the current slot's commentary payload (or null).
  const getCurrentCommentary = () => {
    const slot = slots[currentSlot];
    return (slot && slot.commentary) || null;
  };
  // Convenience flag — true when current slot is in commentary mode.
  const isCommentaryActive = !!getCurrentCommentary();
  // Setter that updates a slot's commentary field while preserving
  // everything else. Used by media picker apply, navigation, PIP edit, etc.
  const updateSlotCommentary = (slotIdx, updater) => {
    setSlots((prev) => prev.map((s, i) => {
      if (i !== slotIdx) return s;
      const next = typeof updater === 'function' ? updater(s.commentary) : updater;
      return { ...s, commentary: next };
    }));
  };
  // Build a commentary payload from a list of files in their chosen order.
  const buildCommentaryFromFiles = (orderedFiles) => ({
    mediaList: orderedFiles.map((file) => ({
      id: `cmt-${nextSlotId()}`,
      file,
      type: detectMediaType(file),
      name: file.name,
    })),
    pipConfig: { ...pipConfigRef.current },
  });

  // ── Commentary handlers ─────────────────────────────────────────────
  const handleOpenAddMedia = () => {
    if (commentaryFileInputRef.current) commentaryFileInputRef.current.click();
  };
  const handleCommentaryFilesPicked = (eventOrFiles) => {
    const fileList = eventOrFiles && eventOrFiles.target
      ? eventOrFiles.target.files
      : eventOrFiles;
    const files = Array.from(fileList || []);
    if (!files.length) return;
    // Filter to image/video only — anything else is silently dropped.
    const valid = files.filter((f) => {
      const t = detectMediaType(f);
      return t === 'image' || t === 'video';
    });
    if (!valid.length) return;
    setCommentaryPendingFiles(valid);
    setCommentaryOrderOpen(true);
    if (eventOrFiles && eventOrFiles.target) {
      // Allow the same file selection to re-trigger onChange next time.
      const inputEl = eventOrFiles.target;
      setTimeout(() => { inputEl.value = ''; }, 0);
    }
  };
  const handleCommentaryOrderDragEnd = (result) => {
    if (!result || !result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    setCommentaryPendingFiles((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };
  const handleCommentaryOrderCancel = () => {
    setCommentaryOrderOpen(false);
    setCommentaryPendingFiles([]);
  };
  const handleCommentaryOrderApply = () => {
    const payload = buildCommentaryFromFiles(commentaryPendingFiles);
    updateSlotCommentary(currentSlot, payload);
    setCommentaryOrderOpen(false);
    setCommentaryPendingFiles([]);
    setCommentaryMediaIndex(0);
    setCommentaryIsPlaying(false);
  };
  const handleRemoveCommentary = () => {
    updateSlotCommentary(currentSlot, null);
    setCommentaryMediaIndex(0);
    setCommentaryIsPlaying(false);
  };
  // User-gesture handler that resumes the AudioContext if it was created
  // in the suspended state (Chrome/Safari default until first user input).
  // Without this, the MediaElementSource → gain → destination graph yields
  // pure silence even though play() succeeds and the video appears to run.
  const ensureAudioContextRunning = () => {
    const ctx = audioContextRef.current;
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      // eslint-disable-next-line no-console
      console.log('[REC_AUDIO] AudioContext suspended → resuming');
      ctx.resume().then(() => {
        // eslint-disable-next-line no-console
        console.log('[REC_AUDIO] AudioContext resumed, state =', ctx.state);
      }).catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[REC_AUDIO] AudioContext resume failed:', err);
      });
    } else {
      // eslint-disable-next-line no-console
      console.log('[REC_AUDIO] AudioContext state =', ctx.state);
    }
  };

  // Navigation — also resume the AudioContext on any user gesture, since
  // the AudioContext is created in a useEffect (before any user input) and
  // sits in 'suspended' state until something resumes it. Browsers count
  // ANY user-initiated input as a valid gesture for that.
  const handleCommentaryPrev = () => {
    ensureAudioContextRunning();
    const c = getCurrentCommentary();
    if (!c) return;
    const next = Math.max(0, commentaryMediaIndex - 1);
    setCommentaryMediaIndex(next);
    setCommentaryIsPlaying(false);
    shouldAutoPlayNextRef.current = true;
  };
  const handleCommentaryNext = () => {
    ensureAudioContextRunning();
    const c = getCurrentCommentary();
    if (!c) return;
    const next = Math.min((c.mediaList.length - 1), commentaryMediaIndex + 1);
    setCommentaryMediaIndex(next);
    setCommentaryIsPlaying(false);
    shouldAutoPlayNextRef.current = true;
  };
  // Video controls (no-op for image items)
  const handleCommentarySeek = (deltaSec) => {
    ensureAudioContextRunning();
    const vid = commentaryVideoElRef.current;
    if (!vid) return;
    try {
      const target = Math.max(0, Math.min((vid.duration || 0), (vid.currentTime || 0) + deltaSec));
      vid.currentTime = target;
    } catch (e) { /* ignore */ }
  };
  const handleCommentaryTogglePlay = () => {
    const vid = commentaryVideoElRef.current;
    if (!vid) return;
    ensureAudioContextRunning();
    try {
      if (vid.paused) {
        const p = vid.play();
        // eslint-disable-next-line no-console
        console.log('[REC_AUDIO] play() called', {
          muted: vid.muted,
          volume: vid.volume,
          duration: vid.duration,
          readyState: vid.readyState,
          src: vid.currentSrc && vid.currentSrc.slice(0, 60),
        });
        if (p && p.then) {
          p.then(() => setCommentaryIsPlaying(true)).catch((err) => {
            // eslint-disable-next-line no-console
            console.warn('[REC_AUDIO] play() rejected:', err);
          });
        } else {
          setCommentaryIsPlaying(true);
        }
      } else {
        vid.pause();
        setCommentaryIsPlaying(false);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[REC_AUDIO] toggle play threw:', e);
    }
  };

  // Auto-play on Prev/Next when the new active media is a video. The
  // Prev/Next handlers set shouldAutoPlayNextRef, which we consume here
  // AFTER React has committed the new `src` to the persistent <video>
  // element. If the new media isn't yet at readyState>=2 we wait for the
  // `canplay` event before calling play(). Initial mount and non-nav
  // index changes don't auto-play (the ref stays false).
  useEffect(() => {
    if (!shouldAutoPlayNextRef.current) return undefined;
    shouldAutoPlayNextRef.current = false;
    const c = getCurrentCommentary();
    const item = c && c.mediaList && c.mediaList[commentaryMediaIndex];
    if (!item || item.type !== 'video') return undefined;
    const vid = commentaryVideoElRef.current;
    if (!vid) return undefined;
    ensureAudioContextRunning();
    const tryPlay = () => {
      const p = vid.play();
      if (p && p.then) {
        p.then(() => setCommentaryIsPlaying(true)).catch(() => { /* swallow */ });
      } else {
        setCommentaryIsPlaying(true);
      }
    };
    if (vid.readyState >= 2) {
      tryPlay();
      return undefined;
    }
    const onCanPlay = () => {
      vid.removeEventListener('canplay', onCanPlay);
      tryPlay();
    };
    vid.addEventListener('canplay', onCanPlay);
    return () => vid.removeEventListener('canplay', onCanPlay);
  }, [commentaryMediaIndex, currentSlot]);

  // Aspect of the displayed canvas (cssWidth / cssHeight). Used by the
  // circle-shape constraint to lock `cfg.h` so the rendered HTML overlay
  // and the canvas-painted ellipse both come out as true circles.
  const getCanvasCssAspect = () => {
    const el = canvasRef.current;
    if (!el) return 16 / 9;
    const r = el.getBoundingClientRect();
    if (!r.height) return 16 / 9;
    return r.width / r.height;
  };

  // PIP edits — pre-record only. Updates the ref immediately (so the draw
  // loop reflects it) AND mirrors into slot state on commit. When shape is
  // 'circle', cfg.h is derived from cfg.w × canvasAspect so the rendered
  // overlay and canvas paint resolve to a perfect circle (not an ellipse)
  // regardless of where on the canvas the PIP sits.
  const setPipConfig = (next) => {
    const prev = pipConfigRef.current;
    const merged = { ...prev, ...next };
    const enteringCircle = merged.shape === 'circle' && prev.shape !== 'circle';
    const leavingCircle = prev.shape === 'circle' && merged.shape !== 'circle';
    // Stash the height the moment we enter circle so we can put it back later.
    if (enteringCircle) preCircleHeightRef.current = prev.h;
    // Leaving circle for square/squircle/rect: restore the pre-circle height
    // (unless this same call explicitly sets a new height, e.g. a resize).
    if (leavingCircle && next.h === undefined && preCircleHeightRef.current != null) {
      merged.h = preCircleHeightRef.current;
      preCircleHeightRef.current = null;
    }
    if (merged.shape === 'circle') {
      const aspect = getCanvasCssAspect();
      // cfg.w × cssW = cfg.h × cssH  →  cfg.h = cfg.w × (cssW / cssH).
      // This makes the PIP a CSS-pixel square; border-radius:50% on the
      // overlay and canvas ellipse(rx=ww/2, ry=hh/2) then both render as
      // a true circle.
      merged.h = merged.w * aspect;
      // Clamp if the derived height would push past the canvas bottom;
      // shrink width instead so the circle stays inside the frame.
      if (merged.y + merged.h > 1) {
        merged.h = Math.max(0.05, 1 - merged.y);
        merged.w = merged.h / aspect;
      }
      // Same clamp on width side (rare — only when canvas is very tall).
      if (merged.x + merged.w > 1) {
        merged.w = Math.max(0.05, 1 - merged.x);
        merged.h = merged.w * aspect;
      }
    }
    pipConfigRef.current = merged;
    setPipConfigVersion((v) => v + 1);
    const c = getCurrentCommentary();
    if (c) {
      updateSlotCommentary(currentSlot, { ...c, pipConfig: { ...pipConfigRef.current } });
    }
  };

  // ── Commentary effects (declared AFTER handlers so they can reference
  // helpers + handlers without hitting no-use-before-define). ──

  // Audio graph wiring for the active commentary VIDEO. The hidden <video>
  // element is muted in the DOM (silent on the page) but Web Audio still
  // pulls PCM from it through MediaElementSource → SourceGain (0.2) → mix
  // destination. Photos contribute nothing; switching media disposes the
  // old MediaElementSource before creating a new one.
  // Attach the persistent <video> element to the Web Audio graph EXACTLY
  // ONCE per element instance. createMediaElementSource throws if called
  // a second time on the same element, so we track attachment via
  // sourceVideoNodeRef. Item navigation never disconnects/reconnects — it
  // only swaps the element's `src`, and the existing source node
  // automatically pulls from the new media.
  useEffect(() => {
    const audioCtx = audioContextRef.current;
    const srcGain = sourceGainNodeRef.current;
    const monitor = monitorGainNodeRef.current;
    const vidEl = commentaryVideoElRef.current;
    // eslint-disable-next-line no-console
    console.log('[REC_AUDIO] (attach effect)', {
      hasCtx: !!audioCtx,
      ctxState: audioCtx ? audioCtx.state : null,
      hasSrcGain: !!srcGain,
      hasMonitor: !!monitor,
      hasVidEl: !!vidEl,
      alreadyAttached: !!sourceVideoNodeRef.current,
    });
    if (!audioCtx || !srcGain || !vidEl) return;
    if (sourceVideoNodeRef.current) return;
    try {
      const node = audioCtx.createMediaElementSource(vidEl);
      node.connect(srcGain);
      if (monitor) node.connect(monitor);
      sourceVideoNodeRef.current = node;
      // eslint-disable-next-line no-console
      console.log('[REC_AUDIO] attached sourceVideo → gains (once-only)');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[REC_AUDIO] createMediaElementSource threw:', e && e.message ? e.message : e);
    }
  }, [isCommentaryActive, permissionState]);

  // Gain control: photos / no-media / mute → silence both record + monitor
  // paths. Active video + unmuted → record 0.2 (ducked), monitor 0.7.
  // Independent from the attach effect so it can run on every nav/toggle
  // without ever touching the source node.
  useEffect(() => {
    const srcGain = sourceGainNodeRef.current;
    const monitor = monitorGainNodeRef.current;
    if (!srcGain) return;
    const c = getCurrentCommentary();
    const item = c && c.mediaList && c.mediaList[commentaryMediaIndex];
    const isVideoItem = item && item.type === 'video';
    const recordLvl = (isVideoItem && !commentarySourceMuted) ? 0.2 : 0.0;
    const monitorLvl = (isVideoItem && !commentarySourceMuted) ? 0.7 : 0.0;
    srcGain.gain.value = recordLvl;
    if (monitor) monitor.gain.value = monitorLvl;
    // eslint-disable-next-line no-console
    console.log('[REC_AUDIO] (gain sync) gains set', {
      isVideoItem,
      muted: commentarySourceMuted,
      recordGain: recordLvl,
      monitorGain: monitorLvl,
    });
  }, [currentSlot, commentaryMediaIndex, slots, commentarySourceMuted]);

  // Close the AudioContext when the recorder unmounts to release resources.
  useEffect(() => () => {
    if (audioContextRef.current) {
      try { audioContextRef.current.close(); } catch (e) { /* ignore */ }
      audioContextRef.current = null;
    }
  }, []);

  // Keyboard navigation for commentary media. ← / → step through the media
  // list; J / L seek -10s / +10s in the active video; K toggles play.
  // Listener is window-level so it works even when no input is focused, but
  // we ignore events whose target is an editable element (textarea/input)
  // so the custom-question modal still accepts arrow keys normally.
  useEffect(() => {
    if (!isCommentaryActive) return undefined;
    if (commentaryOrderOpen) return undefined;
    const isEditable = (target) => {
      if (!target) return false;
      const tag = (target.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || target.isContentEditable;
    };
    const onKey = (e) => {
      if (isEditable(e.target)) return;
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          handleCommentaryPrev();
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleCommentaryNext();
          break;
        case 'j':
        case 'J':
          e.preventDefault();
          handleCommentarySeek(-10);
          break;
        case 'l':
        case 'L':
          e.preventDefault();
          handleCommentarySeek(10);
          break;
        case 'k':
        case 'K':
          e.preventDefault();
          handleCommentaryTogglePlay();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isCommentaryActive, commentaryOrderOpen, commentaryMediaIndex, currentSlot]);

  // Top-right Finish button handler. Marks every still-empty/in-flight
  // slot as SKIPPED so the slot list is fully settled, then:
  //   • non-skipReview flows (sender / contributor) → transition into
  //     PHASE.REVIEW so the user can preview their recorded clips and
  //     submit from there. The host's review submit emits onComplete.
  //   • skipReview flows (receiver tile hub) → the existing settle-
  //     effect (~line 2710) fires onComplete automatically once every
  //     slot is settled, returning the user to the hub.
  const handleFinishAll = () => {
    // Discard any in-flight clip — user is intentionally bailing on
    // this prompt. discardCurrentRef tells recorder.onstop to drop the
    // bytes for both 'recording' and 'paused' MediaRecorder states.
    if (phase === PHASE.PREP || phase === PHASE.RECORDING) {
      discardCurrentRef.current = true;
      stopSpeaking();
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch (e) { /* noop */ }
    }
    // Compute the next slot state locally so the skipReview branch can
    // emit onComplete with the up-to-date slots in the SAME tick — relying
    // on the settled-effect to fire after setSlots is fragile when the
    // restored snapshot already has every slot settled (no state change →
    // effect doesn't run → user can't exit).
    const updatedSlots = slots.map((s) => (
      s.status === SLOT_STATUS.EMPTY || s.status === SLOT_STATUS.RECORDING
        ? {
          ...s,
          status: SLOT_STATUS.SKIPPED,
          file: null,
          url: '',
          thumbnail: '',
        }
        : s
    ));
    setSlots(updatedSlots);
    const hasRecordedClips = updatedSlots.some(
      (s) => s.status === SLOT_STATUS.DONE && s.file,
    );
    if (skipReview) {
      // Receiver tile-hub: fire onComplete directly so this works whether
      // the user just recorded everything OR they re-opened a fully-settled
      // restored section and pressed Finish to leave.
      const done = updatedSlots.filter(
        (s) => s.status === SLOT_STATUS.DONE && s.file,
      );
      const files = done.map((s) => s.file);
      const fileSections = done.map((s) => s.section || null);
      skipReviewSettledRef.current = true;
      onComplete(files, {
        fileSections,
        snapshot: {
          liveQuestions: liveQuestionsRef.current
            ? liveQuestionsRef.current.slice()
            : [],
          slots: updatedSlots,
        },
      });
      return;
    }
    // Sender / contributor: drop into REVIEW so the user sees their
    // clips with the existing Submit / Re-record controls — but
    // only if there's actually something to preview. With zero
    // recorded clips, fire onComplete([]) so the host advances to
    // its next page (Add Media for sender) instead of bouncing
    // back to the prompt screen via onCancel.
    if (hasRecordedClips) {
      setPhase(PHASE.REVIEW);
    } else if (Array.isArray(sections)) {
      onComplete([], { fileSections: [], snapshot: buildSnapshot() });
    } else {
      onComplete([], { snapshot: buildSnapshot() });
    }
    // skipReview flows (receiver tile hub) don't enter REVIEW at all —
    // the existing settle-effect (~line 2710) fires onComplete (with
    // an empty array when nothing was recorded) so the hub host gets
    // notified and can navigate forward.
  };

  const handleAttachAll = () => {
    const done = slots
      .map((s, idx) => ({ slot: s, idx }))
      .filter(({ slot }) => slot.status === SLOT_STATUS.DONE && slot.file);
    if (!done.length) return;
    const files = done.map(({ slot }) => slot.file);
    const snapshot = buildSnapshot();
    if (Array.isArray(sections)) {
      // Parallel array of section titles (or null for custom-added) so the
      // caller can tag each upload's category_name without re-deriving.
      const fileSections = done.map(({ idx }) => {
        const s = getSectionForSlot(idx);
        return s ? s.title : null;
      });
      onComplete(files, { fileSections, snapshot });
    } else {
      onComplete(files, { snapshot });
    }
  };

  // Receiver flow: surface the "Do you have any questions to add?" prompt
  // the FIRST time we enter REVIEW (one-shot). After the user has answered
  // it once, subsequent REVIEW entries (e.g. after recording another custom
  // prompt) go straight to the section-grouped review grid.
  useEffect(() => {
    if (phase === PHASE.REVIEW && autoAskMoreAfterPreset && !receiverAskMoreFired) {
      setReceiverAskMore(true);
      setReceiverAskMoreFired(true);
    }
  }, [phase, autoAskMoreAfterPreset, receiverAskMoreFired]);

  // Tile-hub flow: when `skipReview` is set, the host owns the review UI.
  // The recorder auto-returns to the host whenever every slot is "settled"
  // (DONE or SKIPPED), regardless of which phase we're in. This covers:
  //   • all prompts recorded → fires onComplete with files
  //   • all prompts skipped from IDLE → fires onComplete with files=[]
  //   • all custom slots deleted (slots.length=0) → fires onCancel
  // (skipReviewSettledRef is declared higher up so handleFinishAll can
  // flip it before firing onComplete directly.)
  // When we mount with `initialState`, the restored slots may already all
  // be settled (DONE/SKIPPED) — that's by design (user pressed Finish last
  // time). Without this guard, the settled effect would auto-fire on the
  // very first commit and immediately bounce the user back to the hub, so
  // they'd never see their previously-recorded clips. Consume the guard on
  // the first run, then let normal behavior take over for subsequent
  // user-driven state changes.
  const skipInitialSettledRef = useRef(hasInitialStateRef.current);
  useEffect(() => {
    if (skipInitialSettledRef.current) {
      skipInitialSettledRef.current = false;
      return;
    }
    if (!skipReview || skipReviewSettledRef.current) return;
    if (!Array.isArray(sections)) return;
    if (slots.length === 0) {
      // User deleted every slot — there's nothing to save, so treat as a
      // cancel and bounce back to the host without an empty onComplete.
      skipReviewSettledRef.current = true;
      if (onCancel) onCancel(buildSnapshot());
      return;
    }
    const hasUnsettled = slots.some(
      (s) => s.status === SLOT_STATUS.EMPTY || s.status === SLOT_STATUS.RECORDING,
    );
    if (hasUnsettled) return;
    // All slots settled — emit onComplete with whatever's DONE (could be 0).
    skipReviewSettledRef.current = true;
    const done = slots
      .map((s, idx) => ({ slot: s, idx }))
      .filter(({ slot }) => slot.status === SLOT_STATUS.DONE && slot.file);
    const files = done.map(({ slot }) => slot.file);
    const fileSections = done.map(({ slot }) => slot.section || null);
    onComplete(files, { fileSections, snapshot: buildSnapshot() });
  }, [skipReview, sections, slots, onComplete, onCancel]);

  // Tile hub "Other" mount: open the custom-question modal automatically on
  // first mount so the user lands directly on the add-question dialog. One-
  // shot — guarded by a ref so the modal doesn't re-open after the user
  // submits/cancels it.
  const autoOpenAddFiredRef = useRef(false);
  useEffect(() => {
    if (!autoOpenAddQuestion || autoOpenAddFiredRef.current) return;
    autoOpenAddFiredRef.current = true;
    setAddAndRecord(true);
    setCustomAddOpen(true);
  }, [autoOpenAddQuestion]);

  // Route phone-side commands (Start / Skip / Finish / Re-record) to the
  // matching desktop handler. The handlers themselves already guard on phase,
  // so a phone tap that's invalid for the current phase is a no-op.
  useEffect(() => {
    if (cameraSource !== 'mobile') return;
    if (!mcSession.lastPhoneCommand) return;
    const { command } = mcSession.lastPhoneCommand;
    if (command === 'startFlow') {
      handleStartFlow();
    } else if (command === 'skipQuestion') {
      if (phase === PHASE.IDLE) handleSkipFromIdle();
      else handleSkipCurrent();
    } else if (command === 'deleteQuestion') {
      if (phase === PHASE.IDLE) handleDeleteFromIdle();
      else handleDeleteCurrent();
    } else if (command === 'finishRecording') {
      handleFinishRecording();
    } else if (command === 'rerecord') {
      handleRerecord();
    } else if (command === 'pauseResume') {
      handlePauseResumeRecording();
    } else if (command === 'disconnect') {
      // Phone tapped "Disconnect" — show the fallback modal immediately
      // instead of waiting for the WebRTC connection to time out.
      setMobileDisconnected(true);
    }
    // eslint-disable-next-line
  }, [mcSession.lastPhoneCommand]);

  // Broadcast full recorder state to the phone so its UI mirrors the desktop:
  // slot dots, current question, prep countdown, elapsed seconds, and the
  // exact button visibility per phase. Sent over the desktop→mobile channel.
  useEffect(() => {
    if (cameraSource !== 'mobile') return;
    if (mcSession.status !== MC_STATUS.CONNECTED) return;
    try {
      mcSession.sendPhase({
        command: 'state',
        phase,
        currentSlot,
        totalSlots: liveQuestions.length,
        subject: getSubject(liveQuestions[currentSlot]) || '',
        question: getQuestion(liveQuestions[currentSlot]) || '',
        durationCap,
        prepCountdown,
        elapsedSeconds,
        slotsStatus: slots.map((s) => s.status),
      });
    } catch (e) { /* noop */ }
    // eslint-disable-next-line
  }, [
    phase,
    currentSlot,
    liveQuestions,
    durationCap,
    prepCountdown,
    elapsedSeconds,
    slots,
    cameraSource,
    mcSession.status,
  ]);

  // Derive a short filesystem-safe subject from the question text (first
  // few words, capped). The subject becomes the filename of the recorded
  // clip (handleFileSelect on the receiver maps filename → video title).
  const deriveSubjectFromQuestion = (q) => {
    const trimmed = (q || '').trim();
    if (!trimmed) return '';
    // Strip trailing punctuation, take first ~50 chars at a word boundary.
    const cleaned = trimmed.replace(/[?!.,;:]+$/g, '');
    if (cleaned.length <= 50) return cleaned;
    const head = cleaned.slice(0, 50);
    const lastSpace = head.lastIndexOf(' ');
    return (lastSpace > 24 ? head.slice(0, lastSpace) : head).trim();
  };

  // Append a custom question + matching empty slot from the modal form.
  // If `addAndRecord` is set (from the REVIEW-page "+ Add Question" tile),
  // we mark the new slot's index in a ref so the useEffect below can kick
  // off PREP for it once React commits the new state.
  const handleAddCustomQuestion = () => {
    const question = customQuestion.trim();
    if (!question) return;
    const subject = deriveSubjectFromQuestion(question);
    const recordImmediately = addAndRecord;
    // The new entry is appended, so its index is the CURRENT length. We must
    // read this synchronously here — reading `prev.length` inside the
    // setLiveQuestions updater doesn't work in React 16 because the updater
    // runs after the event handler returns, so any value captured there
    // would be unavailable when we set the ref below.
    const newIndex = liveQuestions.length;
    const sectionTag = pendingCustomSectionRef.current;
    pendingCustomSectionRef.current = null;
    setLiveQuestions((prev) => [...prev, { question, subject }]);
    setSlots((prev) => [...prev, {
      id: nextSlotId(), status: SLOT_STATUS.EMPTY, file: null, url: '', thumbnail: '', section: sectionTag, commentary: null,
    }]);
    if (recordImmediately) {
      pendingRecordIndexRef.current = newIndex;
    }
    setCustomQuestion('');
    setCustomFocused(false);
    setCustomAddOpen(false);
    setAddAndRecord(false);
    // Tiles past the fold (8th onwards) land in the scrollable list below the
    // visible area, and users don't always realise the list scrolls. Bring the
    // freshly added question into view once it has rendered. scrollIntoView
    // targets whichever ancestor actually scrolls (.slotStripList on desktop,
    // .recorderBody on mobile).
    setTimeout(() => {
      const el = slotStripRef.current
        && slotStripRef.current.querySelector(`[data-slot-index="${newIndex}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 80);
  };

  // Dismiss the custom-question modal. If there are no slots at all (e.g. a
  // no-prompt "Other"/custom/added section where the modal auto-opened and
  // the user closed it without adding anything), bail back to the host
  // instead of stranding the user on an empty recorder.
  const handleCustomAddDismiss = () => {
    setCustomAddOpen(false);
    setCustomFocused(false);
    setAddAndRecord(false);
    pendingCustomSectionRef.current = null;
    if (slots.length === 0 && onCancel) onCancel(buildSnapshot());
  };

  // Pick up a pending auto-record once liveQuestions has the new entry.
  useEffect(() => {
    const idx = pendingRecordIndexRef.current;
    if (idx < 0) return;
    if (idx < liveQuestions.length) {
      pendingRecordIndexRef.current = -1;
      // beginRecordingForSlot is defined later in this component (closure
      // ordering is fine — the effect runs after render).
      // eslint-disable-next-line no-use-before-define
      beginRecordingForSlot(idx);
    }
  }, [liveQuestions]);

  // Suggestions for the current occasion(s), filtered by what the user has
  // typed (case-insensitive substring match). Capped to keep the dropdown
  // performant on slower phones. When multiple occasions are selected, we
  // pull suggestions from all of them (deduped, order preserved); falls
  // back to the single-occasion lookup when only `occasionLabel` is set.
  const customSuggestions = (() => {
    const all = (Array.isArray(occasions) && occasions.length > 0)
      ? getSuggestionsForOccasions(occasions)
      : getSuggestionsForOccasion(occasionLabel);
    const q = customQuestion.trim().toLowerCase();
    const filtered = q
      ? all.filter((s) => s.toLowerCase().includes(q))
      : all;
    return filtered.slice(0, 60);
  })();

  // ── Derived ──
  const totalQuestions = liveQuestions.length;
  const remainingSeconds = Math.max(0, durationCap - elapsedSeconds);
  const showWarning = phase === PHASE.RECORDING
    && remainingSeconds > 0
    && remainingSeconds <= WARNING_THRESHOLD_S
    && !warningDismissed;
  const doneCount = slots.filter((s) => s.status === SLOT_STATUS.DONE).length;

  // Derived from current slot tags so deletions/additions stay in sync; only
  // used as a fallback by code paths not yet migrated to slot.section-based
  // bucketing.
  const presetSlotCount = Array.isArray(sections)
    ? slots.filter((s) => s.section != null).length
    : 0;
  const currentSection = getSectionForSlot(currentSlot);

  // Receiver flow grouping data — drives the slot strip's per-section view.
  // The visible-stepper UI was removed (new tile-hub UX scopes the user to
  // one section at a time), so this only computes the structures the slot
  // strip needs: the section groups (with items keyed by slot.section), the
  // custom/unsectioned items, and which group is currently active.
  const receiverStepperData = (() => {
    if (!Array.isArray(sections)) return null;
    const indexed = slots.map((slot, idx) => ({ slot, idx }));
    const groups = sections.map((s) => ({
      title: s.title,
      intro: s.intro,
      items: indexed.filter(({ slot }) => slot.section === s.title),
    }));
    const customItems = indexed.filter(({ slot }) => !slot.section);
    const currentSlotSection = (slots[currentSlot] && slots[currentSlot].section) || null;
    const activeKey = (() => {
      if (phase === PHASE.REVIEW) return 'other';
      if (!currentSlotSection) return 'other';
      const sIdx = sections.findIndex((s) => s.title === currentSlotSection);
      return sIdx >= 0 ? sIdx : 'other';
    })();
    return {
      groups,
      customItems,
      activeKey,
      activeGroup: activeKey === 'other' ? null : groups[activeKey],
    };
  })();

  // ── Permission denied ──
  if (permissionState === 'denied') {
    return (
      <div className={styles.recorderContainer}>
        <div className={styles.permissionBox}>
          <h2 className={styles.title}>Camera Access Needed</h2>
          <p className={styles.subtitle}>{permissionError}</p>
          <div className={styles.actionRow}>
            <button type="button" className={styles.secondaryButton} onClick={onCancel}>
              Skip Recording
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => {
                setPermissionState('pending');
                setPermissionError('');
                navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: true })
                  .then((stream) => {
                    streamRef.current = stream;
                    if (liveVideoRef.current) liveVideoRef.current.srcObject = stream;
                    setPermissionState('granted');
                  })
                  .catch((err) => {
                    setPermissionState('denied');
                    setPermissionError(err?.message || 'Could not access camera or microphone.');
                  });
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  let headerTitle;
  let headerSubtitle;
  if (phase === PHASE.REVIEW) {
    headerTitle = 'Review Your Messages';
    headerSubtitle = `${doneCount} of ${totalQuestions} recorded${doneCount > 0 ? ' — tap any clip to retake.' : '.'}`;
  } else if (currentSection) {
    // Receiver: page heading reflects the section the current slot belongs to.
    headerTitle = currentSection.title;
    headerSubtitle = currentSection.intro || '';
  } else if (Array.isArray(sections)) {
    // Receiver: currentSlot has gone past the preset (a custom-added prompt).
    headerTitle = 'Your own question';
    headerSubtitle = 'Record a clip for the prompt you just added.';
  } else {
    // Sender flow — unchanged.
    headerTitle = `Record a Message${recipientName ? ` for ${recipientName}` : ''}`;
    headerSubtitle = `${totalQuestions} prompts · about a minute each. Delete or extend any.`;
  }

  return (
    <div
      className={styles.recorderContainer}
      style={(Array.isArray(sections) && phase === PHASE.REVIEW) ? {
        // Receiver flow REVIEW only: let the container grow with content so the
        // outer overlay (gift-content) can scroll the whole recorder vertically
        // when the review grid is taller than the viewport. The base CSS pins
        // this at `calc(100vh - 140px)` + overflow:hidden, which clips the tail
        // of the section-grouped review.
        // NOTE: this override must NOT apply during PREP/RECORDING — `height:auto`
        // there removes the fixed height that `.recorderBody { flex: 1 }` fills,
        // collapsing the camera stage + slot strip. Keeping the base CSS height
        // during recording gives the receiver flow the same mobile-responsive
        // camera/clip layout as the sender flow.
        height: 'auto',
        maxHeight: 'none',
        overflow: 'visible',
      } : undefined}
    >
      {/* Page-corner Back button — sender / contributor flows opt in via
          showInRecorderBack. Receiver flow leaves this off because its host
          (PersonalVideoMessageFlow) renders a back at the overlay level.
          Portalled to <body> so position:fixed is relative to the viewport,
          not the gift-flow step container (whose transform: translateY(0)
          would otherwise turn fixed into absolute-relative-to-that-ancestor
          and push the button below the logo). */}
      {showInRecorderBack && (() => {
        const target = getDragPortalTarget();
        const node = (
          <button
            type="button"
            onClick={handleBackButton}
            aria-label="Back"
            title="Back"
            style={{
              position: 'fixed',
              // More top clearance on mobile (incl. notch safe-area) so the
              // button isn't jammed against the screen edge. Desktop unchanged.
              top: isMobile ? 'calc(28px + env(safe-area-inset-top))' : '18px',
              left: isMobile ? '16px' : '18px',
              zIndex: 9600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              borderRadius: '999px',
              border: '1px solid rgba(255,255,255,0.18)',
              background: 'rgba(0,0,0,0.55)',
              color: '#fff',
              fontFamily: 'inherit',
              fontSize: '0.82rem',
              fontWeight: 700,
              lineHeight: 1,
              cursor: 'pointer',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
            }}
          >
            <span aria-hidden="true">←</span>
            <span>Back</span>
          </button>
        );
        return target ? ReactDOM.createPortal(node, target) : node;
      })()}

      {/* Page-corner Finish button — top-right counterpart to the Back
          button. Marks all still-empty/in-flight slots as skipped and
          fires onComplete with whatever's recorded so far, bypassing
          the review screen so the parent (sender flow / receiver hub)
          can navigate forward immediately. Portalled to <body> for the
          same reason as Back. */}
      {showInRecorderFinish && phase !== PHASE.REVIEW && (() => {
        const target = getDragPortalTarget();
        const node = (
          <button
            type="button"
            onClick={handleFinishAll}
            aria-label="Finish — skip remaining and continue"
            title="Finish (skip remaining)"
            style={{
              position: 'fixed',
              top: isMobile ? 'calc(28px + env(safe-area-inset-top))' : '18px',
              right: isMobile ? '16px' : '18px',
              zIndex: 9600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: '999px',
              border: 'none',
              background: '#5aa5ef',
              color: '#fff',
              fontFamily: 'inherit',
              fontSize: '0.82rem',
              fontWeight: 700,
              lineHeight: 1,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(90, 165, 239, 0.35)',
            }}
          >
            <span>Finish</span>
            <span aria-hidden="true">✓</span>
          </button>
        );
        return target ? ReactDOM.createPortal(node, target) : node;
      })()}

      <div className={styles.recorderHeader}>
        {/* Always render the title/subtitle block. For receiver flow with
            `sections` set, headerTitle/Subtitle already resolve to the
            current section's title + intro; for sender flow it's the
            default "Record a Message" copy. The earlier section stepper UI
            was removed since the new tile-hub UX scopes the user to one
            section at a time, making the stepper redundant. */}
        <h2 className={styles.title}>{headerTitle}</h2>
        <p className={styles.subtitle}>{headerSubtitle}</p>
      </div>

      {/* ── Body: vertical slot column on the left, camera stage on the right ── */}
      <div
        className={styles.recorderBody}
        style={phase === PHASE.REVIEW ? { display: 'none' } : undefined}
      >
        <div
          className={styles.slotStrip}
          ref={slotStripRef}
          style={flyingClip ? {
            overflow: 'visible',
            position: 'relative',
            zIndex: 9999,
          } : undefined}
        >
          {Array.isArray(sections) ? (
            // Receiver flow: the stepper at the top drives section selection,
            // so this column only renders the slots belonging to the active
            // section (or the custom-added "Other" slots).
            (() => {
              const { activeGroup, customItems } = receiverStepperData;
              const renderPlainSlot = (slot, i) => {
                // Highlight the active slot at ALL phases (including IDLE)
                // so the user can tell which slot they're parked on. The
                // pulsing ring (slotPulse) is reserved for the active
                // recording / prep-countdown state.
                const isCurrent = i === currentSlot;
                const isRecording = isCurrent
                  && (phase === PHASE.PREP || phase === PHASE.RECORDING);
                const subject = getSubject(liveQuestions[i]);
                const handleSlotKey = (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSwitchToSlot(i);
                  }
                };
                return (
                  <div
                    key={slot.id}
                    data-slot-index={i}
                    role="button"
                    tabIndex={0}
                    aria-label={`Slot ${i + 1}: ${subject}`}
                    className={[
                      styles.slot,
                      slot.status === SLOT_STATUS.DONE && styles.slotDone,
                      slot.status === SLOT_STATUS.SKIPPED && styles.slotSkipped,
                      isCurrent && styles.slotCurrent,
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleSwitchToSlot(i)}
                    onKeyDown={handleSlotKey}
                  >
                    {slot.status === SLOT_STATUS.DONE && slot.thumbnail ? (
                      <img src={slot.thumbnail} alt="" className={styles.slotThumb} />
                    ) : null}
                    <div className={styles.slotMeta}>
                      <span className={styles.slotIndex}>{i + 1}</span>
                      <span className={styles.slotSubject}>{subject}</span>
                    </div>
                    {slot.status === SLOT_STATUS.SKIPPED && (
                      <span className={styles.slotBadgeSkipped}>Skipped</span>
                    )}
                    <button
                      type="button"
                      className={styles.slotDeleteBtn}
                      onClick={(e) => { e.stopPropagation(); handleDeleteSlot(i); }}
                      onKeyDown={(e) => e.stopPropagation()}
                      aria-label={`Delete question ${i + 1}`}
                      title="Delete question"
                    >
                      ✕
                    </button>
                    {isRecording && <span className={styles.slotPulse} aria-hidden="true" />}
                  </div>
                );
              };
              if (activeGroup) {
                return (
                  <div
                    className={styles.slotStripList}
                    style={flyingClip ? { overflow: 'visible' } : undefined}
                  >
                    {activeGroup.items.map(({ slot, idx }) => renderPlainSlot(slot, idx))}
                  </div>
                );
              }
              return (
                <div
                  className={styles.slotStripList}
                  style={flyingClip ? { overflow: 'visible' } : undefined}
                >
                  {customItems.map(({ slot, idx }) => renderPlainSlot(slot, idx))}
                </div>
              );
            })()
          ) : (
            <DragDropContext onDragEnd={handleSlotDragEnd}>
              <Droppable droppableId="gift-recorder-slots">
                {(droppableProvided, droppableSnapshot) => (
                  <div
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                    className={[
                      styles.slotStripList,
                      droppableSnapshot.isDraggingOver && styles.slotStripListDraggingOver,
                    ].filter(Boolean).join(' ')}
                    // The list scrolls (overflow-y:auto); during the FLIP fly-out
                    // it must un-clip so the thumbnail can travel to the camera,
                    // matching the slot/strip overflow toggles.
                    style={flyingClip ? { overflow: 'visible' } : undefined}
                  >
                    {slots.map((slot, i) => {
                      // Selection border at all phases; pulse only while
                      // actively recording / counting down.
                      const isCurrent = i === currentSlot;
                      const isRecording = isCurrent
                        && (phase === PHASE.PREP || phase === PHASE.RECORDING);
                      const subject = getSubject(liveQuestions[i]);
                      const handleSlotKey = (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSwitchToSlot(i);
                        }
                      };
                      return (
                        <Draggable key={slot.id} draggableId={slot.id} index={i}>
                          {(draggableProvided, draggableSnapshot) => {
                            const slotNode = (
                              <div
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                                {...draggableProvided.dragHandleProps}
                                data-slot-index={i}
                                role="button"
                                tabIndex={0}
                                aria-label={`Slot ${i + 1}: ${subject}`}
                                className={[
                                  styles.slot,
                                  slot.status === SLOT_STATUS.DONE && styles.slotDone,
                                  slot.status === SLOT_STATUS.SKIPPED && styles.slotSkipped,
                                  isCurrent && styles.slotCurrent,
                                  draggableSnapshot.isDragging && styles.slotDragging,
                                ].filter(Boolean).join(' ')}
                                style={{
                                  ...draggableProvided.draggableProps.style,
                                  ...(flyingClip?.slotIndex === i ? { overflow: 'visible', zIndex: 9999 } : {}),
                                }}
                                onClick={() => handleSwitchToSlot(i)}
                                onKeyDown={handleSlotKey}
                              >
                                {slot.status === SLOT_STATUS.DONE && slot.thumbnail ? (
                                  <img
                                    src={slot.thumbnail}
                                    alt=""
                                    className={styles.slotThumb}
                                    style={flyingClip?.slotIndex === i ? {
                                      transform: flyingClip.transform,
                                      transformOrigin: 'top left',
                                      zIndex: 9999,
                                    } : undefined}
                                  />
                                ) : null}
                                <span className={styles.slotDragGrip} aria-hidden="true">⋮⋮</span>
                                <div className={styles.slotMeta}>
                                  <span className={styles.slotIndex}>{i + 1}</span>
                                  <span className={styles.slotSubject}>{subject}</span>
                                </div>
                                {slot.status === SLOT_STATUS.SKIPPED && (
                                  <span className={styles.slotBadgeSkipped}>Skipped</span>
                                )}
                                {/* Per-slot delete button — works in any phase. Click is
                                    stopped from propagating so it doesn't trigger switch. */}
                                <button
                                  type="button"
                                  className={styles.slotDeleteBtn}
                                  onClick={(e) => { e.stopPropagation(); handleDeleteSlot(i); }}
                                  onKeyDown={(e) => e.stopPropagation()}
                                  aria-label={`Delete question ${i + 1}`}
                                  title="Delete question"
                                >
                                  ✕
                                </button>
                                {isRecording && <span className={styles.slotPulse} aria-hidden="true" />}
                              </div>
                            );
                            // Portal the dragged slot to document.body so it escapes
                            // the gift-flow step container's transform-induced
                            // containing block (otherwise the chip drifts under the
                            // pointer during drag).
                            if (draggableSnapshot.isDragging) {
                              const portalTarget = getDragPortalTarget();
                              if (portalTarget) return ReactDOM.createPortal(slotNode, portalTarget);
                            }
                            return slotNode;
                          }}
                        </Draggable>
                      );
                    })}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}

          {/* + Add custom question — appears at the bottom of the slot strip
              for both sender (drag-drop list) and receiver (per-section list).
              For receiver, the new question always lands in the "Other" group
              (the recorder appends to liveQuestions, and any index beyond the
              preset count is treated as unsectioned). The button is intentionally
              visible from any section's view so the user can add a prompt
              without first switching to the Other tab. */}
          <button
            type="button"
            className={styles.addCustomBtn}
            onClick={() => {
              // Tag the new prompt with the section the user is currently
              // viewing so its tile lands in (and stays visible within) the
              // active section's list. Without this the slot defaults to
              // section:null and is filed under "Other", invisible while the
              // user is scoped to a specific section. When viewing "Other"
              // (activeGroup === null) or in the sender flow (no sections),
              // the tag stays null, which is correct.
              const activeGroup = receiverStepperData && receiverStepperData.activeGroup;
              pendingCustomSectionRef.current = activeGroup ? activeGroup.title : null;
              setCustomAddOpen(true);
            }}
            aria-label="Add a custom question"
          >
            <span aria-hidden="true">＋</span>
            <span>Add Question</span>
          </button>
        </div>

        {/* ── Camera stage — always mounted so canvas + composite stream survive REVIEW.
                Hidden via the parent's display:none during REVIEW.
                On mobile it's portaled to <body> (getStagePortalTarget) and
                styled as a parked-offscreen / fullscreen-modal element so the
                tile list can take the full width while the camera lives in a
                3D popup. Desktop renders it inline in the grid, unchanged. ── */}
        {(() => {
        const stageNode = (
        <div
          className={[
            styles.recorderStage,
            isMobile && styles.recorderStageMobile,
            isMobile && mobileSessionOpen && styles.recorderStageMobileModal,
          ].filter(Boolean).join(' ')}
          ref={stageRef}
        >
        <div className={styles.videoFrame}>
          {/* ── Add Guest button (top-right of the stage) ──────────────
              Opens a popup with a shareable link. When the guest joins
              the host's outgoing audio (mic + TTS + source video, all
              mixed) is sent to them, so they hear the question being
              read aloud — same audio the recording captures. Hidden
              once a guest is connected; we show a small "Guest" badge
              instead so the host can see the call is live. */}
          {GUEST_INVITE_ENABLED && (guestStatus === 'idle' || guestStatus === 'inviting' || guestStatus === 'connecting') && (
            <button
              type="button"
              onClick={openGuestInvite}
              disabled={guestStatus === 'inviting' || guestStatus === 'connecting'}
              aria-label="Add a guest to the recording"
              title="Add a guest"
              style={{
                position: 'absolute',
                // Desktop: tuck under the timer in the reserved right-hand
                // gutter so it never crosses the question panel (which sits
                // left:64px → right:220px). Mobile: the prompt panel drops
                // to top:60px, leaving the top-left corner free.
                top: isMobile ? '10px' : '60px',
                left: isMobile ? '10px' : 'auto',
                right: isMobile ? 'auto' : '14px',
                zIndex: 6,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px 6px 9px',
                borderRadius: '999px',
                border: '1px solid rgba(255,255,255,0.22)',
                background: 'rgba(0,0,0,0.55)',
                color: '#fff',
                fontFamily: 'inherit',
                fontSize: '0.78rem',
                fontWeight: 700,
                cursor: (guestStatus === 'inviting' || guestStatus === 'connecting') ? 'wait' : 'pointer',
                opacity: (guestStatus === 'inviting' || guestStatus === 'connecting') ? 0.7 : 1,
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <line x1="19" y1="8" x2="19" y2="14" />
                <line x1="22" y1="11" x2="16" y2="11" />
              </svg>
              <span>
                {guestStatus === 'inviting' && 'Inviting…'}
                {guestStatus === 'connecting' && 'Connecting…'}
                {guestStatus === 'idle' && 'Add guest'}
              </span>
            </button>
          )}

          {/* Guest left, but the link/room are still valid (they can rejoin).
              Offer two icon-only actions: copy the existing link (so the same
              guest can hop back in) or mint a brand-new link/room. */}
          {GUEST_INVITE_ENABLED && guestStatus === 'ended' && (
            <div
              style={{
                position: 'absolute',
                top: isMobile ? '10px' : '60px',
                left: isMobile ? '10px' : 'auto',
                right: isMobile ? 'auto' : '14px',
                zIndex: 6,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 6px',
                borderRadius: '999px',
                border: '1px solid rgba(255,255,255,0.22)',
                background: 'rgba(0,0,0,0.55)',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
              }}
            >
              <button
                type="button"
                onClick={handleCopyGuestLink}
                aria-label="Copy the existing invite link"
                title={guestLinkCopied ? 'Link copied' : 'Copy invite link'}
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '999px',
                  border: 'none',
                  background: guestLinkCopied ? 'rgba(90, 215, 122, 0.22)' : 'rgba(255,255,255,0.12)',
                  color: guestLinkCopied ? '#8fe3a3' : '#fff',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {guestLinkCopied ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                )}
              </button>
              <button
                type="button"
                onClick={openGuestInvite}
                aria-label="Create a new guest link"
                title="New guest link"
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '999px',
                  border: 'none',
                  background: 'rgba(255,255,255,0.12)',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <line x1="19" y1="8" x2="19" y2="14" />
                  <line x1="22" y1="11" x2="16" y2="11" />
                </svg>
              </button>
            </div>
          )}

          {/* Connected-guest badge — replaces the Add button so the
              host can see the call is live + has a way to drop it. */}
          {GUEST_INVITE_ENABLED && guestStatus === 'connected' && (
            <div
              style={{
                position: 'absolute',
                // Same placement logic as the Add-guest button it replaces:
                // under the timer (right gutter) on desktop, top-left on
                // mobile — keeps clear of the question panel either way.
                top: isMobile ? '10px' : '60px',
                left: isMobile ? '10px' : 'auto',
                right: isMobile ? 'auto' : '14px',
                zIndex: 6,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 10px 4px 8px',
                borderRadius: '999px',
                border: '1px solid rgba(90, 215, 122, 0.45)',
                background: 'rgba(0,0,0,0.55)',
                color: '#bce8c2',
                fontFamily: 'inherit',
                fontSize: '0.75rem',
                fontWeight: 700,
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '999px',
                  background: '#5ad77a',
                  boxShadow: '0 0 0 4px rgba(90,215,122,0.20)',
                }}
              />
              <span>{displayGuestName}</span>
              {/* Layout toggle (PIP ↔ side-by-side). The toggle text is
                  the NEXT layout to switch TO, not the current one —
                  matches the convention of file-manager "View" menus. */}
              <button
                type="button"
                onClick={() => setGuestLayout((l) => (l === 'pip' ? 'side-by-side' : 'pip'))}
                aria-label={guestLayout === 'pip' ? 'Switch to side-by-side layout' : 'Switch to picture-in-picture layout'}
                title={guestLayout === 'pip' ? 'Side by side' : 'Picture-in-picture'}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.22)',
                  borderRadius: '999px',
                  color: '#cfd1da',
                  cursor: 'pointer',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  lineHeight: 1,
                  padding: '4px 9px',
                  fontFamily: 'inherit',
                  marginLeft: '2px',
                }}
              >
                {guestLayout === 'pip' ? 'Side ⇆' : 'PIP ▢'}
              </button>
              <button
                type="button"
                onClick={tearDownGuestCall}
                aria-label="End guest call"
                title="End guest call"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#ff9b9b',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  lineHeight: 1,
                  padding: 0,
                  marginLeft: '2px',
                }}
              >
                ✕
              </button>
            </div>
          )}

          {/* Hidden draw-source video for the guest stream. The visible
              PIP overlay below is a SEPARATE element so we can hide it
              in side-by-side mode (where the guest is drawn directly
              onto the canvas) without tearing down the decoder. The
              guest stream gets a fresh srcObject binding via the
              guestStream useEffect above.

              This element is also the host's single, always-mounted
              playback source for the guest's audio — it is intentionally
              NOT muted. The visible PIP <video> below IS muted so we never
              double up. Keeping audio here (not on the PIP) means the host
              keeps hearing the guest in side-by-side mode, where the PIP
              overlay isn't rendered. */}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            ref={guestDrawVideoRef}
            autoPlay
            playsInline
            onLoadedMetadata={handleGuestVideoSize}
            onResize={handleGuestVideoSize}
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              opacity: 0,
              pointerEvents: 'none',
            }}
          />

          {/* Remote stream PIP — small overlay at the bottom-right of
              the stage so the host can see who's on the other end. Only
              rendered in PIP layout; side-by-side mode draws the guest
              directly into the canvas instead. */}
          {GUEST_INVITE_ENABLED && guestStatus === 'connected' && guestStream && guestLayout === 'pip' && (
            <div
              style={{
                position: 'absolute',
                right: '12px',
                bottom: '12px',
                zIndex: 5,
                // Flip to a portrait box when the guest is on a phone held
                // upright so their frame fills the tile instead of being
                // cropped into a landscape window.
                width: guestPortrait ? 'min(20vw, 116px)' : 'min(28vw, 180px)',
                aspectRatio: guestPortrait ? '9 / 16' : '16 / 9',
                borderRadius: '10px',
                overflow: 'hidden',
                // Primary-colour active-speaker ring when the guest talks,
                // layered over the resting drop-shadow for depth.
                border: guestSpeaking ? '2px solid #5aa5ef' : '2px solid rgba(255,255,255,0.45)',
                background: '#000',
                boxShadow: guestSpeaking
                  ? '0 0 0 1px #5aa5ef, 0 0 18px rgba(90, 165, 239, 0.55), 0 8px 22px rgba(0,0,0,0.55)'
                  : '0 8px 22px rgba(0,0,0,0.55)',
                transition: 'box-shadow 0.12s ease, border-color 0.12s ease',
              }}
            >
              {/* Muted on purpose — guest audio plays through the always-
                  mounted hidden draw-source <video> above so it survives a
                  switch to side-by-side. Unmuting here would double it up. */}
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video
                ref={attachGuestVideo}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  visibility: guestCamOff ? 'hidden' : 'visible',
                }}
              />
              {/* Guest camera off — letter avatar + name (no camera icon). */}
              {guestCamOff && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    background: '#14161d',
                    color: '#e6e7ea',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '999px',
                      background: 'linear-gradient(135deg, #5aa5ef, #3f78c9)',
                      color: '#fff',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.3rem',
                      fontWeight: 800,
                      textTransform: 'uppercase',
                    }}
                  >
                    {displayGuestName.trim().charAt(0) || '?'}
                  </span>
                  <span style={{ fontSize: '0.62rem', fontWeight: 700 }}>{displayGuestName}</span>
                </div>
              )}
              {/* Guest muted — mic-off badge in the top-left corner (Zoom
                  convention; the "Guest" label sits bottom-left). */}
              {guestMuted && (
                <span
                  aria-label="Guest muted"
                  title="Guest muted"
                  style={{
                    position: 'absolute',
                    top: '5px',
                    left: '5px',
                    width: '22px',
                    height: '22px',
                    borderRadius: '999px',
                    background: 'rgba(255, 77, 79, 0.92)',
                    color: '#fff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="1" y1="1" x2="23" y2="23" />
                    <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                    <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
                    <line x1="12" y1="19" x2="12" y2="23" />
                  </svg>
                </span>
              )}
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  bottom: '4px',
                  left: '6px',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  color: '#fff',
                  textShadow: '0 1px 2px rgba(0,0,0,0.7)',
                }}
              >
                {displayGuestName}
              </span>
            </div>
          )}

          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            key="live"
            ref={setLiveVideoNode}
            className={styles.videoStream}
            autoPlay
            playsInline
            muted
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, pointerEvents: 'none',
            }}
          />
          <canvas
            key="record-canvas"
            ref={canvasRef}
            className={styles.videoStream}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              // Make room for the controller bar at the bottom when commentary
              // is active. We have to set `height` EXPLICITLY here because
              // the .videoStream class sets `height: 100%` — for an
              // absolutely-positioned element with `top`, `height`, AND
              // `bottom` set, the spec ignores `bottom`, which made the
              // canvas keep its full-height pixel mapping while the HTML
              // overlay container correctly shrank by 64px → ~42px vertical
              // misalignment between painted PIP and overlay.
              height: isCommentaryActive ? 'calc(100% - 64px)' : '100%',
              bottom: isCommentaryActive ? '64px' : 0,
              width: '100%',
              // object-fit choice depends on mode:
              //  • Commentary → `fill`. The canvas pixel buffer is stretched
              //    to the CSS box without crop or letterbox so the HTML PIP
              //    overlay's fractional positioning aligns 1:1 with the
              //    painted PIP. (The captured stream is unaffected — it uses
              //    the unstretched pixel buffer directly.)
              //  • Talking-head (default) → `cover`. The canvas buffer is the
              //    camera's NATIVE aspect (square/portrait on mobile, 16:9 on
              //    desktop). `fill` would stretch that buffer to whatever the
              //    box shape is, squishing faces/hands ("pencil") when the
              //    box is portrait but the buffer isn't. `cover` keeps the
              //    true proportions and fully covers the container, cropping
              //    the overflow — no distortion, no black bars. On desktop
              //    the buffer already matches the box so nothing is cropped.
              objectFit: isCommentaryActive ? 'fill' : 'cover',
              pointerEvents: 'none',
            }}
          />

          {/* Host active-speaker ring. The host's camera fills the whole
              stage (the canvas), so the ring is a full-frame overlay drawn
              ON TOP of the canvas (an inset box-shadow on videoFrame would
              sit behind it). Only in PIP mode — in side-by-side both faces
              share the canvas and a full-frame ring would be ambiguous. */}
          {GUEST_INVITE_ENABLED && guestStatus === 'connected' && guestLayout === 'pip' && hostSpeaking && (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 4,
                pointerEvents: 'none',
                border: '3px solid #5aa5ef',
                boxShadow: 'inset 0 0 22px rgba(90, 165, 239, 0.50)',
                transition: 'box-shadow 0.12s ease',
              }}
            />
          )}

          {/* Host's OWN camera-off placeholder. The host fills the canvas,
              so this is a full-frame overlay (PIP mode only — in side-by-side
              it would cover the guest half too). Shown to the host so they
              see their own state; the guest sees it via signalled media-state. */}
          {GUEST_INVITE_ENABLED && guestStatus === 'connected' && guestLayout === 'pip' && hostCamOff && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                background: '#14161d',
                color: '#e6e7ea',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 'clamp(64px, 16%, 110px)',
                  aspectRatio: '1 / 1',
                  borderRadius: '999px',
                  background: 'linear-gradient(135deg, #5aa5ef, #3f78c9)',
                  color: '#fff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(1.8rem, 5vw, 3rem)',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                }}
              >
                {hostName.trim().charAt(0) || '?'}
              </span>
              <span style={{ fontSize: '1rem', fontWeight: 700 }}>{hostName}</span>
            </div>
          )}

          {/* Host's OWN mic-off badge, top-left (Zoom convention). Small, so
              it shows in any layout — top-left maps to the host's region. */}
          {GUEST_INVITE_ENABLED && guestStatus === 'connected' && hostMicMuted && (
            <span
              aria-label="You are muted"
              title="You are muted"
              style={{
                position: 'absolute',
                // On mobile the "Guest live" badge sits top-left, so drop the
                // mute badge just below it; on desktop top-left is clear.
                top: isMobile ? '50px' : '12px',
                left: '12px',
                zIndex: 5,
                width: '30px',
                height: '30px',
                borderRadius: '999px',
                background: 'rgba(255, 77, 79, 0.92)',
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.45)',
              }}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="1" y1="1" x2="23" y2="23" />
                <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
                <line x1="12" y1="19" x2="12" y2="23" />
              </svg>
            </span>
          )}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            ref={splashVideoRef}
            src={splashVideoSrc}
            muted
            playsInline
            preload="auto"
            style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
          />
          {/* ── Commentary mode: PIP overlay + media controls ──
              These sit inside videoFrame (so they line up with the canvas)
              but OUTSIDE the canvas element itself, so canvas.captureStream
              doesn't include them in the recording.
              All inline-styled — no CSS-module additions needed. */}
          {isCommentaryActive && (() => {
            const commentary = getCurrentCommentary();
            const mediaList = (commentary && commentary.mediaList) || [];
            const idx = Math.min(commentaryMediaIndex, Math.max(0, mediaList.length - 1));
            const item = mediaList[idx];
            const cfg = pipConfigRef.current;
            const isPreRecord = phase === PHASE.IDLE;
            const isVideoItem = item && item.type === 'video';
            const timeLabel = (s) => {
              const v = Math.max(0, Math.floor(s || 0));
              const m = Math.floor(v / 60);
              const sec = v % 60;
              return `${m}:${String(sec).padStart(2, '0')}`;
            };
            const ctrlBtn = (extra) => ({
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '34px',
              height: '34px',
              padding: '0 10px',
              borderRadius: '8px',
              border: 'none',
              background: 'rgba(255,255,255,0.10)',
              color: '#fff',
              fontFamily: 'inherit',
              fontSize: '0.92rem',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'background 150ms ease',
              // Keep the glyphs (e.g. "|◀") on one line. In the narrow mobile
              // control row the button was being shrunk to its min-width and the
              // two glyphs wrapped — the "|" landing above the arrow. nowrap +
              // no-shrink + line-height:1 keep every control button on one line.
              whiteSpace: 'nowrap',
              lineHeight: 1,
              flexShrink: 0,
              ...(extra || {}),
            });
            return (
              <>
                {/* Canvas-aligned overlay layer: matches the canvas's visible
                    area so the PIP overlay, remove chip, etc. stay perfectly
                    in register with the painted PIP regardless of the
                    controller bar below. */}
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: '64px',
                    pointerEvents: 'none',
                    zIndex: 2,
                  }}
                >
                  {/* Remove-media chip + count — top-right of canvas area */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 10px 6px 12px',
                      borderRadius: '999px',
                      background: 'rgba(8, 6, 18, 0.72)',
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255,255,255,0.10)',
                      color: '#fff',
                      fontFamily: 'inherit',
                      fontSize: '0.78rem',
                      pointerEvents: 'auto',
                      zIndex: 4,
                      boxShadow: '0 8px 22px rgba(0,0,0,0.4)',
                    }}
                  >
                    <span style={{ color: '#cfd1da', fontWeight: 700 }}>
                      Clear media
                    </span>
                    <button
                      type="button"
                      onClick={handleRemoveCommentary}
                      disabled={phase !== PHASE.IDLE}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '22px',
                        height: '22px',
                        borderRadius: '999px',
                        border: '1px solid rgba(255,255,255,0.18)',
                        background: 'rgba(255,255,255,0.06)',
                        color: '#fff',
                        cursor: phase === PHASE.IDLE ? 'pointer' : 'not-allowed',
                        opacity: phase === PHASE.IDLE ? 1 : 0.5,
                        fontSize: '0.8rem',
                        fontWeight: 800,
                        lineHeight: 1,
                      }}
                      aria-label="Remove commentary media"
                      title="Remove media (return to talking-head mode)"
                    >
                      ✕
                    </button>
                  </div>

                  {/* PIP overlay — pre-record only. Lives inside the canvas-
                      aligned layer so its % positioning matches the painted
                      PIP exactly. */}
                  {isPreRecord && (
                    <div
                      role="button"
                      tabIndex={-1}
                      aria-label="Picture-in-picture frame (drag to move, drag corner to resize)"
                      onKeyDown={() => { /* drag-only — no keyboard interaction */ }}
                      onPointerDown={(e) => {
                        if (e.target !== e.currentTarget) return;
                        const frame = e.currentTarget.parentElement;
                        const rect = frame.getBoundingClientRect();
                        const startX = e.clientX;
                        const startY = e.clientY;
                        const startCfg = { ...pipConfigRef.current };
                        const onMove = (ev) => {
                          const dx = (ev.clientX - startX) / rect.width;
                          const dy = (ev.clientY - startY) / rect.height;
                          setPipConfig({
                            x: Math.max(0, Math.min(1 - startCfg.w, startCfg.x + dx)),
                            y: Math.max(0, Math.min(1 - startCfg.h, startCfg.y + dy)),
                          });
                        };
                        const onUp = () => {
                          window.removeEventListener('pointermove', onMove);
                          window.removeEventListener('pointerup', onUp);
                          window.removeEventListener('pointercancel', onUp);
                        };
                        window.addEventListener('pointermove', onMove);
                        window.addEventListener('pointerup', onUp);
                        window.addEventListener('pointercancel', onUp);
                      }}
                      style={{
                        position: 'absolute',
                        left: `${cfg.x * 100}%`,
                        top: `${cfg.y * 100}%`,
                        width: `${cfg.w * 100}%`,
                        height: `${cfg.h * 100}%`,
                        boxSizing: 'border-box',
                        cursor: 'move',
                        border: '2px dashed rgba(255, 209, 102, 0.85)',
                        borderRadius: cfg.shape === 'circle' ? '50%' : (cfg.shape === 'rect' ? '0' : '16px'),
                        pointerEvents: 'auto',
                        // Stop the browser from treating the touch as a scroll/
                        // pan gesture so the PIP can be dragged on touchscreens.
                        touchAction: 'none',
                        zIndex: 3,
                      }}
                      title="Drag to reposition. Drag corner to resize."
                    >
                      <button
                        type="button"
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          const frame = e.currentTarget.parentElement.parentElement;
                          const rect = frame.getBoundingClientRect();
                          const startX = e.clientX;
                          const startY = e.clientY;
                          const startCfg = { ...pipConfigRef.current };
                          const onMove = (ev) => {
                            const dw = (ev.clientX - startX) / rect.width;
                            const dh = (ev.clientY - startY) / rect.height;
                            const w2 = Math.max(0.1, Math.min(0.6, startCfg.w + dw));
                            const h2 = Math.max(0.12, Math.min(0.7, startCfg.h + dh));
                            setPipConfig({
                              w: Math.min(w2, 1 - startCfg.x),
                              h: Math.min(h2, 1 - startCfg.y),
                            });
                          };
                          const onUp = () => {
                            window.removeEventListener('pointermove', onMove);
                            window.removeEventListener('pointerup', onUp);
                            window.removeEventListener('pointercancel', onUp);
                          };
                          window.addEventListener('pointermove', onMove);
                          window.addEventListener('pointerup', onUp);
                          window.addEventListener('pointercancel', onUp);
                        }}
                        style={{
                          position: 'absolute',
                          right: '-9px',
                          bottom: '-9px',
                          width: '18px',
                          height: '18px',
                          padding: 0,
                          borderRadius: '999px',
                          background: '#5aa5ef',
                          border: '2px solid #0a1322',
                          cursor: 'nwse-resize',
                          boxShadow: '0 4px 10px rgba(0,0,0,0.4)',
                          touchAction: 'none',
                        }}
                        aria-label="Resize PIP"
                      />
                      <div
                        style={{
                          position: 'absolute',
                          left: '50%',
                          top: '-44px',
                          transform: 'translateX(-50%)',
                          display: 'inline-flex',
                          gap: '4px',
                          padding: '4px',
                          borderRadius: '999px',
                          background: 'rgba(8, 6, 18, 0.72)',
                          backdropFilter: 'blur(8px)',
                          WebkitBackdropFilter: 'blur(8px)',
                          border: '1px solid rgba(255,255,255,0.10)',
                          boxShadow: '0 8px 22px rgba(0,0,0,0.4)',
                        }}
                      >
                        {[
                          { key: 'circle', icon: '◯' },
                          { key: 'squircle', icon: '▢' },
                          { key: 'rect', icon: '▭' },
                        ].map((opt) => {
                          const active = (cfg.shape || 'squircle') === opt.key;
                          return (
                            <button
                              type="button"
                              key={opt.key}
                              onClick={() => setPipConfig({ shape: opt.key })}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '28px',
                                height: '28px',
                                padding: '0 8px',
                                borderRadius: '999px',
                                border: 'none',
                                background: active
                                  ? '#5aa5ef'
                                  : 'transparent',
                                color: active ? '#fff' : '#cfd1da',
                                fontFamily: 'inherit',
                                fontSize: '0.85rem',
                                fontWeight: 800,
                                cursor: 'pointer',
                              }}
                              aria-label={`PIP shape: ${opt.key}`}
                              title={`Shape: ${opt.key}`}
                            >
                              {opt.icon}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Controller bar — sits below the (shortened) canvas in the
                    bottom 64px of the videoFrame. Pure HTML so it's never
                    captured into the recording. Layout matches the user's
                    spec:  |<  >|   [seekbar]   12:00/24:32   🔊   3/6  */}
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: '64px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '0 14px',
                    background: 'rgba(10, 8, 20, 0.85)',
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                    color: '#fff',
                    fontFamily: 'inherit',
                    pointerEvents: 'auto',
                    zIndex: 5,
                    boxSizing: 'border-box',
                  }}
                >
                  <button
                    type="button"
                    onClick={handleCommentaryPrev}
                    disabled={idx <= 0}
                    style={ctrlBtn({
                      opacity: idx <= 0 ? 0.4 : 1,
                      cursor: idx <= 0 ? 'not-allowed' : 'pointer',
                    })}
                    aria-label="Previous media"
                    title="Previous (←)"
                  >
                    |◀
                  </button>
                  <button
                    type="button"
                    onClick={handleCommentaryNext}
                    disabled={idx >= mediaList.length - 1}
                    style={ctrlBtn({
                      opacity: idx >= mediaList.length - 1 ? 0.4 : 1,
                      cursor: idx >= mediaList.length - 1 ? 'not-allowed' : 'pointer',
                    })}
                    aria-label="Next media"
                    title="Next (→)"
                  >
                    ▶|
                  </button>

                  {/* Play / Pause — videos only. Greyed out for photos so the
                      controller layout stays consistent across media types. */}
                  <button
                    type="button"
                    onClick={handleCommentaryTogglePlay}
                    disabled={!isVideoItem}
                    style={ctrlBtn({
                      opacity: isVideoItem ? 1 : 0.4,
                      cursor: isVideoItem ? 'pointer' : 'not-allowed',
                      background: isVideoItem ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.10)',
                    })}
                    aria-label={commentaryIsPlaying ? 'Pause' : 'Play'}
                    title={isVideoItem
                      ? (commentaryIsPlaying ? 'Pause (K)' : 'Play (K)')
                      : 'Play (videos only)'}
                  >
                    {commentaryIsPlaying ? '❚❚' : '▶'}
                  </button>

                  {/* Seek bar — only for video items. For photos we render a
                      placeholder track at the same width so the layout
                      doesn't jump when navigating between media types. */}
                  {isVideoItem ? (
                    <input
                      type="range"
                      min={0}
                      max={Math.max(0.01, commentaryVideoDuration)}
                      step={0.05}
                      value={Math.min(commentaryVideoTime, commentaryVideoDuration || 0)}
                      onChange={(e) => {
                        const vid = commentaryVideoElRef.current;
                        if (!vid) return;
                        try {
                          vid.currentTime = parseFloat(e.target.value);
                          setCommentaryVideoTime(parseFloat(e.target.value));
                        } catch (err) { /* ignore */ }
                      }}
                      aria-label="Seek video"
                      style={{
                        flex: 1,
                        height: '6px',
                        accentColor: '#5aa5ef',
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        flex: 1,
                        height: '6px',
                        borderRadius: '999px',
                        background: 'rgba(255,255,255,0.06)',
                        opacity: 0.6,
                      }}
                      aria-hidden="true"
                    />
                  )}

                  {/* Time readout — videos only. Photos get an inert dash. */}
                  <span style={{
                    minWidth: '90px',
                    textAlign: 'center',
                    fontSize: '0.82rem',
                    fontVariantNumeric: 'tabular-nums',
                    color: isVideoItem ? '#fff' : 'rgba(255,255,255,0.4)',
                    fontWeight: 700,
                  }}
                  >
                    {isVideoItem
                      ? `${timeLabel(commentaryVideoTime)} / ${timeLabel(commentaryVideoDuration)}`
                      : '—'}
                  </span>

                  {/* Mute toggle — videos only. Toggles SourceGain between
                      0 (muted) and 0.2 (ducked) via the audio-graph effect. */}
                  <button
                    type="button"
                    onClick={() => setCommentarySourceMuted((m) => !m)}
                    disabled={!isVideoItem}
                    style={ctrlBtn({
                      background: commentarySourceMuted ? 'rgba(214, 67, 95, 0.28)' : 'rgba(255,255,255,0.10)',
                      opacity: isVideoItem ? 1 : 0.4,
                      cursor: isVideoItem ? 'pointer' : 'not-allowed',
                    })}
                    aria-label={commentarySourceMuted ? 'Unmute source audio' : 'Mute source audio'}
                    title={
                      isVideoItem
                        ? (commentarySourceMuted ? 'Source audio muted — click to include' : 'Source audio mixed in — click to mute')
                        : 'Mute (videos only)'
                    }
                  >
                    {commentarySourceMuted ? '🔇' : '🔊'}
                  </button>

                  {/* Counter — always visible. */}
                  <span style={{
                    minWidth: '46px',
                    textAlign: 'center',
                    fontSize: '0.85rem',
                    fontWeight: 800,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                  >
                    {`${idx + 1} / ${mediaList.length}`}
                  </span>
                </div>
              </>
            );
          })()}
        </div>

        {permissionState === 'pending' && (
          <div className={styles.cameraOverlay}>
            <div className={styles.spinner} />
            <p>Waking up your camera…</p>
          </div>
        )}

        {/* Top prompt panel — current question */}
        <div className={styles.stagePromptPanel}>
          <div className={styles.stageMetaBadge}>
            <span className={styles.stageMetaCount}>
              {(() => {
                // Receiver flow: counter is section-relative so the user can
                // see when one section ends and the next begins. Counts are
                // derived from the slot.section bucket (not original section
                // lengths), so they update correctly after slot deletion.
                if (Array.isArray(sections)) {
                  const currentSlotObj = slots[currentSlot];
                  const tag = currentSlotObj && currentSlotObj.section;
                  if (tag) {
                    const sectionItems = slots
                      .map((s, idx) => ({ s, idx }))
                      .filter(({ s }) => s.section === tag);
                    const slotInSection = sectionItems.findIndex(({ idx }) => idx === currentSlot) + 1;
                    return `${tag} · Question ${slotInSection} of ${sectionItems.length}`;
                  }
                  // Custom-added slot — index within the unsectioned group.
                  const customItems = slots
                    .map((s, idx) => ({ s, idx }))
                    .filter(({ s }) => !s.section);
                  const customIdx = customItems.findIndex(({ idx }) => idx === currentSlot) + 1;
                  return `Your own question · ${customIdx}`;
                }
                // Sender flow — unchanged.
                return `Question ${currentSlot + 1} of ${totalQuestions}`;
              })()}
            </span>
          </div>
          <p
            ref={promptQuestionRef}
            className={`${styles.stageQuestionText} ${
              isReadingAloud ? styles.stageQuestionTextReading : ''
            }`}
            style={{
              // Hidden while the centered overlay is reading ('in'); fades in
              // during the fly-to-place ('out') so it lands exactly where the
              // flying copy arrives. Layout box stays intact (opacity only) so
              // the FLIP can still measure this slot as its target.
              opacity: readingStage === 'in' ? 0 : 1,
              // Delayed so it only surfaces in the flight's final leg, meeting
              // the gliding copy right as it lands → one continuous question.
              transition: readingStage === 'out'
                ? 'opacity 320ms ease 440ms'
                : 'opacity 200ms ease',
            }}
          >
            {(() => {
              const text = getQuestion(liveQuestions[currentSlot]) || '';
              // Only highlight when the active schedule was built for this
              // exact text — guards against a stale schedule lingering on
              // an old slot's text after a slot switch.
              const canHighlight = text && ttsHighlightTextRef.current === text
                && ttsHighlightIndex >= 0;
              // Split keeping the whitespace runs so spacing stays intact.
              const parts = text.split(/(\s+)/);
              let wordIdx = -1;
              return parts.map((seg, i) => {
                if (!seg) return null;
                if (/^\s+$/.test(seg)) {
                  // eslint-disable-next-line react/no-array-index-key
                  return <React.Fragment key={`s-${i}`}>{seg}</React.Fragment>;
                }
                wordIdx += 1;
                const isActive = canHighlight && wordIdx === ttsHighlightIndex;
                return (
                  <span
                    // eslint-disable-next-line react/no-array-index-key
                    key={`w-${i}`}
                    className={isActive ? styles.stageWordActive : undefined}
                  >
                    {seg}
                  </span>
                );
              });
            })()}
          </p>
        </div>

        {/* Prep countdown overlay */}
        {phase === PHASE.PREP && prepCountdown > 0 && (
          <div className={styles.countdownOverlay}>
            <span className={styles.countdownLabel}>We are reading out the question out loud</span>
            <span className={styles.countdownNumber}>{prepCountdown}</span>
          </div>
        )}

        {/* Reading-aloud overlay — dimmed glass over the whole stage with the
            question centered while it's read out, then it flies up into the
            top prompt panel as the glass dissolves (FLIP handled in JS). */}
        {readingStage !== 'hidden' && (
          <div
            className={[
              styles.readingOverlay,
              readingStage === 'out' && styles.readingOverlayExiting,
            ].filter(Boolean).join(' ')}
            aria-hidden="true"
          >
            {/* Glass is its own layer so it can clear quickly on exit while the
                question keeps gliding to its slot on a separate, longer curve. */}
            <div className={styles.readingGlass} />
            <div ref={readingContentRef} className={styles.readingContent}>
              <span className={styles.readingPill}>
                <span className={styles.readingPillDot} aria-hidden="true" />
                Listen to the question
              </span>
              <p ref={readingQuestionRef} className={styles.readingQuestion}>
                {(() => {
                  // Same karaoke word-highlight the resting top question uses,
                  // driven by ttsHighlightIndex — now surfaced on the centered
                  // overlay so the blue chip tracks the voice while reading.
                  const text = getQuestion(liveQuestions[currentSlot]) || '';
                  const canHighlight = text && ttsHighlightTextRef.current === text
                    && ttsHighlightIndex >= 0;
                  const parts = text.split(/(\s+)/);
                  let wordIdx = -1;
                  return parts.map((seg, i) => {
                    if (!seg) return null;
                    if (/^\s+$/.test(seg)) {
                      // eslint-disable-next-line react/no-array-index-key
                      return <React.Fragment key={`rs-${i}`}>{seg}</React.Fragment>;
                    }
                    wordIdx += 1;
                    const isActive = canHighlight && wordIdx === ttsHighlightIndex;
                    return (
                      <span
                        // eslint-disable-next-line react/no-array-index-key
                        key={`rw-${i}`}
                        className={isActive ? styles.stageWordActive : undefined}
                      >
                        {seg}
                      </span>
                    );
                  });
                })()}
              </p>
            </div>
          </div>
        )}

        {/* Big elapsed-time timer (top-right of stage). Always mounted so
            the stage layout doesn't shift when recording begins; it just
            switches from a neutral "READY" state to the live red
            REC / PAUSED state. The Add-guest controls live top-LEFT so the
            two never overlap. */}
        {(() => {
          const isRec = phase === PHASE.RECORDING;
          const label = isRec ? (recordingPaused ? 'PAUSED' : 'REC') : 'READY';
          return (
            <div className={[styles.stageTimer, !isRec && styles.stageTimerIdle].filter(Boolean).join(' ')}>
              <span className={[styles.stageTimerDot, !isRec && styles.stageTimerDotIdle].filter(Boolean).join(' ')} />
              <span className={styles.stageTimerLabel}>{label}</span>
              <span className={styles.stageTimerElapsed}>{formatTime(isRec ? elapsedSeconds : 0)}</span>
              <span className={styles.stageTimerCap}>{`/ ${formatTime(durationCap)}`}</span>
            </div>
          );
        })()}

        {/* Back lives at the host/page level (see PersonalVideoMessageFlow)
            so it reads as "leave this page" rather than an in-recorder
            control. Intentionally not rendered here. */}

        {/* Mobile-camera status badge (top-center) */}
        {cameraSource === 'mobile' && (
          <div className={styles.mobileCamBadge}>
            <span className={styles.mobileCamDot} aria-hidden="true" />
            {mcSession.status === MC_STATUS.CONNECTED
              ? 'Mobile Connected'
              : mcSession.status === MC_STATUS.RECONNECTING
                ? 'Reconnecting…'
                : mcSession.status === MC_STATUS.CONNECTING
                  ? 'Connecting…'
                  : 'Waiting for phone…'}
          </div>
        )}

        {/* Auto-stop warning + extend buttons */}
        {showWarning && (
          <div className={styles.warningBar}>
            <span className={styles.warningText}>
              {'Auto-advance in '}
              <strong>{`${remainingSeconds}s`}</strong>
              {' · need more time?'}
            </span>
            <div className={styles.warningActions}>
              <button type="button" className={styles.extendBtn} onClick={() => handleExtend(15)}>+15s</button>
              <button type="button" className={styles.extendBtn} onClick={() => handleExtend(30)}>+30s</button>
              <button type="button" className={styles.extendBtn} onClick={() => handleExtend(60)}>+1m</button>
            </div>
          </div>
        )}

        {/* In-canvas control bar — Start / Skip / Re-record / Finish overlay at bottom of stage.
            Pushed up by the controller bar's height when commentary is
            active so the two strips don't overlap. */}
        <div
          className={styles.stageControlBar}
          style={isCommentaryActive ? { bottom: 'calc(18px + 64px)' } : undefined}
        >
          {/* Host self-controls — only during a connected guest call. Mute /
              camera-off flip the host's own capture (so the guest + the
              recording follow). Placed first so they sit at the bar's left. */}
          {GUEST_INVITE_ENABLED && guestStatus === 'connected' && (
            <>
              <button
                type="button"
                onClick={toggleHostMic}
                aria-label={hostMicMuted ? 'Unmute your microphone' : 'Mute your microphone'}
                title={hostMicMuted ? 'Unmute' : 'Mute'}
                style={hostCallControlBtn(hostMicMuted)}
              >
                {hostMicMuted ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="1" y1="1" x2="23" y2="23" />
                    <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                    <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
                    <line x1="12" y1="19" x2="12" y2="23" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="23" />
                  </svg>
                )}
              </button>
              <button
                type="button"
                onClick={toggleHostCam}
                aria-label={hostCamOff ? 'Turn your camera on' : 'Turn your camera off'}
                title={hostCamOff ? 'Camera on' : 'Camera off'}
                style={hostCallControlBtn(hostCamOff)}
              >
                {hostCamOff ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M23 7l-7 5 7 5V7z" />
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                  </svg>
                )}
              </button>
            </>
          )}
          {phase === PHASE.IDLE && (
            <>
              <button
                type="button"
                className={styles.stagePrimaryButton}
                onClick={isMobile ? handleMobileStartRecording : handleStartFlow}
                disabled={permissionState !== 'granted'}
              >
                <span className={styles.btnLabelFull}>Start Recording</span>
                <span className={styles.btnLabelShort}>Start</span>
              </button>
              <button
                type="button"
                className={styles.stageSecondaryButton}
                onClick={isMobile ? handleMobileSkip : handleSkipFromIdle}
                disabled={permissionState !== 'granted' || slots.every((s) => s.status !== SLOT_STATUS.EMPTY)}
              >
                <span className={styles.btnLabelFull}>Skip Question</span>
                <span className={styles.btnLabelShort}>Skip</span>
              </button>
              {/* "Ask question aloud" toggle — hidden for now per product
                  call but feature stays wired up (default ON via askAloud
                  state, so the question still plays through the speakers
                  when recording starts). Uncomment to expose the toggle
                  again.
              <label
                title="Read the question aloud through the speakers when recording starts"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 14px',
                  borderRadius: '999px',
                  border: '1px solid rgba(255,255,255,0.18)',
                  background: askAloud ? 'rgba(90,165,239,0.16)' : 'rgba(0,0,0,0.35)',
                  color: '#fff',
                  fontFamily: 'inherit',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  lineHeight: 1,
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <input
                  type="checkbox"
                  checked={askAloud}
                  onChange={(e) => setAskAloud(e.target.checked)}
                  style={{
                    width: '15px',
                    height: '15px',
                    accentColor: '#5aa5ef',
                    cursor: 'pointer',
                    margin: 0,
                  }}
                />
                <span aria-hidden="true">🔊</span>
                <span>Ask question aloud</span>
              </label>
              */}
              {cameraSource === 'desktop' ? (
                <button
                  type="button"
                  className={`${styles.stageSecondaryButton} ${styles.cameraSourceToggle}`}
                  onClick={handlePickMobileCamera}
                  aria-label="Use phone as camera"
                >
                  📱 Use Phone Camera
                </button>
              ) : (
                <button
                  type="button"
                  className={`${styles.stageSecondaryButton} ${styles.cameraSourceToggle}`}
                  onClick={handlePickDesktopCamera}
                  aria-label="Use desktop camera"
                >
                  💻 Use Desktop Camera
                </button>
              )}
              {/* Commentary mode trigger — picking media here flips this
                  slot into picture-in-picture commentary layout. If the
                  slot already has media, the button label switches to
                  "Replace media" so the user knows they'll overwrite.
                  Shown in BOTH sender (/gift) and receiver (/gift-content)
                  flows, but only when VIDEO_RECORDER_COMMENTARY_ENABLED
                  is true — single source of truth for hiding commentary. */}
              {VIDEO_RECORDER_COMMENTARY_ENABLED && (
                <button
                  type="button"
                  className={styles.stageSecondaryButton}
                  onClick={handleOpenAddMedia}
                  disabled={permissionState !== 'granted'}
                  aria-label={isCommentaryActive ? 'Replace commentary media' : 'Add media for commentary'}
                >
                  <span className={styles.btnLabelFull}>
                    {isCommentaryActive ? '🎬 Replace Files' : '🎬 Add Files'}
                  </span>
                  <span className={styles.btnLabelShort}>
                    {isCommentaryActive ? 'Replace' : 'Media'}
                  </span>
                </button>
              )}
            </>
          )}
          {(phase === PHASE.PREP || phase === PHASE.RECORDING) && (
            <>
              <button type="button" className={styles.stageSecondaryButton} onClick={handleSkipCurrent}>
                <span className={styles.btnLabelFull}>Skip Question</span>
                <span className={styles.btnLabelShort}>Skip</span>
              </button>
              <button type="button" className={styles.stageSecondaryButton} onClick={handleRerecord}>
                <span className={styles.btnLabelFull}>Re-record</span>
                <span className={styles.btnLabelShort}>Redo</span>
              </button>
              {phase === PHASE.RECORDING && (
                <button
                  type="button"
                  className={styles.stageSecondaryButton}
                  onClick={handlePauseResumeRecording}
                >
                  <span className={styles.btnLabelFull}>{recordingPaused ? 'Resume' : 'Pause'}</span>
                  <span className={styles.btnLabelShort}>{recordingPaused ? '▶' : '❚❚'}</span>
                </button>
              )}
              {phase === PHASE.RECORDING && (
                <button
                  type="button"
                  className={styles.stageSecondaryButton}
                  onClick={handleResetToIdle}
                >
                  <span className={styles.btnLabelFull}>Cancel</span>
                  <span className={styles.btnLabelShort}>✕</span>
                </button>
              )}
              {phase === PHASE.RECORDING ? (
                <button type="button" className={styles.stageStopButton} onClick={handleFinishRecording}>
                  <span className={styles.stopIcon} />
                  Finish
                </button>
              ) : (
                <button type="button" className={styles.stagePrimaryButton} disabled>
                  Starting…
                </button>
              )}
            </>
          )}
        </div>
        </div>
        );
        // On mobile, portal the stage to <body> so its fullscreen-modal /
        // parked-offscreen positioning escapes any transformed ancestor.
        // The portal target is stable for the whole session, so React keeps
        // the same canvas/video DOM nodes alive (camera stream never tears
        // down on open/close). Desktop renders the stage inline, untouched.
        return isMobile
          ? (getStagePortalTarget()
            ? ReactDOM.createPortal(stageNode, getStagePortalTarget())
            : stageNode)
          : stageNode;
        })()}
      </div>

      {/* Receiver flow: "Do you have any questions to add?" prompt that
          fronts the review grid. Rendered when the recorder enters REVIEW
          with `autoAskMoreAfterPreset` and reasserted after each custom
          prompt is recorded, so the user can add 0..N extras before reaching
          the actual review screen. */}
      {phase === PHASE.REVIEW && receiverAskMore && Array.isArray(sections) && (
        <div
          aria-label="Add another question"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px 24px',
            color: '#fff',
          }}
        >
          <div style={{ width: '100%', maxWidth: '560px', textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', lineHeight: 1, marginBottom: '14px' }} aria-hidden="true">💭</div>
            <h2 style={{
              margin: 0, fontSize: '1.6rem', fontWeight: 800, letterSpacing: '-0.01em',
            }}
            >
              Have anything else you&apos;d like to share?
            </h2>
            <p style={{
              margin: '12px auto 24px auto',
              maxWidth: '460px',
              color: '#b9bdc7',
              fontSize: '1rem',
              lineHeight: 1.55,
            }}
            >
              Add your own question and answer it. You can keep adding as many as
              you like before wrapping up.
            </p>
            <div style={{
              display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap',
            }}
            >
              <button
                type="button"
                onClick={() => { setReceiverAskMore(false); }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '10px 22px',
                  minHeight: '42px',
                  lineHeight: 1,
                  borderRadius: '999px',
                  border: '1px solid rgba(255,255,255,0.18)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#cfd1da',
                  fontFamily: 'inherit',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                No, I&apos;m done
              </button>
              <button
                type="button"
                onClick={() => {
                  setReceiverAskMore(false);
                  setAddAndRecord(true);
                  setCustomAddOpen(true);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '10px 26px',
                  minHeight: '42px',
                  lineHeight: 1,
                  borderRadius: '999px',
                  border: 'none',
                  background: '#5aa5ef',
                  color: '#fff',
                  fontFamily: 'inherit',
                  fontSize: '0.95rem',
                  fontWeight: 800,
                  cursor: 'pointer',
                  boxShadow: '0 8px 22px rgba(214, 67, 95, 0.4)',
                }}
              >
                Yes, add a question
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── REVIEW phase: list of recorded clips with inline players ── */}
      {phase === PHASE.REVIEW && !(receiverAskMore && Array.isArray(sections)) && (() => {
        // Renderer for a single review card. Extracted so the flat (sender)
        // and sectioned (receiver) views can share identical card markup.
        const renderReviewCard = (slot, i) => {
          const subject = getSubject(liveQuestions[i]);
          const isPlaying = playingSlot === i;
          return (
            <div
              key={`review-${slot.id}`}
              className={[
                styles.reviewCard,
                slot.status === SLOT_STATUS.DONE && styles.reviewCardDone,
                slot.status === SLOT_STATUS.SKIPPED && styles.reviewCardSkipped,
              ].filter(Boolean).join(' ')}
              style={{ position: 'relative' }}
            >
              <div className={styles.reviewCardHeader}>
                <span className={styles.reviewCardIndex}>{i + 1}</span>
                <span className={styles.reviewCardSubject}>{subject}</span>
              </div>
              <div className={styles.reviewCardMedia}>
                {slot.status === SLOT_STATUS.DONE && slot.url ? (
                  <NetflixPlayer
                    src={slot.url}
                    poster={slot.thumbnail || undefined}
                    fillHeight
                    onPlay={() => setPlayingSlot(i)}
                    onPause={() => { if (isPlaying) setPlayingSlot(-1); }}
                  />
                ) : (
                  <div className={styles.reviewSkippedBox}>
                    <span>{slot.status === SLOT_STATUS.SKIPPED ? 'Skipped' : 'Empty'}</span>
                  </div>
                )}
              </div>
              <div className={styles.reviewCardActions}>
                <button
                  type="button"
                  className={styles.ghostButton}
                  onClick={() => handleRetakeSlot(i)}
                >
                  {slot.status === SLOT_STATUS.DONE ? 'Re-record' : 'Record'}
                </button>
              </div>
              {/* Per-clip delete on the review page */}
              <button
                type="button"
                className={styles.slotDeleteBtn}
                onClick={() => handleDeleteSlot(i)}
                aria-label={`Delete clip ${i + 1}`}
                title="Delete clip"
              >
                ✕
              </button>
            </div>
          );
        };

        // "+ Add Question" tile — pass a sectionTag to land the new prompt
        // back inside that section (Beginning's tile → new slot tagged
        // 'Beginning'); pass null/omit to land it under Unsectioned/Other.
        const renderAddTile = (sectionTag = null, keySuffix = '') => {
          const openWithTag = () => {
            pendingCustomSectionRef.current = sectionTag;
            setAddAndRecord(true);
            setCustomAddOpen(true);
          };
          return (
            <div
              key={`add-question-tile-${keySuffix || (sectionTag || 'other')}`}
              role="button"
              tabIndex={0}
              className={`${styles.reviewCard} ${styles.reviewAddCard}`}
              onClick={openWithTag}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openWithTag();
                }
              }}
              aria-label={sectionTag
                ? `Add another question to ${sectionTag} and record`
                : 'Add another question and record'}
            >
              <div className={styles.reviewCardHeader}>
                <span className={`${styles.reviewCardIndex} ${styles.reviewAddIndex}`}>＋</span>
                <span className={styles.reviewCardSubject}>Add Question</span>
              </div>
              <div className={`${styles.reviewCardMedia} ${styles.reviewAddMedia}`}>
                <span className={styles.reviewAddIcon} aria-hidden="true">＋</span>
                <span className={styles.reviewAddCaption}>Tap to add &amp; record</span>
              </div>
              <div className={styles.reviewCardActions}>
                <span className={`${styles.ghostButton} ${styles.reviewAddCta}`}>Add &amp; Record</span>
              </div>
            </div>
          );
        };

        // Sender flow (no sections prop) — flat grid, behavior unchanged.
        if (!Array.isArray(sections)) {
          return (
            <div className={styles.reviewGrid}>
              {slots.map((slot, i) => renderReviewCard(slot, i))}
              {renderAddTile()}
            </div>
          );
        }

        // Receiver flow — bucket slots by their `section` tag (deletion-safe)
        // and render each group as its own grid. Custom-added/unsectioned
        // slots land in a final "Unsectioned" group with the "+ Add" tile.
        const indexedAll = slots.map((slot, idx) => ({ slot, idx }));
        const sectionItemGroups = sections.map((s) => ({
          title: s.title,
          items: indexedAll.filter(({ slot }) => slot.section === s.title),
        }));
        const unsectionedItems = indexedAll.filter(({ slot }) => !slot.section);
        const sectionTitleStyle = {
          fontSize: '0.85rem',
          fontWeight: 700,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: '#5aa5ef',
          margin: '24px 0 12px 0',
        };
        // Inline grid (no .reviewGrid class) so each section's grid grows
        // to its natural height instead of scrolling internally. The outer
        // overlay handles all vertical scroll.
        const sectionGridStyle = {
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gridAutoRows: 'minmax(180px, 240px)',
          gap: '16px',
          width: '100%',
          alignContent: 'start',
        };
        return (
          <div style={{ paddingBottom: '12px' }}>
            {sectionItemGroups.map((g) => (
              <div key={`sect-${g.title}`}>
                <div style={sectionTitleStyle}>{g.title}</div>
                <div style={sectionGridStyle}>
                  {g.items.map(({ slot, idx }) => renderReviewCard(slot, idx))}
                  {renderAddTile(g.title)}
                </div>
              </div>
            ))}
            <div>
              <div style={sectionTitleStyle}>Unsectioned</div>
              <div style={sectionGridStyle}>
                {unsectionedItems.map(({ slot, idx }) => renderReviewCard(slot, idx))}
                {renderAddTile(null)}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Bottom control bar — only for REVIEW (recording controls live on the canvas).
          Hidden while the receiver's "add more?" prompt is fronting the review,
          so the Attach CTA doesn't compete with Yes/No. ── */}
      {phase === PHASE.REVIEW && !(receiverAskMore && Array.isArray(sections)) && (
        <div className={styles.controlBar}>
          <button type="button" className={styles.secondaryButton} onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleAttachAll}
            disabled={doneCount === 0}
          >
            {`Attach ${doneCount} ${doneCount === 1 ? 'Clip' : 'Clips'} to Gift`}
          </button>
        </div>
      )}

      {/* ── Commentary mode: hidden file picker, off-screen media elements,
          ordering modal, floating controls, PIP overlay. Stacked here so
          they survive phase transitions and live above the recorder UI.
          ── */}
      <input
        ref={commentaryFileInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        style={{ display: 'none' }}
        onChange={handleCommentaryFilesPicked}
      />
      {/* Hidden media playback elements — ALWAYS mounted (regardless of
          commentary state) so the same <video> DOM node serves the entire
          session. createMediaElementSource only allows ONE source node per
          element for the lifetime of the page, so re-mounting the video
          across media changes triggered the
          "HTMLMediaElement already connected" error we saw in logs.
          With one persistent element we just swap `src` when the active
          media changes, and Web Audio stays happily attached. The video is
          also intentionally NOT `muted` here: once MediaElementSource is
          wired, Web Audio takes over the audio output (the element no
          longer plays through the default speakers), so leaving muted=true
          would block audio from reaching the gain graph in some browsers. */}
      {(() => {
        const c = getCurrentCommentary();
        const item = c && c.mediaList && c.mediaList[Math.min(commentaryMediaIndex, (c.mediaList.length || 1) - 1)];
        const videoSrc = item && item.type === 'video' ? getCommentaryMediaUrl(item.file) : '';
        const imageSrc = item && item.type === 'image' ? getCommentaryMediaUrl(item.file) : '';
        const hiddenStyle = {
          position: 'fixed',
          left: '-10000px',
          top: '-10000px',
          width: '2px',
          height: '2px',
          opacity: 0,
          pointerEvents: 'none',
        };
        return (
          <>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              ref={commentaryVideoElRef}
              src={videoSrc || undefined}
              playsInline
              preload="auto"
              style={hiddenStyle}
              onLoadedMetadata={(e) => {
                const d = e.currentTarget.duration;
                if (Number.isFinite(d)) setCommentaryVideoDuration(d);
              }}
              onTimeUpdate={(e) => {
                setCommentaryVideoTime(e.currentTarget.currentTime || 0);
              }}
              onEnded={() => setCommentaryIsPlaying(false)}
              onPause={() => setCommentaryIsPlaying(false)}
              onPlay={() => setCommentaryIsPlaying(true)}
            />
            <img
              ref={commentaryImageElRef}
              src={imageSrc || undefined}
              alt=""
              style={hiddenStyle}
            />
          </>
        );
      })()}

      {/* Ordering modal — drag thumbnails into the order you want before
          applying. Reuses react-beautiful-dnd already in the file. */}
      {commentaryOrderOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Order your media"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9700,
            background: 'rgba(8, 6, 18, 0.86)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px 24px',
          }}
        >
          <div style={{
            width: '100%',
            maxWidth: '640px',
            maxHeight: '85vh',
            background: 'linear-gradient(160deg, rgba(50, 56, 72, 0.96), rgba(28, 32, 42, 0.98))',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: '20px',
            boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
          >
            <div style={{ padding: '22px 24px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800 }}>Order your media</h3>
              <p style={{
                margin: '4px 0 0 0', color: '#b9bdc7', fontSize: '0.88rem', lineHeight: 1.5,
              }}
              >
                Drag to set the order they&apos;ll appear in the background while you record.
              </p>
            </div>
            <div style={{
              flex: 1, overflowY: 'auto', padding: '14px 18px',
            }}
            >
              <DragDropContext onDragEnd={handleCommentaryOrderDragEnd}>
                <Droppable droppableId="commentary-order">
                  {(droppableProvided) => (
                    <div
                      ref={droppableProvided.innerRef}
                      {...droppableProvided.droppableProps}
                      style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                    >
                      {commentaryPendingFiles.map((file, i) => (
                        <Draggable key={`pending-${i}-${file.name}`} draggableId={`pending-${i}-${file.name}`} index={i}>
                          {(draggableProvided, draggableSnapshot) => {
                            const rowNode = (
                              <div
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                                {...draggableProvided.dragHandleProps}
                                style={{
                                  ...draggableProvided.draggableProps.style,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  padding: '10px 12px',
                                  borderRadius: '12px',
                                  border: '1px solid rgba(255,255,255,0.10)',
                                  background: draggableSnapshot.isDragging
                                    ? 'rgba(255,255,255,0.10)'
                                    : 'rgba(255,255,255,0.04)',
                                  color: '#fff',
                                  boxShadow: draggableSnapshot.isDragging
                                    ? '0 12px 28px rgba(0,0,0,0.45)'
                                    : 'none',
                                }}
                              >
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '28px',
                                  height: '28px',
                                  borderRadius: '999px',
                                  background: 'rgba(255,255,255,0.08)',
                                  fontSize: '0.85rem',
                                  fontWeight: 800,
                                }}
                                >
                                  {i + 1}
                                </span>
                                <span style={{ fontSize: '1.05rem' }} aria-hidden="true">
                                  {detectMediaType(file) === 'video' ? '🎥' : '🖼️'}
                                </span>
                                <span style={{
                                  flex: 1,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.9rem',
                                }}
                                >
                                  {file.name}
                                </span>
                                <span style={{
                                  color: '#8a8e98', fontSize: '0.78rem', letterSpacing: '0.04em',
                                }}
                                >
                                  ⋮⋮
                                </span>
                              </div>
                            );
                            // Portal the dragged row to document.body so it escapes
                            // the gift-flow step container's transform-induced
                            // containing block (otherwise the row drifts away from
                            // the pointer during drag in the sender flow).
                            if (draggableSnapshot.isDragging) {
                              const portalTarget = getDragPortalTarget();
                              if (portalTarget) return ReactDOM.createPortal(rowNode, portalTarget);
                            }
                            return rowNode;
                          }}
                        </Draggable>
                      ))}
                      {droppableProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
            <div style={{
              display: 'flex',
              gap: '10px',
              alignItems: 'center',
              justifyContent: 'flex-end',
              padding: '14px 22px',
              borderTop: '1px solid rgba(255,255,255,0.08)',
              background: 'rgba(0,0,0,0.18)',
              flexWrap: 'wrap',
            }}
            >
              {/* Recording resolution picker. Higher = preserves source
                  detail (4K, 2K) at the cost of larger files and more
                  GPU/CPU. 1080p is safe everywhere; 4K is Chrome/Edge
                  best — Safari may stutter or refuse. */}
              <label
                htmlFor="commentary-quality-select"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginRight: 'auto',
                  color: '#cfd1da',
                  fontFamily: 'inherit',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                }}
              >
                <span aria-hidden="true">🎬</span>
                <span>Recording quality</span>
                <select
                  id="commentary-quality-select"
                  value={commentaryQualityCap}
                  onChange={(e) => setCommentaryQualityCap(Number(e.target.value))}
                  disabled={availableQualityCaps.length <= 1}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.18)',
                    background: 'rgba(255,255,255,0.06)',
                    color: '#fff',
                    fontFamily: 'inherit',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    cursor: availableQualityCaps.length <= 1 ? 'not-allowed' : 'pointer',
                    opacity: availableQualityCaps.length <= 1 ? 0.7 : 1,
                  }}
                >
                  {availableQualityCaps.map((h) => {
                    let label;
                    if (h === 1080) label = '1080p · HD (recommended)';
                    else if (h === 1440) label = '1440p · 2K';
                    else if (h === 2160) label = '2160p · 4K (heavy)';
                    else label = `${h}p · Native (source resolution)`;
                    return (
                      <option key={h} value={h} style={{ background: '#1a1f2c' }}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </label>
              <button
                type="button"
                onClick={handleCommentaryOrderCancel}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                  borderRadius: '999px',
                  border: '1px solid rgba(255,255,255,0.18)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#cfd1da',
                  fontFamily: 'inherit',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCommentaryOrderApply}
                disabled={commentaryPendingFiles.length === 0}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '8px 20px',
                  borderRadius: '999px',
                  border: 'none',
                  background: commentaryPendingFiles.length === 0
                    ? 'rgba(255,255,255,0.08)'
                    : '#5aa5ef',
                  color: commentaryPendingFiles.length === 0 ? '#7f828b' : '#fff',
                  fontFamily: 'inherit',
                  fontSize: '0.9rem',
                  fontWeight: 800,
                  cursor: commentaryPendingFiles.length === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR pairing modal — opened when user picks "Use Phone Camera". */}
      <QRPairingModal
        open={qrOpen}
        joinUrl={mcSession.joinUrl}
        status={mcSession.status}
        onClose={handleQrClose}
        onRetry={handleQrRetry}
      />

      {/* Phone-camera disconnected modal — shown when the paired phone drops
          the connection. Acknowledging falls back to the desktop camera. */}
      {mobileDisconnected && (
        <div className={styles.switchConfirmRoot} role="dialog" aria-modal="true" aria-label="Phone camera disconnected">
          <button
            type="button"
            className={styles.switchConfirmBackdrop}
            onClick={handleMobileDisconnectAck}
            aria-label="Dismiss"
          />
          <div className={styles.switchConfirmCard}>
            <h3 className={styles.switchConfirmTitle}>Phone camera disconnected</h3>
            <p className={styles.switchConfirmBody}>
              Your phone camera lost connection. Reconnect to pair your phone again, or switch back to your computer&apos;s camera to keep recording.
            </p>
            <div className={`${styles.switchConfirmActions} ${styles.switchConfirmActionsCentered}`}>
              <button
                type="button"
                className={styles.switchCancelButton}
                onClick={handleMobileDisconnectAck}
              >
                Use computer camera
              </button>
              <button
                type="button"
                className={styles.switchSaveButton}
                onClick={handleMobileReconnect}
              >
                Reconnect phone
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Guest-invite popup ────────────────────────────────────────
          Open while we're inviting a guest (room created but not yet
          joined). Auto-dismisses when peer-joined arrives. Inline
          styles to avoid touching the module css for this one-off. */}
      {GUEST_INVITE_ENABLED && guestPopupOpen && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Invite a guest to the recording"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9700,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) cancelGuestInvite(); }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '440px',
              borderRadius: '18px',
              background: 'radial-gradient(circle at center, #1f2330, #07080c)',
              border: '1px solid rgba(255,255,255,0.10)',
              boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
              color: '#fff',
              fontFamily: 'inherit',
              padding: '26px 24px',
            }}
          >
            <div style={{ fontSize: '2.2rem', lineHeight: 1, textAlign: 'center', marginBottom: '12px' }} aria-hidden="true">
              👥
            </div>
            <h2
              style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: 800,
                letterSpacing: '-0.01em',
                textAlign: 'center',
              }}
            >
              Invite a guest
            </h2>
            <p
              style={{
                margin: '8px auto 18px',
                maxWidth: '340px',
                color: '#b9bdc7',
                fontSize: '0.92rem',
                lineHeight: 1.45,
                textAlign: 'center',
              }}
            >
              Share this link. They&apos;ll join the recording and hear the questions read out loud.
            </p>

            {/* Link + copy row */}
            <div
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'stretch',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: '12px',
                padding: '8px',
                marginBottom: '16px',
              }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: '8px 10px',
                  borderRadius: '8px',
                  background: 'rgba(0,0,0,0.25)',
                  color: '#e6e8ee',
                  fontFamily: "'Menlo', 'Consolas', monospace",
                  fontSize: '0.78rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={guestInviteLink || 'Generating link…'}
              >
                {guestInviteLink || 'Generating link…'}
              </div>
              <button
                type="button"
                onClick={handleCopyGuestLink}
                disabled={!guestInviteLink}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  background: guestInviteLink ? '#5aa5ef' : 'rgba(255,255,255,0.08)',
                  color: guestInviteLink ? '#fff' : '#7f828b',
                  fontFamily: 'inherit',
                  fontSize: '0.82rem',
                  fontWeight: 800,
                  cursor: guestInviteLink ? 'pointer' : 'not-allowed',
                  minWidth: '78px',
                }}
              >
                {guestLinkCopied ? 'Copied ✓' : 'Copy'}
              </button>
            </div>

            {/* Status — small dot + text, mirrors the interview UI */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                color: '#ffd166',
                fontSize: '0.88rem',
                fontWeight: 600,
                marginBottom: '18px',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '999px',
                  background: '#ffd166',
                  boxShadow: '0 0 0 5px rgba(255, 209, 102, 0.18)',
                  animation: 'pulse 1.6s ease-in-out infinite',
                }}
              />
              Waiting for your guest to join…
            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={cancelGuestInvite}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '10px 22px',
                  minHeight: '42px',
                  lineHeight: 1,
                  borderRadius: '999px',
                  border: '1px solid rgba(255,255,255,0.18)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#cfd1da',
                  fontFamily: 'inherit',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {customAddOpen && (
        <div className={styles.customAddRoot} role="dialog" aria-modal="true" aria-label="Add a custom question">
          <button
            type="button"
            className={styles.customAddBackdrop}
            onClick={handleCustomAddDismiss}
            aria-label="Close"
          />
          <div className={styles.customAddCard}>
            <h3 className={styles.customAddTitle}>Add a custom question</h3>
            <p className={styles.customAddHint}>
              Type your prompt or pick from the suggestions below.
            </p>
            <label className={styles.customAddLabel} htmlFor="gift-recorder-custom-question">
              Question
              <textarea
                id="gift-recorder-custom-question"
                className={styles.customAddTextarea}
                rows={3}
                maxLength={200}
                value={customQuestion}
                placeholder="e.g. Share a memory of them that always makes you smile."
                onFocus={() => setCustomFocused(true)}
                onChange={(e) => setCustomQuestion(e.target.value)}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
            </label>

            {customFocused && customSuggestions.length > 0 && (
              <div className={styles.customSuggestList} role="listbox" aria-label="Question suggestions">
                {customSuggestions.map((s) => (
                  <button
                    type="button"
                    key={s}
                    className={styles.customSuggestItem}
                    role="option"
                    aria-selected="false"
                    // onMouseDown so the click registers before the textarea blur
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setCustomQuestion(s);
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <div className={styles.customAddActions}>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={handleCustomAddDismiss}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={handleAddCustomQuestion}
                disabled={!customQuestion.trim()}
              >
                Add Question
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Switch-mid-recording confirm modal ──
          Opens when the user taps a different slot while RECORDING.
          Cancel keeps recording; Dump throws away the in-flight clip
          and starts the new one; Save lands the current clip then jumps. */}
      {pendingSwitch && (
        <div className={styles.switchConfirmRoot} role="dialog" aria-modal="true" aria-label="Save this clip before jumping?">
          <button
            type="button"
            className={styles.switchConfirmBackdrop}
            onClick={cancelSwitch}
            aria-label="Cancel"
          />
          <div className={styles.switchConfirmCard}>
            <div className={styles.switchConfirmJump}>
              <span className={styles.switchConfirmChip}>{pendingSwitch.fromSlot + 1}</span>
              <span className={styles.switchConfirmArrow} aria-hidden="true">→</span>
              <span className={`${styles.switchConfirmChip} ${styles.switchConfirmChipTo}`}>{pendingSwitch.toSlot + 1}</span>
            </div>
            <h3 className={styles.switchConfirmTitle}>Save this clip before jumping?</h3>
            <p className={styles.switchConfirmBody}>
              {`You're moving from slot ${pendingSwitch.fromSlot + 1} to slot ${pendingSwitch.toSlot + 1}. Keep this recording or throw it away?`}
            </p>
            <div className={styles.switchConfirmActions}>
              <button
                type="button"
                className={styles.switchCancelButton}
                onClick={cancelSwitch}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.switchDumpButton}
                onClick={confirmSwitchDump}
              >
                Throw away
              </button>
              <button
                type="button"
                className={styles.switchSaveButton}
                onClick={confirmSwitchSave}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Back-button confirmation modal ──
          Triggered by the page-corner Back when there are recorded clips or
          an in-flight recording. Reuses the switch-confirm modal's classes
          for a consistent look. */}
      {pendingBackExit && (
        <div
          className={styles.switchConfirmRoot}
          role="dialog"
          aria-modal="true"
          aria-label="Discard your recordings and go back?"
        >
          <button
            type="button"
            className={styles.switchConfirmBackdrop}
            onClick={cancelBackExit}
            aria-label="Cancel"
          />
          <div className={styles.switchConfirmCard}>
            <h3 className={styles.switchConfirmTitle}>Go back?</h3>
            <p className={styles.switchConfirmBody}>
              Your recordings will be lost. Are you sure you want to leave?
            </p>
            <div className={styles.switchConfirmActions}>
              <button
                type="button"
                className={styles.switchCancelButton}
                onClick={cancelBackExit}
              >
                Stay here
              </button>
              <button
                type="button"
                className={styles.switchDumpButton}
                onClick={confirmBackExit}
              >
                Leave &amp; discard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftRecorder;
